import React from 'react';
import { useParams, useNavigate, useLocation, Location } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import SignatureModal from '../components/SignatureModal';
import DateInput from '../components/DateInput';
import DatePicker from 'react-datepicker';
require('react-datepicker/dist/react-datepicker.css');
//push changes
interface LocationState {
  acceptMethod?: 'online' | 'email';
  quoteId?: string;
  clientId?: string;
  fromAcceptQuotation?: boolean;
}

// Custom Date Picker with manual formatting
const StyledDatePicker: React.FC<{
  value: string;
  onChange: (date: string) => void;
  className?: string;
}> = ({ value, onChange, className }) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(() => {
    if (value) {
      const parts = value.split('-');
      if (parts.length === 3) {
        return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      }
    }
    return null;
  });
  const [displayValue, setDisplayValue] = useState(() => {
    if (value) {
      const parts = value.split('-');
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
    }
    return '';
  });

  // Convert storage (yyyy-mm-dd) to display (dd/mm/yyyy)
  const toDisplayFormat = (storageDate: string): string => {
    if (!storageDate) return '';
    const parts = storageDate.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return storageDate;
  };

  // Convert display (dd/mm/yyyy) to storage (yyyy-mm-dd)
  const toStorageFormat = (displayDate: string): string => {
    if (!displayDate) return '';
    const parts = displayDate.split('/');
    if (parts.length === 3 && parts[2].length === 4) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return '';
  };

  // Handle manual input with auto-formatting
  const handleManualChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Get only digits
    const digits = inputValue.replace(/\D/g, '');
    
    let formatted = '';
    
    if (digits.length >= 1) {
      formatted = digits.substring(0, 2);
      if (digits.length >= 3) {
        formatted = digits.substring(0, 2) + '/' + digits.substring(2, 4);
      }
      if (digits.length >= 5) {
        formatted = digits.substring(0, 2) + '/' + digits.substring(2, 4) + '/' + digits.substring(4, 8);
      }
    }
    
    setDisplayValue(formatted);
    
    // Save if complete
    if (formatted.length === 10 && formatted.includes('/')) {
      const storageDate = toStorageFormat(formatted);
      if (storageDate) {
        onChange(storageDate);
        const parts = formatted.split('/');
        setSelectedDate(new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0])));
      }
    } else if (formatted.length === 0) {
      onChange('');
      setSelectedDate(null);
    }
  };

  // Handle date selection from calendar
  const handleCalendarChange = (date: Date | null) => {
    if (date) {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const displayDate = `${day}/${month}/${year}`;
      const storageDate = `${year}-${month}-${day}`;
      
      setDisplayValue(displayDate);
      setSelectedDate(date);
      onChange(storageDate);
    }
  };

  // Update when external value changes
  useEffect(() => {
    const newDisplayValue = toDisplayFormat(value);
    setDisplayValue(newDisplayValue);
    if (value) {
      const parts = value.split('-');
      if (parts.length === 3) {
        setSelectedDate(new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2])));
      }
    }
  }, [value]);

  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative w-full" ref={containerRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          className={className}
          placeholder="dd/mm/yyyy"
          value={displayValue}
          onChange={handleManualChange}
          onFocus={() => setIsOpen(true)}
        />
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            className="h-5 w-5" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor" 
            strokeWidth={1.5}
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" 
            />
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              d="M12 12.75h.008v.008H12v-.008zM15 12.75h.008v.008H15v-.008zM9 12.75h.008v.008H9v-.008zM12 16.5h.008v.008H12V16.5zM15 16.5h.008v.008H15V16.5zM9 16.5h.008v.008H9V16.5z" 
            />
          </svg>
        </button>
      </div>
      
      {isOpen && (
        <div className="absolute z-[99999] mt-1 bg-white rounded-lg shadow-lg border border-gray-200">
          <DatePicker
            selected={selectedDate}
            onChange={(date) => {
              handleCalendarChange(date);
              setIsOpen(false);
            }}
            dateFormat="dd/MM/yyyy"
            inline
            showYearDropdown
            showMonthDropdown
            dropdownMode="select"
          />
        </div>
      )}
    </div>
  );
};

const ProposalApplicationForm: React.FC = () => {
  const { quoteId } = useParams<{ quoteId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isPreview = new URLSearchParams(location.search).get('preview') === 'true';
  const [acceptData, setAcceptData] = useState<any>(null);
  const [client, setClient] = useState<any>(null);
  const [loadingClient, setLoadingClient] = useState(true);
  const [address, setAddress] = useState<any>(null);
  const [loadingAddress, setLoadingAddress] = useState(true);
  const [broker, setBroker] = useState<any>(null);
  const [loadingBroker, setLoadingBroker] = useState(true);
  const [debitOrder, setDebitOrder] = useState({
    surnameInitials: '',
    accountNumber: '',
    accountType: '',
    bankName: '',
    branchCode: '',
    bankBranch: '',
    debitOrderDate: '',
    premiumPayable: '',
    accountHolderId: '',
  });
  const [inceptionDate, setInceptionDate] = useState(() => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`; // YYYY-MM-DD format for the DateInput component
});

const [declarationDateObj, setDeclarationDateObj] = useState<Date | null>(new Date());

const [declarationDate, setDeclarationDate] = useState(() => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
});


  // For Important section
  interface ApplicationQuestion {
    id?: number;
    question: string;
    is_default: boolean;
    created_at?: string;
    updated_at?: string;
  }

  const [importantQuestions, setImportantQuestions] = useState<ApplicationQuestion[]>([]);
  const [loadingQuestions, setLoadingQuestions] = useState(true);

  interface DebitOrderQuestion {
    id?: number;
    question: string;
    is_default: boolean;
    created_at?: string;
    updated_at?: string;
  }

  const [debitOrderQuestions, setDebitOrderQuestions] = useState<DebitOrderQuestion[]>([]);
  const [loadingDebitOrderQuestions, setLoadingDebitOrderQuestions] = useState(true);
  const [debitOrderResponses, setDebitOrderResponses] = useState<{ [key: number]: boolean }>({});

  // Management Fees state
  const [managementFeesQuestions, setManagementFeesQuestions] = useState<ApplicationQuestion[]>([]);
  const [loadingManagementFees, setLoadingManagementFees] = useState(true);
  const [managementFeesResponses, setManagementFeesResponses] = useState<{ [key: number]: string }>({});
  const [brokerFee, setBrokerFee] = useState<string>('');
  const [managementFee, setManagementFee] = useState<string>('');
  const [premiumAmount, setPremiumAmount] = useState<string>('');

  const [saveStatus, setSaveStatus] = useState<'idle' | 'loading' | 'success' | 'error' | 'info'>('idle');
  const [saveMessage, setSaveMessage] = useState('');

  // Service Provider Questions state
  interface ServiceProviderQuestion {
    id?: number;
    question: string;
    is_default: boolean;
    created_at?: string;
    updated_at?: string;
  }

  interface DeclarationQuestion {
  id?: number;
  question: string;
  is_default: boolean;
  created_at?: string;
  updated_at?: string;
  response_type?: string;
}

interface PropertyData {
  id: number;
  client_id: number;
  address_line_1: string;
  address_line_2?: string;
  suburb: string;
  city: string;
  postal_code: string;
  region: string;
  country: string;
  property_type: string;
  residence_type: string;
  building_age: string;
  situated_at: string;
  cover_type: string;
  insured_value: number;
  premium?: number;
  excess?: number;
  no_claim_bonus?: string;
  quote_id?: number;
  has_swimming_pool: boolean;
  has_motorized_gate: boolean;
  is_unoccupied_more_than_60_days: boolean;
  is_commune: boolean;
  under_construction: boolean;
  built_on_steep_slope: boolean;
  has_excessive_retaining_walls: boolean;
  near_water_body: boolean;
  has_rising_damp: boolean;
  has_wall_cracks: boolean;
  has_subsidence_history: boolean;
  has_outbuilding: boolean;
  has_perimeter_fence: boolean;
  has_electric_fence: boolean;
  has_thatch_roof: boolean;
  has_accidental_damage_cover: boolean;
  has_power_surge_cover: boolean;
  wall_materials?: string[] | string;
  roof_materials?: string[] | string;
  floor_materials?: string[] | string;
  other_surrounding_areas?: string[] | string;
  thatch_less_than_5m: boolean;
  thatch_sabs_conductor: boolean;
  thatch_treated: boolean;
  accidental_damage_cover_value?: number;
  power_surge_cover_value?: number;
  created_at?: string;
  updated_at?: string;
}

interface PropertyQuestion {
  id: number;
  uuid: string;
  question: string;
  category: string;
  is_default: boolean;
  is_important: boolean;
  insurer_id: number;
  type: string;
  property_type: string;
  coverage_type: string;
  created_at?: string;
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
}

const [propertyQuestions, setPropertyQuestions] = useState<PropertyQuestion[]>([]);
const [loadingPropertyQuestions, setLoadingPropertyQuestions] = useState(true);
const [propertyQuestionResponses, setPropertyQuestionResponses] = useState<{ [key: number]: string | boolean }>({});

const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
const [isProcessingSignature, setIsProcessingSignature] = useState(false);

interface HouseholdContentQuestion {
  id: number;
  question: string;
  is_default: boolean;
  is_important: boolean;
  insurer_id: number;
  type?: string;
  created_at?: string;
  updated_at?: string;
}

const [householdContentQuestions, setHouseholdContentQuestions] = useState<HouseholdContentQuestion[]>([]);
const [loadingHouseholdContentQuestions, setLoadingHouseholdContentQuestions] = useState(true);
const [householdContentQuestionResponses, setHouseholdContentQuestionResponses] = useState<{ [key: number]: string | boolean }>({});

interface PersonOfInterest {
  id: number;
  user_id?: number;
  name: string;
  surname: string;
  drivers_license_codes?: any[];
}
interface VehicleQuestion {
  id: number;
  question: string;
  is_default: boolean;
  is_important: boolean;
  insurer_id: number;
  type?: string;
  created_at?: string;
  updated_at?: string;
}

const [vehicleQuestions, setVehicleQuestions] = useState<VehicleQuestion[]>([]);
const [loadingVehicleQuestions, setLoadingVehicleQuestions] = useState(true);

interface TrailerCaravanQuestion {
  id: number;
  question: string;
  is_default: boolean;
  is_important: boolean;
  insurer_id: number;
  type?: string;
  created_at?: string;
  updated_at?: string;
}

const [trailerCaravanQuestions, setTrailerCaravanQuestions] = useState<TrailerCaravanQuestion[]>([]);
const [loadingTrailerCaravanQuestions, setLoadingTrailerCaravanQuestions] = useState(true);

interface WatercraftQuestion {
  id: number;
  question: string;
  is_default: boolean;
  is_important: boolean;
  insurer_id: number;
  type?: string;
  created_at?: string;
  updated_at?: string;
}

const [watercraftQuestions, setWatercraftQuestions] = useState<WatercraftQuestion[]>([]);
const [loadingWatercraftQuestions, setLoadingWatercraftQuestions] = useState(true);

interface AllRiskQuestion {
  id: number;
  question: string;
  is_default: boolean;
  is_important: boolean;
  insurer_id: number;
  type?: string;
  created_at?: string;
  updated_at?: string;
}

const [allRiskQuestions, setAllRiskQuestions] = useState<AllRiskQuestion[]>([]);
const [loadingAllRiskQuestions, setLoadingAllRiskQuestions] = useState(true);

interface ValueAddedQuestion {
  id: number;
  question: string;
  is_default: boolean;
  is_important: boolean;
  insurer_id: number;
  type?: string;
  created_at?: string;
  updated_at?: string;
}

const [valueAddedQuestions, setValueAddedQuestions] = useState<ValueAddedQuestion[]>([]);
const [loadingValueAddedQuestions, setLoadingValueAddedQuestions] = useState(true);
// Add missing response states for sections that don't have them yet
const [vehicleQuestionResponses, setVehicleQuestionResponses] = useState<{ [key: number]: boolean }>({});
const [trailerCaravanQuestionResponses, setTrailerCaravanQuestionResponses] = useState<{ [key: number]: boolean }>({});
const [watercraftQuestionResponses, setWatercraftQuestionResponses] = useState<{ [key: number]: boolean }>({});
const [allRiskQuestionResponses, setAllRiskQuestionResponses] = useState<{ [key: number]: boolean }>({});
const [valueAddedQuestionResponses, setValueAddedQuestionResponses] = useState<{ [key: number]: boolean }>({});

interface HouseholdContentData {
  id: number;
  clientId: number;
  quoteId: number;
  yearsLivedOnProperty?: number;
  burglarBarType?: string;
  dayTimeOccupantType?: string;
  coverType: string;
  insuredValue: number;
  conductsBusiness?: boolean;
  hasSurveillanceCamera?: boolean;
  hasExternalSlidingDoor?: boolean;
  hasSecurityGate?: boolean;
  inSecurityComplex?: boolean;
  hasAlarmRecords?: boolean;
  hasNeighborhoodWatch?: boolean;
  hasAccidentalDamageCover?: boolean;
  accidentalDamageValue?: number;
  hasPowerSurgeCover?: boolean;
  powerSurgeValue?: number;
  alarmSystemType?: string;
  perimeterType?: string | string[];
  addressLine1?: string;
  addressLine2?: string;
  suburb?: string;
  cityTown?: string;
  postalCode?: string;
  region?: string;
  country?: string;
  propertyType?: string;
  residenceType?: string;
  buildingAge?: string;
  situatedType?: string;
  hasSwimmingPool?: boolean;
  hasMotorizedGate?: boolean;
  isPropertyUnoccupied?: boolean;
  isCommune?: boolean;
  underConstruction?: boolean;
  builtOnSteepSlope?: boolean;
  hasRetainingWalls?: boolean;
  nearWaterBody?: boolean;
  hasRisingDamp?: boolean;
  hasWallCracks?: boolean;
  historyOfLandslip?: boolean;
  hasOutbuilding?: boolean;
  hasPerimeterFence?: boolean;
  hasElectricFence?: boolean;
  hasThatchRoof?: boolean;
  wallMaterials?: string | string[];
  roofMaterials?: string | string[];
  floorMaterials?: string | string[];
  otherSurroundingArea?: string | string[];
  premium?: number;
  excess?: number;
  noClaimBonus?: string;
}

interface VehicleData {
  id: number;
  client_id: number;
  quote_id: number;
  vehicle_type: string;
  registration_number: string;
  make: string;
  model: string;
  year: number;
  variant: string;
  color: string;
  usage: string;
  vin_number: string;
  engine_number: string;
  mmcode: string;
  insured_value: string;
  cover_type: string;
  use_retail_value: boolean;
  excess_buy_back: boolean;
  credit_shortfall: boolean;
  car_hire: boolean;
  day_parking_location: string | { name?: string; description?: string } | null;
  night_parking_location: string | { name?: string; description?: string } | null;
  registered_owner: string | { full_name?: string; name?: string } | null;
  regular_driver: string | { full_name?: string; name?: string } | null;
  other_drivers: string | Array<{ full_name?: string; name?: string }> | null;
  safety_features: string | Array<{ name?: string; description?: string }> | null;
  address_line1: string;
  address_line2: string;
  suburb: string;
  city_town: string;
  postal_code: string;
  region: string;
  country: string;
  overnight_parking_elsewhere: boolean;
  premium: string;
  excess: string;
  no_claim_bonus: string;
  created_at?: string;
  updated_at?: string;
}

interface TrailerCaravanData {
  id: number;
  vehicle_type: string;
  registration_number: string;
  make: string;
  model: string;
  year: number;
  cover_type: string;
  insured_value: number;
  client_id: number;
  use_retail_value: boolean;
  premium: number;
  excess: number;
  no_claims_bonus: string;
  quote_id?: number;
  created_at?: string;
  updated_at?: string;
}

interface WatercraftData {
  id: number;
  year: number;
  craft_type: string;
  hull_make: string;
  hull_length: number;
  year_model?: number;
  body_paint?: string;
  outboard_motor_make?: string;
  outboard_engine_number?: string;
  horse_power?: number;
  max_speed?: number;
  vessel_name: string;
  vessel_storage?: string;
  vessel_usage?: string;
  amateur_built: boolean;
  registered_owner?: string;
  finance_house?: string;
  finance_branch?: string;
  hull_value: number;
  outboard_value: number;
  sails_value: number;
  total_sum_insured: number;
  total_monthly_premium: number;
  trailer_make?: string;
  trailer_model?: string;
  trailer_registration?: string;
  trailer_value: number;
  trailer_premium: number;
  client_id: number;
  quote_id?: number;
  created_at?: string;
  updated_at?: string;
}

interface AllRiskData {
  id: number;
  item_type: string;
  description: string;
  item_name: string;
  insured_value: number;
  client_id: number;
  quote_id?: number;
  premium: number;
  serial_number?: string;
  cover_type: string;
  no_claims_bonus: string;
  excess: number;
  created_at?: string;
  updated_at?: string;
}

interface ValueAddedProductData {
  id: number;
  product_type: string;
  description: string;
  value: number;
  client_id: number;
  premium: number;
  quote_id?: number;
  created_at?: string;
  updated_at?: string;
}

const [commentForm, setCommentForm] = useState({
  comment: '',
  comment_by_name: '',
  comment_by_id_no: '',
  signed_at: '',
  comment_date: new Date().toISOString().split('T')[0],
});

  const [serviceProviderQuestions, setServiceProviderQuestions] = useState<ServiceProviderQuestion[]>([]);
  const [loadingServiceProviderQuestions, setLoadingServiceProviderQuestions] = useState(true);

  interface ClientClaim {
    id?: number;
    client_id: number;
    description: string;
    policy_number: string;
    previous_insurer: string;
    claim_date: string;
    claim_value: string;
    created_at?: string;
    updated_at?: string;
    quote_id?: number;
  }

  const [clientClaims, setClientClaims] = useState<ClientClaim[]>([]);
  const [loadingClaims, setLoadingClaims] = useState(true);

  const [previousInsurance, setPreviousInsurance] = useState<any[]>([]);
  const [loadingPreviousInsurance, setLoadingPreviousInsurance] = useState(true);

  const [declarationQuestions, setDeclarationQuestions] = useState<DeclarationQuestion[]>([]);
  const [loadingDeclarationQuestions, setLoadingDeclarationQuestions] = useState(true);
  const [declarationResponses, setDeclarationResponses] = useState<{ [key: number]: string | boolean }>({});

  const [propertyData, setPropertyData] = useState<PropertyData | null>(null);
  const [loadingProperty, setLoadingProperty] = useState(true);

  const [householdContents, setHouseholdContents] = useState<HouseholdContentData[]>([]);
  const [loadingHouseholdContents, setLoadingHouseholdContents] = useState(true);
  const [vehicles, setVehicles] = useState<VehicleData[]>([]);
  const [personsOfInterest, setPersonsOfInterest] = useState<PersonOfInterest[]>([]);
  const [loadingPersonsOfInterest, setLoadingPersonsOfInterest] = useState(true);
  const [loadingVehicles, setLoadingVehicles] = useState(true);
  const [trailersCaravans, setTrailersCaravans] = useState<TrailerCaravanData[]>([]);
  const [loadingTrailersCaravans, setLoadingTrailersCaravans] = useState(true);
  const [watercraft, setWatercraft] = useState<WatercraftData[]>([]);
  const [loadingWatercraft, setLoadingWatercraft] = useState(true);
  const [allRiskItems, setAllRiskItems] = useState<AllRiskData[]>([]);
  const [loadingAllRiskItems, setLoadingAllRiskItems] = useState(true);
  const [valueAddedProducts, setValueAddedProducts] = useState<ValueAddedProductData[]>([]);
  const [loadingValueAddedProducts, setLoadingValueAddedProducts] = useState(true);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [isSendingPDF, setIsSendingPDF] = useState(false);
  const [user, setUser] = useState<{ id: string; email: string } | null>(null);

  const [consentClausQuestions, setConsentClausQuestions] = useState<any[]>([]);
  const [loadingConsentClausQuestions, setLoadingConsentClausQuestions] = useState(true);

    // Broker Consent Questions
  const [brokerConsentQuestions, setBrokerConsentQuestions] = useState<any[]>([]);
  const [loadingBrokerConsentQuestions, setLoadingBrokerConsentQuestions] = useState(true);

  // Fetch client and address data
useEffect(() => {
  const fetchClientData = async () => {
    if (!quoteId) {
      setLoadingClient(false);
      setLoadingAddress(false);
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      console.error("No token found");
      setLoadingClient(false);
      setLoadingAddress(false);
      return;
    }

    try {
      // Fetch quote to get client_id
      const quoteResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/quotes/${quoteId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const quoteData = quoteResponse.data.data || quoteResponse.data;
      const clientId = quoteData?.client_id || quoteData?.clientId;
      
      if (clientId) {
        // Fetch client data
        const clientResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/clients/${clientId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const clientData = clientResponse.data.data || clientResponse.data;
        setClient(clientData);
        setLoadingClient(false);
        
        // Fetch address data
        try {
          const addressResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/addresses/client/${clientId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setAddress(addressResponse.data);
        } catch (addrError) {
          console.error("Error fetching address:", addrError);
          setAddress(null);
        }
        setLoadingAddress(false);
      } else {
        setLoadingClient(false);
        setLoadingAddress(false);
      }
    } catch (error) {
      console.error("Error fetching client data:", error);
      setLoadingClient(false);
      setLoadingAddress(false);
    }
  };
  
  fetchClientData();
}, [quoteId]);

// Fetch broker details
useEffect(() => {
  const fetchBrokerDetails = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error("No token found for broker details");
      setLoadingBroker(false);
      return;
    }

        try {
      console.log("🔍 Fetching broker details...");

      // Pass forQuoteId so the backend resolves the quote's broker even when
      // the caller's tenant doesn't match (e.g. super admin preview).
      const params: Record<string, string> = {};
      if (quoteId) params.forQuoteId = quoteId;

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/broker-details`,
        {
          params,
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log("✅ Broker details API response:", response.data);

      const brokerData = response.data?.data;

      if (!brokerData || !brokerData.companyName) {
        console.warn("⚠️ Broker details response contained no company name");
        setBroker(null);
      } else {
        // Normalize the sales consultant field just in case
        const normalizedBroker = {
          ...brokerData,
          salesConsultant: 
            brokerData.salesConsultant || 
            brokerData.sales_consultant || 
            brokerData.consultant_name || 
            brokerData.primaryContactPerson || 
            brokerData.primary_contact_person || 
            ''
        };
        
        console.log("📋 Loaded broker details:", normalizedBroker);
        setBroker(normalizedBroker);
      }
    } catch (error: any) {
      console.error("❌ Error loading broker details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      setBroker(null);
    } finally {
      setLoadingBroker(false);
    }
  };

  fetchBrokerDetails();
  // Re-fetch if quoteId becomes available (defensive; quoteId comes from route params)
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [quoteId]);

// Get logged-in user for broker ID
useEffect(() => {
  const storedUser = localStorage.getItem('user');
  if (storedUser) {
    try {
      const userData = JSON.parse(storedUser);
      setUser(userData);
      console.log("✅ User loaded for broker ID:", userData.id);
    } catch (e) {
      console.error("Error parsing user data:", e);
    }
  }
}, []);

  useEffect(() => {
  const state = location.state as any;
  
    // If coming from Accept Quotation with email method, just show a message or update UI
    if (state?.fromAcceptQuotation && state?.acceptMethod === 'email') {
      console.log("📧 Email flow activated - user can review form before sending");
      
      // Optional: Show a toast or message that they're in email flow
      setSaveStatus('info');
      setSaveMessage('Please review the proposal and click "Send to Client" when ready');
      
      // You could also scroll to the bottom or highlight the button
      setTimeout(() => {
        setSaveStatus('idle');
        setSaveMessage('');
      }, 5000);
    }
  }, [location.state, setSaveStatus]); // Remove handleAcceptEmail from dependencies

  const handleDebitChange = (field: keyof typeof debitOrder) => 
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setDebitOrder(prev => ({
        ...prev,
        [field]: e.target.value,
      }));
    };

    const handleInceptionDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/[^0-9/]/g, '');
    
    // Auto-format as dd/mm/yyyy
    if (value.length === 2 && !value.includes('/')) {
      value = value + '/';
    } else if (value.length === 5 && value.split('/').length === 2 && !value.endsWith('/')) {
      value = value + '/';
    }
    
    // Limit to 10 characters (dd/mm/yyyy)
    if (value.length <= 10) {
      setInceptionDate(value);
    }
  };

  const handleDebitOrderCheckbox = (questionId: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setDebitOrderResponses(prev => ({
      ...prev,
      [questionId]: e.target.checked,
    }));
  };

  // Handle management fee question responses
  const handleManagementFeeResponse = (questionId: number, value: string) => {
    setManagementFeesResponses(prev => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleDeclarationResponse = (questionId: number, value: string | boolean) => {
  setDeclarationResponses(prev => ({
    ...prev,
    [questionId]: value,
  }));
};

  const formatCurrency = (amount: number | undefined) => {
    if (!amount || typeof amount !== 'number') return "0.00";
    return amount.toFixed(2);
  };

  // ADD THIS HELPER FUNCTION HERE
  const formatAddress = (address: string | undefined | null): string => {
    if (!address) return "—";
    // Replace literal \n with actual newlines
    return address.replace(/\\n/g, '\n');
  };

  // Helper to safely extract display name from joined objects or strings
  const getDisplayName = (value: any, fallback: string = '—'): string => {
    if (!value) return fallback;
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) {
      return value
        .map(item => 
          item?.full_name || item?.name || item?.description || item || ''
        )
        .filter(Boolean)
        .join(', ') || fallback;
    }
    return value?.full_name || value?.name || value?.description || String(value) || fallback;
  };

    // Helper to match PDF logic for Safety Features
  const getSafetyFeaturesDisplay = (value: any): string => {
    if (!value) return 'None';
    if (typeof value === 'string') {
      const map: { [key: string]: string } = {
        '1': 'Alarm',
        '2': 'Tracking Device',
        '3': 'Factory Fitted Alarm',
        '4': 'Data Dot',
        '5': 'Immobiliser',
        '6': 'Smash and Grab',
      };
      return map[value] || value;
    }
    return getDisplayName(value, 'None');
  };

  // Helper to match PDF logic for Parking Locations
  const getParkingLocationDisplay = (value: any): string => {
    if (!value) return '—';
    if (typeof value === 'string') {
      const map: { [key: string]: string } = {
        '1': 'Garage',
        '2': 'Car Park',
        '3': 'Marked parking area',
        '4': 'Undercover parking',
        '5': 'Behind close gates',
        '6': 'House carport',
        '7': 'Security estate carport',
        '8': 'Street',
        '9': 'Driveway',
        '10': 'Security Complex',
        '11': 'Other',
      };
      return map[value] || value;
    }
    return getDisplayName(value);
  };

  // Helper to map cover type from numeric value to display name
  const getCoverTypeDisplay = (value: any): string => {
    if (!value) return '—';
    
    const coverTypeMap: { [key: string]: string } = {
      '1': 'Comprehensive',
      '2': 'Third Party Fire and Theft',
      '3': 'Third Party Property Damage',
      '4': 'Compulsory Third Party',
      'Comprehensive': 'Comprehensive',
      'Third Party Fire and Theft': 'Third Party Fire and Theft',
      'Third Party Property Damage': 'Third Party Property Damage',
      'Compulsory Third Party': 'Compulsory Third Party'
    };
    
    return coverTypeMap[value.toString()] || value.toString();
  };

  // Helper to map no claim bonus from numeric/backend value to display name
  const getNoClaimBonusDisplay = (value: any): string => {
    if (!value) return 'None';
    
    // Convert to string for comparison
    const stringValue = value.toString();
    
    // Map numeric/backend values to display format
    const noClaimBonusMap: { [key: string]: string } = {
      // Numeric values (from database)
      '0': 'None',
      '1': '12 Months (1 Year)',
      '2': '24 Months (2 Year)',
      '3': '36 Months (3 Year)',
      '4': '48 Months (4 Year)',
      '5': '60 Months (5 Year)',
      '6': '72 Months (6 Year)',
      '7': '84 Months (7 Year)',
      '8': '96 Months (8 Year)',
      '9': '108 Months (9 Year)',
      '10': '120 Months (10 Year)',
      '11': 'More than 120 Months (More than 10 Years)',
      // String values (already formatted)
      'None': 'None',
      '12 Months (1 Year)': '12 Months (1 Year)',
      '24 Months (2 Year)': '24 Months (2 Year)',
      '36 Months (3 Year)': '36 Months (3 Year)',
      '48 Months (4 Year)': '48 Months (4 Year)',
      '60 Months (5 Year)': '60 Months (5 Year)',
      '72 Months (6 Year)': '72 Months (6 Year)',
      '84 Months (7 Year)': '84 Months (7 Year)',
      '96 Months (8 Year)': '96 Months (8 Year)',
      '108 Months (9 Year)': '108 Months (9 Year)',
      '120 Months (10 Year)': '120 Months (10 Year)',
      'More than 120 Months (More than 10 Years)': 'More than 120 Months (More than 10 Years)',
      // Backend format mapping
      '1 Year': '12 Months (1 Year)',
      '2 Year': '24 Months (2 Year)',
      '3 Year': '36 Months (3 Year)',
      '4 Year': '48 Months (4 Year)',
      '5 Year Plus': '60 Months (5 Year)'
    };
    
    return noClaimBonusMap[stringValue] || stringValue;
  };

    // Helper to get driver license display text
  const getDriverLicenseDisplay = (personId: any, personsOfInterest: any[] = [], clientData?: any): string => {
    if (!personId && !clientData) return '';

    let rawCodes: any = null;

    // 1. Try to find the person in personsOfInterest
    if (typeof personId === 'object' && personId !== null) {
      rawCodes = personId.drivers_license_codes || personId.license_codes;
    } else if (personId) {
      const person = personsOfInterest.find((p: any) => String(p.id) === String(personId));
      if (person) {
        rawCodes = person.drivers_license_codes || person.license_codes;
      }
    }

    // 2. If not found, but we are dealing with the main client, use client data
    const isClient = !personId || String(personId).includes('client-') || (clientData && String(personId) === String(clientData.id));
    if (!rawCodes && isClient && clientData?.drivers_license_codes) {
      rawCodes = clientData.drivers_license_codes;
    }

    if (!rawCodes) return '';

    // 3. Format the license codes
    let licenseTexts: string[] = [];

    if (Array.isArray(rawCodes)) {
      licenseTexts = rawCodes.map((l: any) => {
        if (typeof l === 'string') return l;
        const code = l.code || '';
        const issue = l.dateOfFirstIssue || l.date_of_first_issue;
        const expiry = l.expiresOn || l.expires_on;
        return formatLicenseString(code, issue, expiry);
      });
    } else if (typeof rawCodes === 'string') {
      // Handle pipe-delimited format: "EC|1990-01-01|2030-01-01"
      // Assuming multiple licenses could be comma-separated: "EC|1990-01-01|2030-01-01, B|2010-01-01|2040-01-01"
      const licenseBlocks = rawCodes.split(',');
      licenseTexts = licenseBlocks.map(block => {
        const parts = block.split('|').map(s => s.trim());
        if (parts.length >= 3) {
          return formatLicenseString(parts[0], parts[1], parts[2]);
        }
        return block; // fallback if not in expected format
      });
    }

    const cleanTexts = licenseTexts.filter(Boolean);
    return cleanTexts.length > 0 ? ` - License: ${cleanTexts.join(', ')}` : '';
  };

  // Helper to format a single license entry
  const formatLicenseString = (code: string, issueDate?: string, expiryDate?: string) => {
    let result = code;
    const formattedDates = [];
    
    if (issueDate) {
      const d = new Date(issueDate);
      if (!isNaN(d.getTime())) formattedDates.push(`Issued: ${d.toLocaleDateString('en-ZA')}`);
    }
    if (expiryDate) {
      const d = new Date(expiryDate);
      if (!isNaN(d.getTime())) formattedDates.push(`Expires: ${d.toLocaleDateString('en-ZA')}`);
    }
    
    if (formattedDates.length > 0) {
      result += ` (${formattedDates.join(', ')})`;
    }
    return result;
  };

    // Helper for Other Drivers (handles array or single value)
  const getOtherDriversDisplay = (drivers: any, clientData: any, persons: any[] = []): string => {
    if (!drivers) return 'None';
    
    const driverArray = Array.isArray(drivers) ? drivers : [drivers];
    
    const names = driverArray
      .map((d: any) => getPersonName(d, clientData, persons))
      .filter(name => name && name !== '—');
    
    return names.length > 0 ? names.join(', ') : 'None';
  };

  // Helper to get person name (already in PDF - reuse here)
  const getPersonName = (personId: any, clientData: any, personsOfInterest: any[] = []): string => {
    if (!personId) return '—';

    // If it's already an object with a name
    if (typeof personId === 'object') {
      const name = personId.full_name || personId.name || personId.fullName;
      if (name) return name;
    }

    // Client case
    if (typeof personId === 'string' && personId.startsWith('client-')) {
      return `${clientData?.name || ''} ${clientData?.surname || ''}`.trim() || 'Client';
    }

    // Lookup in persons of interest
    const person = personsOfInterest.find((p: any) => 
      p.id?.toString() === personId.toString()
    );

    if (person) {
      return `${person.name || ''} ${person.surname || ''}`.trim() || '—';
    }

    return String(personId);
  };

  const handleCommentFormChange = (field: keyof typeof commentForm) => 
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setCommentForm(prev => ({
        ...prev,
        [field]: e.target.value,
      }));
    };

    const handleAcceptClick = () => {
  setShowAcceptModal(true);
};

const handleAcceptOnline = () => {
  // Open signature modal instead of external service
  setIsSignatureModalOpen(true);
};

const handleAcceptOnlinePreview = async () => {
  if (isSendingPDF) return;
  
  if (!quoteId) {
    setSaveStatus('error');
    setSaveMessage("Quote ID is required");
    return;
  }

  const clientEmail = client?.email;
  if (!clientEmail) {
    setSaveStatus('error');
    setSaveMessage("Client email address is required to send the signature link");
    return;
  }

  try {
    setIsSendingPDF(true);
    setSaveStatus('loading');
    setSaveMessage(`Generating signature link and sending email to ${clientEmail}...`);

    const token = localStorage.getItem('token');
    if (!token) {
      setSaveStatus('error');
      setSaveMessage("Authentication required");
      return;
    }

    // Get the accept data that was passed via location state or localStorage
    let acceptDataForLink = acceptData;
    
    // If in preview mode, get data from localStorage (passed from Accept Quotation modal)
    if (isPreview) {
      const savedData = localStorage.getItem('proposalAcceptData');
      if (savedData) {
        acceptDataForLink = JSON.parse(savedData);
        console.log("📋 Using saved accept data for signature link:", acceptDataForLink);
      }
    }

    // ✅ Get the correct quote number from acceptData
    const quoteNumber = acceptDataForLink?.insurers?.[0]?.insurerQuotationNumber || 
                        acceptDataForLink?.insurers?.[0]?.quotation_ref ||
                        acceptDataForLink?.quotation_ref ||
                        acceptDataForLink?.insurerQuotationNumber ||
                        quoteId;

    console.log("🔵 Quote number being sent to backend:", quoteNumber);

    const backendBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const signatureLink = `${backendBaseUrl}/api/proposal/view/${quoteId}?quoteNumber=${encodeURIComponent(quoteNumber)}`;

    // Call backend to generate signature link and send email
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/quotes/${quoteId}/send-signature-link`,
      {
        clientEmail: clientEmail,
        clientName: `${client?.name || ''} ${client?.surname || ''}`.trim(),
        quoteId: quoteId,
        clientId: client?.id,
        acceptData: acceptDataForLink,
        signatureLink: signatureLink,
        premiumAmount: acceptDataForLink?.premium || acceptDataForLink?.insurers?.[0]?.premium || '',
        brokerFee: acceptDataForLink?.brokerFee || acceptDataForLink?.insurers?.[0]?.brokerFee || '',
        managementFee: acceptDataForLink?.managementFee || acceptDataForLink?.insurers?.[0]?.managementFee || '',
        expiryHours: 48,
        insurerQuotationNumber: quoteNumber  // ✅ Pass the quote number explicitly
      },
      {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000
      }
    );

    console.log("📧 Signature link response:", response.data);

    if (response.data.success) {
      setSaveStatus('success');
      setSaveMessage(`✅ Secure signature link sent to ${clientEmail}. Link expires in 48 hours.`);
      
      setTimeout(() => {
        navigate(`/quotes/${quoteId}`, {
          state: { clientId: client?.id, selectedQuoteId: quoteId }
        });
      }, 3000);
    } else {
      setSaveStatus('error');
      setSaveMessage("Failed to send signature link: " + (response.data.message || "Unknown error"));
    }

  } catch (error: any) {
    console.error("Error sending signature link:", error);
    
    let errorMessage = "Failed to send signature link";
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.code === 'ECONNABORTED') {
      errorMessage = "Request timed out. Please try again.";
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    setSaveStatus('error');
    setSaveMessage(`❌ ${errorMessage}`);
    
  } finally {
    setIsSendingPDF(false);
  }
};

const handleSignatureSave = async (signatureDataUrl: string) => {
  try {
    setIsProcessingSignature(true);
    setShowAcceptModal(false);
    
    const token = localStorage.getItem('token');
    
    if (!token) {
      alert("Authentication required");
      return;
    }

    // Collect ALL form data from the current state
    const formData = {
      client,
      address,
      broker,
      importantQuestions,
      debitOrderQuestions,
      managementFeesQuestions,
      serviceProviderQuestions,
      clientClaims,
      declarationQuestions,
      propertyData,
      propertyQuestions,
      householdContents,
      vehicles,
      vehicleQuestions,
      trailersCaravans,
      trailerCaravanQuestions,
      watercraft,
      watercraftQuestions,
      allRiskItems,
      allRiskQuestions,
      valueAddedProducts,
      valueAddedQuestions,
      debitOrder: debitOrder,
      commentForm: commentForm,
      responses: {
        debitOrder: debitOrderResponses,
        managementFees: managementFeesResponses,
        declaration: declarationResponses,
        property: propertyQuestionResponses,
        householdContent: householdContentQuestionResponses,
        vehicle: vehicleQuestionResponses,
        trailerCaravan: trailerCaravanQuestionResponses,
        watercraft: watercraftQuestionResponses,
        allRisk: allRiskQuestionResponses,
        valueAdded: valueAddedQuestionResponses
      },
      signature: signatureDataUrl,
      signatureDate: new Date().toISOString(),
      signatureName: `${client?.name || ''} ${client?.surname || ''}`.trim()
    };

    console.log("📤 Sending signed proposal for email");

    // Show loading notification
    alert("Generating signed PDF and sending email... Please wait.");

    // Send to your existing PDF email endpoint
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/proposal/send-pdf/${quoteId}`,
      {
        clientIdNumber: client?.id_passport_number || client?.id_no,
        clientEmail: client?.email,
        clientName: `${client?.name || ''} ${client?.surname || ''}`.trim(),
        formData
      },
      {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 60000 // 60 seconds timeout for PDF generation
      }
    );

    console.log("✅ Signed proposal response:", response.data);

    if (response.data.success) {
      setIsSignatureModalOpen(false);
      alert(`✅ Proposal signed successfully!\n\nA copy has been sent to ${client?.email}`);
    } else {
      alert("Failed to send signed proposal: " + (response.data.message || "Unknown error"));
    }

  } catch (error: any) {
    console.error("Error processing signature:", error);
    
    let errorMessage = "Failed to process signature";
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    alert(`❌ ${errorMessage}\n\nPlease try again.`);
    
  } finally {
    setIsProcessingSignature(false);
  }
};

const handleAcceptEmail = async () => {
  if (isSendingPDF) return;
  
  if (!quoteId) {
    setSaveStatus('error');
    setSaveMessage("Quote ID is required");
    return;
  }

  const clientEmail = client?.email;
  if (!clientEmail) {
    setSaveStatus('error');
    setSaveMessage("Client email address is required to send the proposal");
    return;
  }

  // Get client ID number - check multiple possible field names
  const clientIdNumber = client?.id_passport_number || client?.id_no || client?.idNumber || client?.id_number || '';
  
  console.log("📝 Sending proposal with:", {
    clientEmail,
    clientIdNumber: clientIdNumber || 'NOT FOUND',
    clientName: client ? `${client.name || ''} ${client.surname || ''}`.trim() : 'Client'
  });

  try {
    setIsSendingPDF(true);
    setSaveStatus('loading');
    setSaveMessage(`Generating PDF and sending email to ${clientEmail}...`);

    const token = localStorage.getItem('token');
    if (!token) {
      setSaveStatus('error');
      setSaveMessage("Authentication required");
      return;
    }

    setShowAcceptModal(false);

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/proposal/send-pdf/${quoteId}`,
      {
        clientIdNumber: clientIdNumber, // Add this back!
        clientEmail: clientEmail,
        clientName: client ? `${client.name || ''} ${client.surname || ''}`.trim() : 'Client',
        acceptData: acceptData,
        premiumAmount: premiumAmount,
        brokerFee: brokerFee,
        managementFee: managementFee,
        formData: {
          debitOrder: debitOrder,
          commentForm: commentForm,
          responses: {
            debitOrder: debitOrderResponses,
            managementFees: managementFeesResponses,
            declaration: declarationResponses,
            property: propertyQuestionResponses,
            householdContent: householdContentQuestionResponses,
            vehicle: vehicleQuestionResponses,
            trailerCaravan: trailerCaravanQuestionResponses,
            watercraft: watercraftQuestionResponses,
            allRisk: allRiskQuestionResponses,
            valueAdded: valueAddedQuestionResponses
          }
        }
      },
      {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 60000,
      }
    );

    if (response.data.success) {
      setSaveStatus('success');
      setSaveMessage(`✅ Proposal sent successfully to ${clientEmail}`);
      
      setTimeout(() => {
        navigate(`/quotes/${quoteId}`, {
          state: { clientId: client?.id, selectedQuoteId: quoteId }
        });
      }, 3000);
    } else {
      setSaveStatus('error');
      setSaveMessage("Failed to send proposal: " + (response.data.message || "Unknown error"));
    }

  } catch (error: any) {
    console.error("Error sending proposal:", error);
    
    let errorMessage = "Failed to send proposal";
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.code === 'ECONNABORTED') {
      errorMessage = "PDF generation is taking too long. Please try again.";
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    setSaveStatus('error');
    setSaveMessage(`❌ ${errorMessage}`);
    
  } finally {
    setIsSendingPDF(false);
  }
};

const handleAcceptEmailPreview = async () => {
  if (isSendingPDF) return;
  
  if (!quoteId) {
    setSaveStatus('error');
    setSaveMessage("Quote ID is required");
    return;
  }

  const clientEmail = client?.email;
  if (!clientEmail) {
    setSaveStatus('error');
    setSaveMessage("Client email address is required to send the proposal");
    return;
  }

  // Get client ID number - check multiple possible field names
  const clientIdNumber = client?.id_passport_number || client?.id_no || client?.idNumber || client?.id_number || '';
  
  // Get accept data from localStorage (passed from Accept Quotation modal)
  let acceptDataForEmail = acceptData;
  if (isPreview) {
    const savedData = localStorage.getItem('proposalAcceptData');
    if (savedData) {
      acceptDataForEmail = JSON.parse(savedData);
      console.log("📋 Using saved accept data for email proposal:", acceptDataForEmail);
    }
  }

  console.log("📝 Sending proposal with:", {
    clientEmail,
    clientIdNumber: clientIdNumber || 'NOT FOUND',
    clientName: client ? `${client.name || ''} ${client.surname || ''}`.trim() : 'Client'
  });

  try {
    setIsSendingPDF(true);
    setSaveStatus('loading');
    setSaveMessage(`Generating PDF and sending email to ${clientEmail}...`);

    const token = localStorage.getItem('token');
    if (!token) {
      setSaveStatus('error');
      setSaveMessage("Authentication required");
      return;
    }

    const activeInsurer = acceptDataForEmail?.insurers?.[0] || acceptDataForEmail;
    const brokerFeeValue = acceptDataForEmail?.brokerFee || acceptDataForEmail?.insurers?.[0]?.brokerFee || brokerFee;
    const managementFeeValue = acceptDataForEmail?.managementFee || acceptDataForEmail?.insurers?.[0]?.managementFee || managementFee;
    const premiumValue = acceptDataForEmail?.premium || acceptDataForEmail?.insurers?.[0]?.premium || premiumAmount;

    // Get broker/user ID from localStorage
//const brokerUserId = localStorage.getItem('userId') || localStorage.getItem('brokerId') || user?.id || '';

// Get broker/user ID from multiple sources
const brokerUserId = localStorage.getItem('userId') || localStorage.getItem('brokerId') || user?.id || '';

console.log("🔍 Broker ID being sent:", brokerUserId);

const response = await axios.post(
  `${process.env.REACT_APP_API_URL}/api/proposal/send-pdf/${quoteId}`,
  {
    clientIdNumber: clientIdNumber,
    clientEmail: clientEmail,
    clientName: client ? `${client.name || ''} ${client.surname || ''}`.trim() : 'Client',
    acceptData: acceptDataForEmail,
    premiumAmount: premiumValue,
    brokerFee: brokerFeeValue,
    managementFee: managementFeeValue,
    brokerUserId: brokerUserId,
    userId: brokerUserId,
    formData: {
      debitOrder: debitOrder,
      commentForm: commentForm,
      responses: {
        debitOrder: debitOrderResponses,
        managementFees: managementFeesResponses,
        declaration: declarationResponses,
        property: propertyQuestionResponses,
        householdContent: householdContentQuestionResponses,
        vehicle: vehicleQuestionResponses,
        trailerCaravan: trailerCaravanQuestionResponses,
        watercraft: watercraftQuestionResponses,
        allRisk: allRiskQuestionResponses,
        valueAdded: valueAddedQuestionResponses
      }
    }
  },
  {
    headers: { 
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    timeout: 60000,
  }
);

    if (response.data.success) {
      setSaveStatus('success');
      setSaveMessage(`✅ Proposal sent successfully to ${clientEmail}`);
      
      setTimeout(() => {
        navigate(`/quotes/${quoteId}`, {
          state: { clientId: client?.id, selectedQuoteId: quoteId }
        });
      }, 3000);
    } else {
      setSaveStatus('error');
      setSaveMessage("Failed to send proposal: " + (response.data.message || "Unknown error"));
    }

  } catch (error: any) {
    console.error("Error sending proposal:", error);
    
    let errorMessage = "Failed to send proposal";
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.code === 'ECONNABORTED') {
      errorMessage = "PDF generation is taking too long. Please try again.";
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    setSaveStatus('error');
    setSaveMessage(`❌ ${errorMessage}`);
    
  } finally {
    setIsSendingPDF(false);
  }
};

const handleCloseModal = () => {
  setShowAcceptModal(false);
};

  useEffect(() => {
  const saved = localStorage.getItem('proposalAcceptData');
  if (saved) {
    const data = JSON.parse(saved);
    console.log("📊 Raw proposal acceptData from localStorage:", data);
    
    // ✅ Ensure data has the correct structure for quote number display
    let normalizedData = { ...data };
    
    // If data doesn't have insurers array but has direct fields, create it
    if (!normalizedData.insurers && (normalizedData.insurerQuotationNumber || normalizedData.quotation_ref)) {
      normalizedData.insurers = [{
        id: normalizedData.insurerId || normalizedData.id,
        name: normalizedData.name,
        type: normalizedData.type,
        premium: normalizedData.premium,
        brokerFee: normalizedData.brokerFee,
        managementFee: normalizedData.managementFee,
        insurerQuotationNumber: normalizedData.insurerQuotationNumber || normalizedData.quotation_ref,
        quotation_ref: normalizedData.quotation_ref || normalizedData.insurerQuotationNumber
      }];
      console.log("✅ Created insurers array from direct fields:", normalizedData.insurers);
    }
    
    // Also ensure the top-level quotation_ref is set
    if (normalizedData.insurers?.[0]?.insurerQuotationNumber && !normalizedData.quotation_ref) {
      normalizedData.quotation_ref = normalizedData.insurers[0].insurerQuotationNumber;
    }
    
    console.log("📊 Normalized acceptData for proposal form:", normalizedData);
    setAcceptData(normalizedData);
    
    // Extract premium amount from acceptData if available
    const premiumValue = normalizedData?.premium || normalizedData?.insurers?.[0]?.premium;
    if (premiumValue) {
      setPremiumAmount(formatCurrency(parseFloat(premiumValue)));
    }
    
    // Also update debitOrder premiumPayable with the premium value
    if (premiumValue) {
      setDebitOrder(prev => ({
        ...prev,
        premiumPayable: `R ${parseFloat(premiumValue).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`
      }));
    }
  }
}, []);

// Update premiumPayable when acceptData changes
useEffect(() => {
  if (acceptData?.premium) {
    setDebitOrder(prev => ({
      ...prev,
      premiumPayable: `R ${parseFloat(acceptData.premium).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`
    }));
  } else if (acceptData?.insurers?.[0]?.premium) {
    setDebitOrder(prev => ({
      ...prev,
      premiumPayable: `R ${parseFloat(acceptData.insurers[0].premium).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`
    }));
  }
}, [acceptData]);

  // Fetch Important category questions
useEffect(() => {
  const fetchImportantQuestions = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error("No token found");
      setLoadingQuestions(false);
      return;
    }

    try {
      // Get insurerId from multiple sources
      let insurerId: number | undefined;
      
      // First try from acceptData (if coming from modal)
      if (acceptData?.insurers && Array.isArray(acceptData.insurers) && acceptData.insurers.length > 0) {
        const firstInsurer = acceptData.insurers[0];
        insurerId = firstInsurer?.id || firstInsurer?.insurerId;
        console.log("✅ Found insurerId from acceptData.insurers:", insurerId);
      }
      
      // If not found, try to get from quote data
      if (!insurerId && quoteId) {
        try {
          const quoteResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/quotes/${quoteId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          const quoteData = quoteResponse.data.data || quoteResponse.data;
          insurerId = quoteData?.administrator_id || quoteData?.underwriter_id;
          console.log("✅ Found insurerId from quote data:", insurerId);
        } catch (quoteError) {
          console.error("Failed to fetch quote for insurer ID:", quoteError);
        }
      }
      
      // If still no insurerId, don't fetch
      if (!insurerId) {
        console.warn("⚠️ No insurer ID found, cannot fetch important questions");
        setImportantQuestions([]);
        setLoadingQuestions(false);
        return;
      }
      
      console.log(`🔍 Fetching important questions for insurer ID: ${insurerId}`);
      
      const response = await axios.get(
  `${process.env.REACT_APP_API_URL}/api/applicationimportant?insurerId=${insurerId}`,
  { headers: { Authorization: `Bearer ${token}` } }
);

console.log("✅ Important questions API response:", response.data);

// Handle the wrapped response structure { success: true, data: [...] }
let questions = [];
if (response.data?.data && Array.isArray(response.data.data)) {
  questions = response.data.data;
} else if (Array.isArray(response.data)) {
  questions = response.data;
} else {
  questions = [];
}

console.log(`📋 Loaded ${questions.length} important questions for insurer ${insurerId}:`, questions);
setImportantQuestions(questions);

    } catch (error: any) {
      console.error("❌ Error loading important questions:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      setImportantQuestions([]);
    } finally {
      setLoadingQuestions(false);
    }
  };

  // Run when we have either acceptData or quoteId
  if (acceptData || quoteId) {
    fetchImportantQuestions();
  } else {
    setLoadingQuestions(false);
  }
}, [acceptData, quoteId]);

  // Fetch Debit Order questions
  useEffect(() => {
    const fetchDebitOrderQuestions = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error("No token found");
        setLoadingDebitOrderQuestions(false);
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/debitorderquestions`,
          {
              headers: { Authorization: `Bearer ${token}` },
          }
        );

        let questions = response.data.data || response.data || [];
        if (!Array.isArray(questions)) questions = [];
        setDebitOrderQuestions(questions);

        // Initialize responses with false
        const initialResponses: { [key: number]: boolean } = {};
        questions.forEach((q: DebitOrderQuestion) => {
          if (q.id) initialResponses[q.id] = false;
        });
        setDebitOrderResponses(initialResponses);
      } catch (error) {
        console.error("Error loading debit order questions:", error);
      } finally {
        setLoadingDebitOrderQuestions(false);
      }
    };

    fetchDebitOrderQuestions();
  }, []);

  // Fetch Management Fees questions
  useEffect(() => {
  const fetchManagementFeesQuestions = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error("❌ No token found");
      setLoadingManagementFees(false);
      return;
    }

    try {
      console.log("🚀 Starting management fees fetch");
      console.log("📊 acceptData:", acceptData);
      
      // Check if we have insurer info in acceptData
      let insurerId: number | undefined;
      
      if (acceptData?.insurers && Array.isArray(acceptData.insurers) && acceptData.insurers.length > 0) {
        // Get the first insurer from the array (or whichever one is relevant)
        const firstInsurer = acceptData.insurers[0];
        console.log("📋 First insurer from acceptData:", firstInsurer);
        
        // Try different possible field names
        insurerId = firstInsurer?.id || firstInsurer?.insurerId || firstInsurer?.administrator_id || firstInsurer?.underwriter_id;
        
        if (insurerId) {
          console.log("✅ Found insurerId from acceptData.insurers:", insurerId);
        }
      }
      
      // If not found in acceptData, try to get from quote data
      if (!insurerId && quoteId) {
        console.log("🔍 insurerId not in acceptData, checking quote data...");
        
        const quoteResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/quotes/${quoteId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const quoteData = quoteResponse.data.data || quoteResponse.data;
        console.log("📋 Quote data for insurer lookup:", quoteData);
        
        // Try administrator_id first, then underwriter_id
        insurerId = quoteData?.administrator_id || quoteData?.underwriter_id;
        
        if (insurerId) {
          console.log("✅ Found insurerId from quote data:", insurerId);
        }
      }
      
      if (!insurerId) {
        console.warn("⚠️ Could not find insurerId from any source");
        console.log("📊 acceptData.insurers:", acceptData?.insurers);
        console.log("📊 quoteId:", quoteId);
        setLoadingManagementFees(false);
        return;
      }

      // Now fetch management fees using the found insurerId
      console.log("🔍 Fetching management fees for insurerId:", insurerId);
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/managementfees?insurerId=${insurerId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log("✅ Management fees API response:", response.data);
      
      let questions = response.data || [];
      if (!Array.isArray(questions)) {
        console.warn("⚠️ Response is not an array:", questions);
        questions = [];
      }
      
      console.log(`📋 Loaded ${questions.length} management fees questions:`, questions);
      setManagementFeesQuestions(questions);

      // Initialize empty responses
      const initialResponses: { [key: number]: string } = {};
      questions.forEach((q: ApplicationQuestion) => {
        if (q.id) initialResponses[q.id] = '';
      });
      setManagementFeesResponses(initialResponses);

    } catch (error: any) {
      console.error("❌ Error loading management fees questions:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });
      setManagementFeesQuestions([]);
    } finally {
      setLoadingManagementFees(false);
    }
  };

  // Run when we have either acceptData (with insurers) or quoteId
  if (acceptData || quoteId) {
    console.log("🎯 Starting fetch with acceptData or quoteId:", { 
      hasAcceptData: !!acceptData, 
      hasQuoteId: !!quoteId,
      acceptDataInsurers: acceptData?.insurers 
    });
    fetchManagementFeesQuestions();
  } else {
    console.log("⏳ Waiting for data...");
    setLoadingManagementFees(false);
  }
}, [acceptData, quoteId]);

// Fetch Service Provider Questions
useEffect(() => {
  const fetchServiceProviderQuestions = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error("No token found");
      setLoadingServiceProviderQuestions(false);
      return;
    }

    // Get insurerId (same logic as management fees)
    let insurerId: number | undefined;
    
    if (acceptData?.insurers && Array.isArray(acceptData.insurers) && acceptData.insurers.length > 0) {
      const firstInsurer = acceptData.insurers[0];
      insurerId = firstInsurer?.id || firstInsurer?.insurerId;
      
      if (insurerId) {
        console.log("✅ Using insurerId from acceptData for service provider questions:", insurerId);
      }
    }
    
    // If not found in acceptData, try to get from quote data
    if (!insurerId && quoteId) {
      console.log("🔍 insurerId not in acceptData, checking quote data for service provider questions...");
      
      try {
        const quoteResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/quotes/${quoteId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const quoteData = quoteResponse.data.data || quoteResponse.data;
        insurerId = quoteData?.administrator_id;
        
        if (insurerId) {
          console.log("✅ Found insurerId from quote data for service provider questions:", insurerId);
        }
      } catch (error) {
        console.error("Failed to fetch quote for service provider questions:", error);
      }
    }
    
    if (!insurerId) {
      console.warn("⚠️ Could not find insurerId for service provider questions");
      setLoadingServiceProviderQuestions(false);
      return;
    }

    try {
      console.log("🚀 Fetching service provider questions for insurerId:", insurerId);
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/serviceproviderquestions?insurerId=${insurerId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log("✅ Service provider questions API response:", response.data);
      
      let questions = response.data.data || response.data || [];
      if (!Array.isArray(questions)) {
        console.warn("⚠️ Response is not an array:", questions);
        questions = [];
      }
      
      console.log(`📋 Loaded ${questions.length} service provider questions:`, questions);
      setServiceProviderQuestions(questions);
    } catch (error: any) {
      console.error("❌ Error loading service provider questions:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });
      setServiceProviderQuestions([]);
    } finally {
      setLoadingServiceProviderQuestions(false);
    }
  };

  // Run when we have either acceptData (with insurers) or quoteId
  if (acceptData || quoteId) {
    console.log("🎯 Starting service provider questions fetch with acceptData or quoteId:", { 
      hasAcceptData: !!acceptData, 
      hasQuoteId: !!quoteId,
      acceptDataInsurers: acceptData?.insurers 
    });
    fetchServiceProviderQuestions();
  } else {
    console.log("⏳ Waiting for data for service provider questions...");
    setLoadingServiceProviderQuestions(false);
  }
}, [acceptData, quoteId]);

// Fetch Client Claims
useEffect(() => {
  const fetchClientClaims = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error("No token found");
      setLoadingClaims(false);
      return;
    }

    try {
      console.log("🚀 Fetching client claims for quote:", quoteId);
      
      // Using the endpoint with query parameter (recommended based on the route)
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/clients-personal-claims?quote_id=${quoteId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log("✅ Client claims API response:", response.data);
      
      // The response structure might be different - adjust based on your API
      let claims = response.data.data || response.data || [];
      if (!Array.isArray(claims)) {
        console.warn("⚠️ Claims response is not an array:", claims);
        claims = [];
      }
      
      console.log(`📋 Loaded ${claims.length} client claims:`, claims);
      setClientClaims(claims);
    } catch (error: any) {
      console.error("❌ Error loading client claims:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });
      
      // If the query param version fails, try the path param version
      if (error.response?.status === 404 || error.response?.status === 400) {
        console.log("🔄 Trying alternative endpoint with path parameter...");
        try {
          const altResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/clients-personal-claims/by-quote/${quoteId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          
          let altClaims = altResponse.data.data || altResponse.data || [];
          if (!Array.isArray(altClaims)) altClaims = [];
          console.log(`✅ Loaded ${altClaims.length} claims from alternative endpoint`);
          setClientClaims(altClaims);
        } catch (altError: any) {
          console.error("❌ Alternative endpoint also failed:", altError.message);
          setClientClaims([]);
        }
      } else {
        setClientClaims([]);
      }
    } finally {
      setLoadingClaims(false);
    }
  };

  if (quoteId) {
    fetchClientClaims();
  } else {
    setLoadingClaims(false);
  }
}, [quoteId]);

// Fetch Previous Insurance
useEffect(() => {
  const fetchPreviousInsurance = async () => {
    if (!quoteId) {
      setLoadingPreviousInsurance(false);
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      console.error("No token found");
      setLoadingPreviousInsurance(false);
      return;
    }

    try {
      console.log("🚀 Fetching previous insurance for quote:", quoteId);
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/previous-insurances?quote_id=${quoteId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log("✅ Previous insurance API response:", response.data);
      
      let insuranceData = response.data.data || response.data || [];
      if (!Array.isArray(insuranceData)) insuranceData = [];
      
      console.log(`📋 Loaded ${insuranceData.length} previous insurance records`);
      setPreviousInsurance(insuranceData);
    } catch (error: any) {
      console.error("❌ Error loading previous insurance:", error);
      setPreviousInsurance([]);
    } finally {
      setLoadingPreviousInsurance(false);
    }
  };

  fetchPreviousInsurance();
}, [quoteId]);

// Fetch Declaration Questions
useEffect(() => {
  const fetchDeclarationQuestions = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error("No token found");
      setLoadingDeclarationQuestions(false);
      return;
    }

    try {
      console.log("🚀 Fetching declaration questions...");
      
      // First, try to get questions using the endpoint that doesn't require quote_id
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/application-quotes-questions/declaration`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log("✅ Declaration questions API response:", response.data);
      
      // This endpoint returns questions directly
      let questions = response.data || [];
      
      if (Array.isArray(questions) && questions.length > 0) {
        console.log(`📋 Loaded ${questions.length} declaration questions:`, questions);
        
        // These should already be in the right format
        setDeclarationQuestions(questions);

        // Initialize responses
        const initialResponses: { [key: number]: string | boolean } = {};
        questions.forEach((q: DeclarationQuestion) => {
          if (q.id) {
            // Determine if it's a yes/no question
            const isYesNoQuestion = q.question.toLowerCase().includes('agree') || 
                                  q.question.toLowerCase().startsWith('have you') ||
                                  q.question.toLowerCase().includes('any') ||
                                  q.question.toLowerCase().includes('do you') ||
                                  q.question.toLowerCase().includes('confirm') ||
                                  q.question.toLowerCase().includes('acknowledge') ||
                                  q.question.toLowerCase().includes('declare') ||
                                  q.question.toLowerCase().includes('consent');
            initialResponses[q.id] = isYesNoQuestion ? false : '';
          }
        });
        setDeclarationResponses(initialResponses);
        
      } else {
        // Try fallback: get from quote data
        console.log("📭 No declaration questions from API, trying quote data...");
        await fetchFromQuoteData();
      }

    } catch (error: any) {
      console.error("❌ Error loading declaration questions from /declaration endpoint:", {
        message: error.message,
        status: error.response?.status,
      });
      
      // Try the quote-specific endpoint as backup
      console.log("🔄 Trying quote-specific endpoint...");
      await tryQuoteSpecificEndpoint();
    } finally {
      setLoadingDeclarationQuestions(false);
    }
    
    // Helper function for quote-specific endpoint
    async function tryQuoteSpecificEndpoint() {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/application-quotes-questions/quote/${quoteId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        let questions = response.data || [];
        
        if (Array.isArray(questions) && questions.length > 0) {
          console.log(`📋 Loaded ${questions.length} declaration questions from quote ${quoteId}:`, questions);
          
          // Transform database rows
          const transformedQuestions: DeclarationQuestion[] = questions.map((row: any) => ({
            id: row.id,
            question: row.question,
            is_default: row.is_selected || false,
            response_type: row.question_type || 'text',
            created_at: row.created_at,
            updated_at: row.updated_at
          }));
          
          setDeclarationQuestions(transformedQuestions);

          // Initialize responses
          const initialResponses: { [key: number]: string | boolean } = {};
          transformedQuestions.forEach((q: DeclarationQuestion) => {
            if (q.id) {
              const isYesNoQuestion = q.question.toLowerCase().includes('agree') || 
                                    q.question.toLowerCase().startsWith('have you') ||
                                    q.question.toLowerCase().includes('any') ||
                                    q.question.toLowerCase().includes('do you') ||
                                    q.question.toLowerCase().includes('confirm') ||
                                    q.question.toLowerCase().includes('acknowledge') ||
                                    q.question.toLowerCase().includes('declare') ||
                                    q.question.toLowerCase().includes('consent');
              initialResponses[q.id] = isYesNoQuestion ? false : '';
            }
          });
          setDeclarationResponses(initialResponses);
          
        } else {
          // Try fallback from quote data
          console.log(`📭 No questions for quote ${quoteId}, trying quote data fallback...`);
          await fetchFromQuoteData();
        }
      } catch (error) {
        console.error("❌ Quote-specific endpoint failed:", error);
        await fetchFromQuoteData();
      }
    }
    
    // Helper function to fetch from quote data
    async function fetchFromQuoteData() {
      try {
        const token = localStorage.getItem('token');
        const quoteResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/quotes/${quoteId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const quoteData = quoteResponse.data.data || quoteResponse.data;
        const questions = quoteData?.declaration_questions || [];
        
        if (Array.isArray(questions) && questions.length > 0) {
          console.log(`📋 Loaded ${questions.length} declaration questions from quote data (final fallback)`);
          setDeclarationQuestions(questions);
          
          // Initialize responses
          const initialResponses: { [key: number]: string | boolean } = {};
          questions.forEach((q: DeclarationQuestion) => {
            if (q.id) {
              const isYesNoQuestion = q.question.toLowerCase().includes('agree') || 
                                    q.question.toLowerCase().includes('have you') ||
                                    q.question.toLowerCase().includes('any') ||
                                    q.question.toLowerCase().includes('do you') ||
                                    q.question.toLowerCase().includes('confirm') ||
                                    q.question.toLowerCase().includes('acknowledge') ||
                                    q.question.toLowerCase().includes('declare') ||
                                    q.question.toLowerCase().includes('consent');
              initialResponses[q.id] = isYesNoQuestion ? false : '';
            }
          });
          setDeclarationResponses(initialResponses);
        } else {
          console.log("📭 No declaration questions found anywhere");
          setDeclarationQuestions([]);
        }
      } catch (fallbackError) {
        console.error("❌ All fallbacks failed:", fallbackError);
        setDeclarationQuestions([]);
      }
    }
  };

  if (quoteId) {
    fetchDeclarationQuestions();
  } else {
    setLoadingDeclarationQuestions(false);
  }
}, [quoteId]);

// Fetch Consent Clause Questions
useEffect(() => {
  const fetchConsentClausQuestions = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error("No token found");
      setLoadingConsentClausQuestions(false);
      return;
    }

    try {
      console.log("🚀 Fetching consent clause questions...");

      let insurerId: number | undefined;

      // Try to get insurerId from acceptData first
      if (acceptData?.insurers && Array.isArray(acceptData.insurers) && acceptData.insurers.length > 0) {
        const firstInsurer = acceptData.insurers[0];
        insurerId = firstInsurer?.id || firstInsurer?.insurerId;
      }

      // Fallback to quote data
      if (!insurerId && quoteId) {
        const quoteResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/quotes/${quoteId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const quoteData = quoteResponse.data.data || quoteResponse.data;
        insurerId = quoteData?.administrator_id || quoteData?.underwriter_id;
      }

      if (!insurerId) {
        console.warn("⚠️ No insurer ID found for consent clause questions");
        setConsentClausQuestions([]);
        setLoadingConsentClausQuestions(false);
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/application/questions`,
        {
          params: {
            insurerId: insurerId,
            category: 'Consent Clause'   // Must match backend category
          },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      let questions = [];
      if (Array.isArray(response.data)) {
        questions = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        questions = response.data.data;
      }

      console.log(`✅ Loaded ${questions.length} consent clause questions`);
      setConsentClausQuestions(questions);

      // Initialize responses as boolean (yes/no)
      const initialResponses: { [key: number]: boolean } = {};
      questions.forEach((q: any) => {
        if (q.id) initialResponses[q.id] = false;
      });
      setDeclarationResponses(prev => ({ ...prev, ...initialResponses })); // reuse declarationResponses

    } catch (error: any) {
      console.error("❌ Error loading consent clause questions:", error);
      setConsentClausQuestions([]);
    } finally {
      setLoadingConsentClausQuestions(false);
    }
  };

  if (acceptData || quoteId) {
    fetchConsentClausQuestions();
  } else {
    setLoadingConsentClausQuestions(false);
  }
}, [acceptData, quoteId]);

// Fetch Broker Consent Questions
useEffect(() => {
  const fetchBrokerConsentQuestions = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error("No token found");
      setLoadingBrokerConsentQuestions(false);
      return;
    }

    try {
      console.log("🚀 Fetching broker consent questions...");

      let insurerId: number | undefined;

      if (acceptData?.insurers && Array.isArray(acceptData.insurers) && acceptData.insurers.length > 0) {
        const firstInsurer = acceptData.insurers[0];
        insurerId = firstInsurer?.id || firstInsurer?.insurerId;
      }

      if (!insurerId && quoteId) {
        const quoteResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/quotes/${quoteId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const quoteData = quoteResponse.data.data || quoteResponse.data;
        insurerId = quoteData?.administrator_id || quoteData?.underwriter_id;
      }

      if (!insurerId) {
        console.warn("⚠️ No insurer ID found for broker consent questions");
        setBrokerConsentQuestions([]);
        setLoadingBrokerConsentQuestions(false);
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/application/questions`,
        {
          params: {
            insurerId: insurerId,
            category: 'Broker Consent'   // Must match backend category
          },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      let questions = [];
      if (Array.isArray(response.data)) {
        questions = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        questions = response.data.data;
      }

      console.log(`✅ Loaded ${questions.length} broker consent questions`);
      setBrokerConsentQuestions(questions);

      // Initialize responses
      const initialResponses: { [key: number]: boolean } = {};
      questions.forEach((q: any) => {
        if (q.id) initialResponses[q.id] = false;
      });
      setDeclarationResponses(prev => ({ ...prev, ...initialResponses }));

    } catch (error: any) {
      console.error("❌ Error loading broker consent questions:", error);
      setBrokerConsentQuestions([]);
    } finally {
      setLoadingBrokerConsentQuestions(false);
    }
  };

  if (acceptData || quoteId) {
    fetchBrokerConsentQuestions();
  } else {
    setLoadingBrokerConsentQuestions(false);
  }
}, [acceptData, quoteId]);

// Fetch Property Data
useEffect(() => {
  const fetchPropertyData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error("No token found");
      setLoadingProperty(false);
      return;
    }

    try {
      console.log("🚀 Fetching property data for quote:", quoteId);
      
      // Use the properties endpoint directly
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/properties`,
        {
          params: { quote_id: quoteId },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log("✅ Properties API response:", response.data);
      
      // Handle response format
      let properties = [];
      if (response.data?.data && Array.isArray(response.data.data)) {
        properties = response.data.data;
      } else if (Array.isArray(response.data)) {
        properties = response.data;
      } else {
        properties = [];
      }
      
      if (properties.length > 0) {
        const property = properties[0];
        
        // Map snake_case to camelCase for thatch fields
        const mappedProperty = {
          ...property,
          hasThatchRoof: property.has_thatch_roof || property.hasThatchRoof || false,
          thatchLessThan5m: property.thatch_less_than_5m || property.thatchLessThan5m || false,
          thatchSabsConductor: property.thatch_sabs_conductor || property.thatchSabsConductor || false,
          thatchTreated: property.thatch_treated || property.thatchTreated || false,
        };
        
        console.log("📋 Loaded property with thatch fields:", {
          hasThatchRoof: mappedProperty.hasThatchRoof,
          thatchLessThan5m: mappedProperty.thatchLessThan5m,
          thatchSabsConductor: mappedProperty.thatchSabsConductor,
          thatchTreated: mappedProperty.thatchTreated,
        });
        
        setPropertyData(mappedProperty);
      } else {
        console.log("📭 No property data found for this quote");
        setPropertyData(null);
      }

    } catch (error: any) {
      console.error("❌ Error loading property data:", error);
      setPropertyData(null);
    } finally {
      setLoadingProperty(false);
    }
  };

  if (quoteId) {
    fetchPropertyData();
  } else {
    setLoadingProperty(false);
  }
}, [quoteId]);

// Fetch Property Questions
useEffect(() => {
  const fetchPropertyQuestions = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoadingPropertyQuestions(false);
      return;
    }

    try {
      console.log("🚀 Fetching property questions");
      
      // Get insurer ID from acceptData or quote
      let insurerId: number | undefined;
      
      if (acceptData?.insurers && Array.isArray(acceptData.insurers) && acceptData.insurers.length > 0) {
        const firstInsurer = acceptData.insurers[0];
        insurerId = firstInsurer?.id || firstInsurer?.insurerId;
        console.log("✅ Found insurerId from acceptData.insurers:", insurerId);
      }
      
      // If not found in acceptData, try to get from quote data
      if (!insurerId && quoteId) {
        try {
          const quoteResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/quotes/${quoteId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          const quoteData = quoteResponse.data.data || quoteResponse.data;
          insurerId = quoteData?.administrator_id || quoteData?.underwriter_id;
          console.log("✅ Found insurerId from quote:", insurerId);
        } catch (quoteError) {
          console.error("Failed to fetch quote for insurer ID:", quoteError);
        }
      }
      
      if (!insurerId) {
        console.warn("⚠️ No insurer ID found, cannot fetch property questions");
        setLoadingPropertyQuestions(false);
        return;
      }
      
      // Build params according to backend API
      const params: any = { 
        insurerId: insurerId,
        category: 'Property'  // This must match exactly what's in your getTableName() function
      };
      
      console.log("📤 Sending request to /api/application/questions with params:", params);
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/application/questions`,
        {
          params: params,
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log("✅ Property questions API response:", response.data);
      
      // Handle response - backend returns array directly
      let questions = [];
      if (Array.isArray(response.data)) {
        questions = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        questions = response.data.data;
      } else {
        questions = [];
      }
      
      console.log(`📋 Loaded ${questions.length} property questions:`, questions);
      setPropertyQuestions(questions);

      // Initialize responses
      const initialResponses: { [key: number]: string | boolean } = {};
      questions.forEach((q: PropertyQuestion) => {
        if (q.id) {
          // Check the type field from database
          if (q.type === 'yes_no' || q.type === 'boolean') {
            initialResponses[q.id] = false;
          } else {
            initialResponses[q.id] = '';
          }
        }
      });
      setPropertyQuestionResponses(initialResponses);

    } catch (error: any) {
      console.error("❌ Error loading property questions:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      setPropertyQuestions([]);
    } finally {
      setLoadingPropertyQuestions(false);
    }
  };

  if (acceptData || quoteId) {
    fetchPropertyQuestions();
  } else {
    setLoadingPropertyQuestions(false);
  }
}, [acceptData, quoteId]);

// Handler for property question responses
const handlePropertyQuestionResponse = (questionId: number, value: string | boolean) => {
  setPropertyQuestionResponses(prev => ({
    ...prev,
    [questionId]: value,
  }));
};

// Add missing handler functions
const handleVehicleQuestionResponse = (questionId: number, value: boolean) => {
  setVehicleQuestionResponses(prev => ({
    ...prev,
    [questionId]: value,
  }));
};

const handleTrailerCaravanQuestionResponse = (questionId: number, value: boolean) => {
  setTrailerCaravanQuestionResponses(prev => ({
    ...prev,
    [questionId]: value,
  }));
};

const handleWatercraftQuestionResponse = (questionId: number, value: boolean) => {
  setWatercraftQuestionResponses(prev => ({
    ...prev,
    [questionId]: value,
  }));
};

const handleAllRiskQuestionResponse = (questionId: number, value: boolean) => {
  setAllRiskQuestionResponses(prev => ({
    ...prev,
    [questionId]: value,
  }));
};

const handleValueAddedQuestionResponse = (questionId: number, value: boolean) => {
  setValueAddedQuestionResponses(prev => ({
    ...prev,
    [questionId]: value,
  }));
};

// Fetch Household Contents Questions
useEffect(() => {
  const fetchHouseholdContentQuestions = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoadingHouseholdContentQuestions(false);
      return;
    }

    try {
      console.log("🚀 Fetching household contents questions");
      
      // Get insurer ID from acceptData or quote
      let insurerId: number | undefined;
      
      if (acceptData?.insurers && Array.isArray(acceptData.insurers) && acceptData.insurers.length > 0) {
        const firstInsurer = acceptData.insurers[0];
        insurerId = firstInsurer?.id || firstInsurer?.insurerId;
        console.log("✅ Found insurerId from acceptData.insurers:", insurerId);
      }
      
      // If not found in acceptData, try to get from quote data
      if (!insurerId && quoteId) {
        try {
          const quoteResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/quotes/${quoteId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          const quoteData = quoteResponse.data.data || quoteResponse.data;
          insurerId = quoteData?.administrator_id || quoteData?.underwriter_id;
          console.log("✅ Found insurerId from quote:", insurerId);
        } catch (quoteError) {
          console.error("Failed to fetch quote for insurer ID:", quoteError);
        }
      }
      
      if (!insurerId) {
        console.warn("⚠️ No insurer ID found, cannot fetch household contents questions");
        setLoadingHouseholdContentQuestions(false);
        return;
      }
      
      // Build params according to backend API
      const params: any = { 
        insurerId: insurerId,
        category: 'Household Contents'  // Must match getTableName() mapping
      };
      
      console.log("📤 Sending request to /api/application/questions with params:", params);
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/application/questions`,
        {
          params: params,
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log("✅ Household contents questions API response:", response.data);
      
      // Handle response - backend returns array directly
      let questions = [];
      if (Array.isArray(response.data)) {
        questions = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        questions = response.data.data;
      } else {
        questions = [];
      }
      
      console.log(`📋 Loaded ${questions.length} household contents questions:`, questions);
      setHouseholdContentQuestions(questions);

      // Initialize responses
      const initialResponses: { [key: number]: string | boolean } = {};
      questions.forEach((q: HouseholdContentQuestion) => {
        if (q.id) {
          // Check the type field from database
          if (q.type === 'yes_no' || q.type === 'boolean') {
            initialResponses[q.id] = false;
          } else {
            initialResponses[q.id] = '';
          }
        }
      });
      setHouseholdContentQuestionResponses(initialResponses);

    } catch (error: any) {
      console.error("❌ Error loading household contents questions:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      setHouseholdContentQuestions([]);
    } finally {
      setLoadingHouseholdContentQuestions(false);
    }
  };

  if (acceptData || quoteId) {
    fetchHouseholdContentQuestions();
  } else {
    setLoadingHouseholdContentQuestions(false);
  }
}, [acceptData, quoteId]);

// Handler for household content question responses
const handleHouseholdContentQuestionResponse = (questionId: number, value: string | boolean) => {
  setHouseholdContentQuestionResponses(prev => ({
    ...prev,
    [questionId]: value,
  }));
};

// Fetch Vehicle Questions
useEffect(() => {
  const fetchVehicleQuestions = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoadingVehicleQuestions(false);
      return;
    }

    try {
      console.log("🚀 Fetching vehicle questions");
      
      // Get insurer ID from acceptData or quote
      let insurerId: number | undefined;
      
      if (acceptData?.insurers && Array.isArray(acceptData.insurers) && acceptData.insurers.length > 0) {
        const firstInsurer = acceptData.insurers[0];
        insurerId = firstInsurer?.id || firstInsurer?.insurerId;
      }
      
      // If not found in acceptData, try to get from quote data
      if (!insurerId && quoteId) {
        try {
          const quoteResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/quotes/${quoteId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          const quoteData = quoteResponse.data.data || quoteResponse.data;
          insurerId = quoteData?.administrator_id || quoteData?.underwriter_id;
        } catch (quoteError) {
          console.error("Failed to fetch quote for insurer ID:", quoteError);
        }
      }
      
      if (!insurerId) {
        console.warn("⚠️ No insurer ID found, cannot fetch vehicle questions");
        setLoadingVehicleQuestions(false);
        return;
      }
      
      // Build params according to backend API
      const params: any = { 
        insurerId: insurerId,
        category: 'Vehicle'  // Must match getTableName() mapping
      };
      
      console.log("📤 Sending request to /api/application/questions with params:", params);
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/application/questions`,
        {
          params: params,
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log("✅ Vehicle questions API response:", response.data);
      
      // Handle response - backend returns array directly
      let questions = [];
      if (Array.isArray(response.data)) {
        questions = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        questions = response.data.data;
      } else {
        questions = [];
      }
      
      console.log(`📋 Loaded ${questions.length} vehicle questions:`, questions);
      setVehicleQuestions(questions);
      // Initialize responses
      const vehicleInitialResponses: { [key: number]: boolean } = {};
      questions.forEach((q: VehicleQuestion) => {
        if (q.id) vehicleInitialResponses[q.id] = false;
      });
      setVehicleQuestionResponses(vehicleInitialResponses);

    } catch (error: any) {
      console.error("❌ Error loading vehicle questions:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      setVehicleQuestions([]);
    } finally {
      setLoadingVehicleQuestions(false);
    }
  };

  if (acceptData || quoteId) {
    fetchVehicleQuestions();
  } else {
    setLoadingVehicleQuestions(false);
  }
}, [acceptData, quoteId]);

// Fetch Trailer/Caravan Questions
useEffect(() => {
  const fetchTrailerCaravanQuestions = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoadingTrailerCaravanQuestions(false);
      return;
    }

    try {
      console.log("🚀 Fetching trailer/caravan questions");
      
      // Get insurer ID from acceptData or quote
      let insurerId: number | undefined;
      
      if (acceptData?.insurers && Array.isArray(acceptData.insurers) && acceptData.insurers.length > 0) {
        const firstInsurer = acceptData.insurers[0];
        insurerId = firstInsurer?.id || firstInsurer?.insurerId;
      }
      
      // If not found in acceptData, try to get from quote data
      if (!insurerId && quoteId) {
        try {
          const quoteResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/quotes/${quoteId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          const quoteData = quoteResponse.data.data || quoteResponse.data;
          insurerId = quoteData?.administrator_id || quoteData?.underwriter_id;
        } catch (quoteError) {
          console.error("Failed to fetch quote for insurer ID:", quoteError);
        }
      }
      
      if (!insurerId) {
        console.warn("⚠️ No insurer ID found, cannot fetch trailer/caravan questions");
        setLoadingTrailerCaravanQuestions(false);
        return;
      }
      
      // Build params according to backend API
      const params: any = { 
        insurerId: insurerId,
        category: 'Trailer/Caravan'  // Must match getTableName() mapping
      };
      
      console.log("📤 Sending request to /api/application/questions with params:", params);
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/application/questions`,
        {
          params: params,
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log("✅ Trailer/Caravan questions API response:", response.data);
      
      // Handle response - backend returns array directly
      let questions = [];
      if (Array.isArray(response.data)) {
        questions = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        questions = response.data.data;
      } else {
        questions = [];
      }
      
      console.log(`📋 Loaded ${questions.length} trailer/caravan questions:`, questions);
      setTrailerCaravanQuestions(questions);
      // Initialize responses
      const trailerInitialResponses: { [key: number]: boolean } = {};
      questions.forEach((q: TrailerCaravanQuestion) => {
        if (q.id) trailerInitialResponses[q.id] = false;
      });
      setTrailerCaravanQuestionResponses(trailerInitialResponses);

    } catch (error: any) {
      console.error("❌ Error loading trailer/caravan questions:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      setTrailerCaravanQuestions([]);
    } finally {
      setLoadingTrailerCaravanQuestions(false);
    }
  };

  if (acceptData || quoteId) {
    fetchTrailerCaravanQuestions();
  } else {
    setLoadingTrailerCaravanQuestions(false);
  }
}, [acceptData, quoteId]);

// Fetch Watercraft Questions
useEffect(() => {
  const fetchWatercraftQuestions = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoadingWatercraftQuestions(false);
      return;
    }

    try {
      console.log("🚀 Fetching watercraft questions");
      
      // Get insurer ID from acceptData or quote
      let insurerId: number | undefined;
      
      if (acceptData?.insurers && Array.isArray(acceptData.insurers) && acceptData.insurers.length > 0) {
        const firstInsurer = acceptData.insurers[0];
        insurerId = firstInsurer?.id || firstInsurer?.insurerId;
      }
      
      // If not found in acceptData, try to get from quote data
      if (!insurerId && quoteId) {
        try {
          const quoteResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/quotes/${quoteId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          const quoteData = quoteResponse.data.data || quoteResponse.data;
          insurerId = quoteData?.administrator_id || quoteData?.underwriter_id;
        } catch (quoteError) {
          console.error("Failed to fetch quote for insurer ID:", quoteError);
        }
      }
      
      if (!insurerId) {
        console.warn("⚠️ No insurer ID found, cannot fetch watercraft questions");
        setLoadingWatercraftQuestions(false);
        return;
      }
      
      // Build params according to backend API
      const params: any = { 
        insurerId: insurerId,
        category: 'Water Craft'  // Must match getTableName() mapping - note the space
      };
      
      console.log("📤 Sending request to /api/application/questions with params:", params);
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/application/questions`,
        {
          params: params,
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log("✅ Watercraft questions API response:", response.data);
      
      // Handle response - backend returns array directly
      let questions = [];
      if (Array.isArray(response.data)) {
        questions = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        questions = response.data.data;
      } else {
        questions = [];
      }
      
      console.log(`📋 Loaded ${questions.length} watercraft questions:`, questions);
      setWatercraftQuestions(questions);
      // Initialize responses
      const watercraftInitialResponses: { [key: number]: boolean } = {};
      questions.forEach((q: WatercraftQuestion) => {
        if (q.id) watercraftInitialResponses[q.id] = false;
      });
      setWatercraftQuestionResponses(watercraftInitialResponses);

    } catch (error: any) {
      console.error("❌ Error loading watercraft questions:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      setWatercraftQuestions([]);
    } finally {
      setLoadingWatercraftQuestions(false);
    }
  };

  if (acceptData || quoteId) {
    fetchWatercraftQuestions();
  } else {
    setLoadingWatercraftQuestions(false);
  }
}, [acceptData, quoteId]);

// Fetch All Risk Questions
useEffect(() => {
  const fetchAllRiskQuestions = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoadingAllRiskQuestions(false);
      return;
    }

    try {
      console.log("🚀 Fetching all risk questions");
      
      // Get insurer ID from acceptData or quote
      let insurerId: number | undefined;
      
      if (acceptData?.insurers && Array.isArray(acceptData.insurers) && acceptData.insurers.length > 0) {
        const firstInsurer = acceptData.insurers[0];
        insurerId = firstInsurer?.id || firstInsurer?.insurerId;
      }
      
      // If not found in acceptData, try to get from quote data
      if (!insurerId && quoteId) {
        try {
          const quoteResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/quotes/${quoteId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          const quoteData = quoteResponse.data.data || quoteResponse.data;
          insurerId = quoteData?.administrator_id || quoteData?.underwriter_id;
        } catch (quoteError) {
          console.error("Failed to fetch quote for insurer ID:", quoteError);
        }
      }
      
      if (!insurerId) {
        console.warn("⚠️ No insurer ID found, cannot fetch all risk questions");
        setLoadingAllRiskQuestions(false);
        return;
      }
      
      // Build params according to backend API
      const params: any = { 
        insurerId: insurerId,
        category: 'All Risk'  // Must match getTableName() mapping
      };
      
      console.log("📤 Sending request to /api/application/questions with params:", params);
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/application/questions`,
        {
          params: params,
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log("✅ All Risk questions API response:", response.data);
      
      // Handle response - backend returns array directly
      let questions = [];
      if (Array.isArray(response.data)) {
        questions = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        questions = response.data.data;
      } else {
        questions = [];
      }
      
      console.log(`📋 Loaded ${questions.length} all risk questions:`, questions);
      setAllRiskQuestions(questions);
      // Initialize responses
      const allRiskInitialResponses: { [key: number]: boolean } = {};
      questions.forEach((q: AllRiskQuestion) => {
        if (q.id) allRiskInitialResponses[q.id] = false;
      });
      setAllRiskQuestionResponses(allRiskInitialResponses);

    } catch (error: any) {
      console.error("❌ Error loading all risk questions:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      setAllRiskQuestions([]);
    } finally {
      setLoadingAllRiskQuestions(false);
    }
  };

  if (acceptData || quoteId) {
    fetchAllRiskQuestions();
  } else {
    setLoadingAllRiskQuestions(false);
  }
}, [acceptData, quoteId]);

// Fetch Value Added Questions
useEffect(() => {
  const fetchValueAddedQuestions = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoadingValueAddedQuestions(false);
      return;
    }

    try {
      console.log("🚀 Fetching value added questions");
      
      // Get insurer ID from acceptData or quote
      let insurerId: number | undefined;
      
      if (acceptData?.insurers && Array.isArray(acceptData.insurers) && acceptData.insurers.length > 0) {
        const firstInsurer = acceptData.insurers[0];
        insurerId = firstInsurer?.id || firstInsurer?.insurerId;
      }
      
      // If not found in acceptData, try to get from quote data
      if (!insurerId && quoteId) {
        try {
          const quoteResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/quotes/${quoteId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          const quoteData = quoteResponse.data.data || quoteResponse.data;
          insurerId = quoteData?.administrator_id || quoteData?.underwriter_id;
        } catch (quoteError) {
          console.error("Failed to fetch quote for insurer ID:", quoteError);
        }
      }
      
      if (!insurerId) {
        console.warn("⚠️ No insurer ID found, cannot fetch value added questions");
        setLoadingValueAddedQuestions(false);
        return;
      }
      
      // Build params according to backend API
      const params: any = { 
        insurerId: insurerId,
        category: 'Value Added'  // Must match getTableName() mapping
      };
      
      console.log("📤 Sending request to /api/application/questions with params:", params);
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/application/questions`,
        {
          params: params,
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log("✅ Value Added questions API response:", response.data);
      
      // Handle response - backend returns array directly
      let questions = [];
      if (Array.isArray(response.data)) {
        questions = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        questions = response.data.data;
      } else {
        questions = [];
      }
      
      console.log(`📋 Loaded ${questions.length} value added questions:`, questions);
      setValueAddedQuestions(questions);
      // Initialize responses
      const valueAddedInitialResponses: { [key: number]: boolean } = {};
      questions.forEach((q: ValueAddedQuestion) => {
        if (q.id) valueAddedInitialResponses[q.id] = false;
      });
      setValueAddedQuestionResponses(valueAddedInitialResponses);

    } catch (error: any) {
      console.error("❌ Error loading value added questions:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      setValueAddedQuestions([]);
    } finally {
      setLoadingValueAddedQuestions(false);
    }
  };

  if (acceptData || quoteId) {
    fetchValueAddedQuestions();
  } else {
    setLoadingValueAddedQuestions(false);
  }
}, [acceptData, quoteId]);

useEffect(() => {
  const fetchHouseholdContents = async () => {
    const token = localStorage.getItem('token');
    if (!token || !quoteId) {
      setLoadingHouseholdContents(false);
      return;
    }

    try {
      console.log("🚀 Fetching household contents for quote:", quoteId);
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/household-contents`,
        {
          params: { 
            quote_id: Number(quoteId)
          },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log("✅ Household contents API response:", response.data);
      
      let rawContents: any[] = [];
      
      if (response.data && response.data.success === true && Array.isArray(response.data.data)) {
        rawContents = response.data.data;
      } else if (Array.isArray(response.data)) {
        rawContents = response.data;
      }
      
      // Map snake_case fields to camelCase for consistent access
      const contents = rawContents.map((item: any) => {
        // Helper function to safely get value from either snake_case or camelCase
        const getValue = (snakeKey: string, camelKey: string, defaultValue: any = '') => {
          return item[snakeKey] !== undefined ? item[snakeKey] : (item[camelKey] !== undefined ? item[camelKey] : defaultValue);
        };

        return {
          ...item,
          // Map material fields from API (snake_case) to component (camelCase)
          wallMaterials: getValue('wall_materials', 'wallMaterials', ''),
          roofMaterials: getValue('roof_materials', 'roofMaterials', ''),
          floorMaterials: getValue('floor_materials', 'floorMaterials', ''),
          otherSurroundingArea: getValue('other_surrounding_area', 'otherSurroundingArea', ''),
          perimeterType: getValue('perimeter_type', 'perimeterType', ''),
          // Map other fields as needed
          yearsLivedOnProperty: getValue('years_lived_on_property', 'yearsLivedOnProperty'),
          burglarBarType: getValue('burglar_bar_type', 'burglarBarType'),
          dayTimeOccupantType: getValue('day_time_occupant_type', 'dayTimeOccupantType'),
          coverType: getValue('cover_type', 'coverType'),
          insuredValue: getValue('insured_value', 'insuredValue'),
          conductsBusiness: getValue('conducts_business', 'conductsBusiness', false),
          hasSurveillanceCamera: getValue('has_surveillance_camera', 'hasSurveillanceCamera', false),
          hasExternalSlidingDoor: getValue('has_external_sliding_door', 'hasExternalSlidingDoor', false),
          hasSecurityGate: getValue('has_security_gate', 'hasSecurityGate', false),
          hasAlarmRecords: getValue('has_alarm_records', 'hasAlarmRecords', false),
          inSecurityComplex: getValue('in_security_complex', 'inSecurityComplex', false),
          alarmSystemType: getValue('alarm_system_type', 'alarmSystemType'),
          hasAccidentalDamageCover: getValue('has_accidental_damage_cover', 'hasAccidentalDamageCover', false),
          accidentalDamageValue: getValue('accidental_damage_value', 'accidentalDamageValue', 0),
          hasPowerSurgeCover: getValue('has_power_surge_cover', 'hasPowerSurgeCover', false),
          powerSurgeValue: getValue('power_surge_value', 'powerSurgeValue', 0),
          addressLine1: getValue('address_line_1', 'addressLine1'),
          addressLine2: getValue('address_line_2', 'addressLine2'),
          cityTown: getValue('city_town', 'cityTown'),
          postalCode: getValue('postal_code', 'postalCode'),
          propertyType: getValue('property_type', 'propertyType'),
          residenceType: getValue('residence_type', 'residenceType'),
          buildingAge: getValue('building_age', 'buildingAge'),
          situatedType: getValue('situated_type', 'situatedType'),
          hasSwimmingPool: getValue('has_swimming_pool', 'hasSwimmingPool', false),
          hasMotorizedGate: getValue('has_motorized_gate', 'hasMotorizedGate', false),
          isPropertyUnoccupied: getValue('is_property_unoccupied', 'isPropertyUnoccupied', false),
          isCommune: getValue('is_commune', 'isCommune', false),
          underConstruction: getValue('under_construction', 'underConstruction', false),
          builtOnSteepSlope: getValue('built_on_steep_slope', 'builtOnSteepSlope', false),
          hasRetainingWalls: getValue('has_retaining_walls', 'hasRetainingWalls', false),
          nearWaterBody: getValue('near_water_body', 'nearWaterBody', false),
          hasRisingDamp: getValue('has_rising_damp', 'hasRisingDamp', false),
          hasWallCracks: getValue('has_wall_cracks', 'hasWallCracks', false),
          historyOfLandslip: getValue('history_of_landslip', 'historyOfLandslip', false),
          hasOutbuilding: getValue('has_outbuilding', 'hasOutbuilding', false),
          hasPerimeterFence: getValue('has_perimeter_fence', 'hasPerimeterFence', false),
          hasElectricFence: getValue('has_electric_fence', 'hasElectricFence', false),
          hasThatchRoof: getValue('has_thatch_roof', 'hasThatchRoof', false),
          noClaimBonus: getValue('no_claim_bonus', 'noClaimBonus')
        };
      });
      
      console.log("📋 Mapped household contents with material fields:", contents.map((c: any) => ({
        wallMaterials: c.wallMaterials,
        roofMaterials: c.roofMaterials,
        floorMaterials: c.floorMaterials,
        otherSurroundingArea: c.otherSurroundingArea,
        perimeterType: c.perimeterType
      })));
      
      setHouseholdContents(contents);

    } catch (error: any) {
      console.error("❌ Error loading household contents:", error);
      setHouseholdContents([]);
    } finally {
      setLoadingHouseholdContents(false);
    }
  };

  fetchHouseholdContents();
}, [quoteId]);

useEffect(() => {
  const fetchVehicles = async () => {
    const token = localStorage.getItem('token');
    if (!token || !quoteId) {
      setLoadingVehicles(false);
      return;
    }

    try {
      console.log("🚀 Fetching vehicles for quote:", quoteId);
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/vehicles`,
        {
          params: { quote_id: Number(quoteId) },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log("✅ Vehicles API response:", response.data);
      
      // The endpoint returns { success: true, data: [...] }
      let vehiclesData = [];
      if (response.data && response.data.success === true && Array.isArray(response.data.data)) {
        vehiclesData = response.data.data;
        console.log(`📋 Loaded ${vehiclesData.length} vehicles`);
      } else if (Array.isArray(response.data)) {
        vehiclesData = response.data;
        console.log(`📋 Loaded ${vehiclesData.length} vehicles (direct array)`);
      } else if (Array.isArray(response.data.data)) {
        vehiclesData = response.data.data;
        console.log(`📋 Loaded ${vehiclesData.length} vehicles (from data property)`);
      } else {
        console.warn("⚠️ Unexpected vehicles response format:", response.data);
        vehiclesData = [];
      }
      
      setVehicles(vehiclesData);

    } catch (error: any) {
      console.error("❌ Error loading vehicles:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      
      // Try fallback: check if vehicles are in quote data
      if (quoteId) {
        try {
          const quoteResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/quotes/${quoteId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          
          const quoteData = quoteResponse.data.data || quoteResponse.data;
          if (quoteData.vehicles && Array.isArray(quoteData.vehicles)) {
            console.log(`✅ Loaded ${quoteData.vehicles.length} vehicles from quote data (fallback)`);
            setVehicles(quoteData.vehicles);
          } else {
            setVehicles([]);
          }
        } catch (quoteError) {
          console.error("Failed to fetch quote data for vehicles fallback:", quoteError);
          setVehicles([]);
        }
      } else {
        setVehicles([]);
      }
    } finally {
      setLoadingVehicles(false);
    }
  };

  fetchVehicles();
}, [quoteId]);

// Fetch Persons of Interest - Fixed to match backend (expects ?clientId=xxx)
useEffect(() => {
  const fetchPersonsOfInterest = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoadingPersonsOfInterest(false);
      return;
    }

    // Get clientId from client state or acceptData
    let clientId: number | null = null;
    
    if (client?.id) clientId = Number(client.id);
    else if (client?.client_id) clientId = Number(client.client_id);
    else if (acceptData?.clientId) clientId = Number(acceptData.clientId);

    if (!clientId || isNaN(clientId)) {
      console.warn("⚠️ No valid clientId found for persons of interest");
      setPersonsOfInterest([]);
      setLoadingPersonsOfInterest(false);
      return;
    }

    try {
      console.log(`🚀 Fetching persons of interest for clientId: ${clientId}`);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/persons-of-interest`,
        {
          params: { clientId: clientId },   // <-- Capital C as required by backend
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      let data: any[] = [];
      if (response.data && Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.success && Array.isArray(response.data.data)) {
        data = response.data.data;
      }

      console.log(`✅ Loaded ${data.length} persons of interest for client ${clientId}`);
      console.log("🔍 Persons of Interest Data (License check):", data.map((p: any) => ({
        id: p.id,
        name: `${p.name} ${p.surname}`,
        licenses: p.drivers_license_codes
      })));
      setPersonsOfInterest(data);
    } catch (error: any) {
      console.error("❌ Error loading persons of interest:", error.response?.data || error.message);
      setPersonsOfInterest([]);
    } finally {
      setLoadingPersonsOfInterest(false);
    }
  };

  fetchPersonsOfInterest();
}, [client, acceptData]);   // Depend on client + acceptData

useEffect(() => {
  const fetchTrailersCaravans = async () => {
    const token = localStorage.getItem('token');
    if (!token || !quoteId) {
      setLoadingTrailersCaravans(false);
      return;
    }

    try {
      console.log("🚀 Fetching trailers/caravans for quote:", quoteId);
      
      // Note: endpoint is /api/trailer-caravans (singular), not /api/trailers-caravans
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/trailer-caravans`,
        {
          params: { quote_id: Number(quoteId) },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log("✅ Trailers/Caravans API response:", response.data);
      
      // The endpoint returns { success: true, data: [...] }
      let trailersData = [];
      if (response.data && response.data.success === true && Array.isArray(response.data.data)) {
        trailersData = response.data.data;
        console.log(`📋 Loaded ${trailersData.length} trailers/caravans`);
      } else if (Array.isArray(response.data)) {
        trailersData = response.data;
        console.log(`📋 Loaded ${trailersData.length} trailers/caravans (direct array)`);
      } else {
        console.warn("⚠️ Unexpected trailers/caravans response format:", response.data);
        trailersData = [];
      }
      
      setTrailersCaravans(trailersData);

    } catch (error: any) {
      console.error("❌ Error loading trailers/caravans:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      setTrailersCaravans([]);
    } finally {
      setLoadingTrailersCaravans(false);
    }
  };

  fetchTrailersCaravans();
}, [quoteId]);

useEffect(() => {
  const fetchWatercraft = async () => {
    const token = localStorage.getItem('token');
    if (!token || !quoteId) {
      setLoadingWatercraft(false);
      return;
    }

    try {
      console.log("🚀 Fetching watercraft for quote:", quoteId);
      
      // Note: endpoint is /api/water-craft (singular)
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/water-craft`,
        {
          params: { quote_id: Number(quoteId) },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log("✅ Watercraft API response:", response.data);
      
      // The endpoint returns { success: true, data: [...] }
      let watercraftData = [];
      if (response.data && response.data.success === true && Array.isArray(response.data.data)) {
        watercraftData = response.data.data;
        console.log(`📋 Loaded ${watercraftData.length} watercraft items`);
      } else if (Array.isArray(response.data)) {
        watercraftData = response.data;
        console.log(`📋 Loaded ${watercraftData.length} watercraft items (direct array)`);
      } else {
        console.warn("⚠️ Unexpected watercraft response format:", response.data);
        watercraftData = [];
      }
      
      setWatercraft(watercraftData);

    } catch (error: any) {
      console.error("❌ Error loading watercraft:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      setWatercraft([]);
    } finally {
      setLoadingWatercraft(false);
    }
  };

  fetchWatercraft();
}, [quoteId]);

useEffect(() => {
  const fetchAllRiskItems = async () => {
    const token = localStorage.getItem('token');
    if (!token || !quoteId) {
      setLoadingAllRiskItems(false);
      return;
    }

    try {
      console.log("🚀 Fetching all risk items for quote:", quoteId);
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/risk-items`,
        {
          params: { quote_id: Number(quoteId) },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log("✅ All Risk Items API response:", response.data);
      
      // The endpoint returns { success: true, data: [...] }
      let allRiskData = [];
      if (response.data && response.data.success === true && Array.isArray(response.data.data)) {
        allRiskData = response.data.data;
        console.log(`📋 Loaded ${allRiskData.length} all risk items`);
      } else if (Array.isArray(response.data)) {
        allRiskData = response.data;
        console.log(`📋 Loaded ${allRiskData.length} all risk items (direct array)`);
      } else {
        console.warn("⚠️ Unexpected all risk items response format:", response.data);
        allRiskData = [];
      }
      
      setAllRiskItems(allRiskData);

    } catch (error: any) {
      console.error("❌ Error loading all risk items:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      setAllRiskItems([]);
    } finally {
      setLoadingAllRiskItems(false);
    }
  };

  fetchAllRiskItems();
}, [quoteId]);

useEffect(() => {
  const fetchValueAddedProducts = async () => {
    const token = localStorage.getItem('token');
    if (!token || !quoteId) {
      setLoadingValueAddedProducts(false);
      return;
    }

    try {
      console.log("🚀 Fetching value added products for quote:", quoteId);
      
      // Note: endpoint is /api/value-added (singular)
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/value-added`,
        {
          params: { quote_id: Number(quoteId) },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log("✅ Value Added Products API response:", response.data);
      
      // The endpoint returns { success: true, data: [...] }
      let valueAddedData = [];
      if (response.data && response.data.success === true && Array.isArray(response.data.data)) {
        valueAddedData = response.data.data;
        console.log(`📋 Loaded ${valueAddedData.length} value added products`);
      } else if (Array.isArray(response.data)) {
        valueAddedData = response.data;
        console.log(`📋 Loaded ${valueAddedData.length} value added products (direct array)`);
      } else {
        console.warn("⚠️ Unexpected value added products response format:", response.data);
        valueAddedData = [];
      }
      
      setValueAddedProducts(valueAddedData);

    } catch (error: any) {
      console.error("❌ Error loading value added products:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      setValueAddedProducts([]);
    } finally {
      setLoadingValueAddedProducts(false);
    }
  };

  fetchValueAddedProducts();
}, [quoteId]);

  if (!acceptData) return <div className="p-10">Loading proposal form...</div>;

  return (
  <div className="max-w-5xl mx-auto p-8 bg-white font-sans">
    {/* PREVIEW BANNER - Only show in preview mode */}
    {isPreview && (
      <div className="mb-6 p-4 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-800 rounded-md">
        <div className="flex items-center">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          <span className="font-medium">PREVIEW MODE</span>
        </div>
        <p className="text-sm mt-1 ml-7">
          This is a preview of how the proposal will appear to the client. No email will be sent.
        </p>
      </div>
    )}
      <div className="border-2 border-black p-8">

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold tracking-widest">INSURANCE PROPOSAL FORM</h1>
        </div>

        {/* Status Messages */}
        {saveStatus === 'loading' && (
          <div className="mb-6 bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded flex items-center gap-2">
            <svg className="animate-spin h-5 w-5 text-blue-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {saveMessage}
          </div>
        )}
        {saveStatus === 'success' && (
          <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
            {saveMessage}
          </div>
        )}
        {saveStatus === 'error' && (
          <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {saveMessage}
          </div>
        )}

        {/* Quote Number & Inception Date */}
        <div className="grid grid-cols-2 gap-12 mb-10">
          <div>
            <div className="text-sm mb-1">Quote Number:</div>
            <div className="border-b border-black pb-1 font-medium">
              {acceptData?.insurers?.[0]?.insurerQuotationNumber || acceptData?.insurers?.[0]?.quotation_ref || acceptData?.quotation_ref || quoteId}
            </div>
          </div>

          <div>
            <div className="text-sm mb-1">Inception Date:</div>
            <DateInput
              value={inceptionDate}
              onChange={(date) => setInceptionDate(date)}
              className="border-b border-black w-full py-1 focus:outline-none"
            />
          </div>
        </div>

        {/* Important Notes Section - Shows questions from database (same as online signing) */}
<div className="mb-12 text-sm leading-relaxed print:mb-8">
  <p className="font-bold text-lg mb-4 uppercase tracking-wide border-b border-gray-400 pb-2">
    IMPORTANT
  </p>

  {loadingQuestions ? (
    <div className="py-6 text-center text-gray-500 italic">
      Loading important information...
    </div>
  ) : importantQuestions.length === 0 ? (
    <p className="text-gray-700 italic">
      No important notes configured at this time.
    </p>
  ) : (
    <div className="space-y-4 text-gray-800">
      {importantQuestions.map((q, idx) => (
        <p 
          key={q.id || idx} 
          className={`leading-relaxed ${q.is_default ? 'font-medium' : ''}`}
        >
          {q.question}
        </p>
      ))}
    </div>
  )}
</div>

        {/* Particulars of Insured */}
        <div className="mb-12">
          <h2 className="font-bold text-lg mb-4 border-b border-black pb-1">PARTICULARS OF INSURED</h2>
          
          {loadingClient || loadingAddress ? (
            <div className="py-4 text-gray-500">Loading insured details...</div>
          ) : (
            <div className="grid grid-cols-2 gap-x-16 gap-y-3 text-sm">
              {/* Client Name */}
                <div>Client Name</div>
                <div className="border-b border-black pb-1 font-medium">
                  {client 
                    ? `${client.name || ''} ${client.middle_names || ''} ${client.surname || ''}`.trim() || "—"
                    : "Not found"}
                </div>

                {/* Gender */}
                <div>Gender</div>
                <div className="border-b border-black pb-1 font-medium">
                  {client?.gender || client?.sex || "—"}
                </div>

              {/* Identification Number */}
              <div>Identification Number</div>
              <div className="border-b border-black pb-1 font-medium">
                {client?.id_passport_number || client?.id_no || "—"}
              </div>

              {/* Date of Birth - FIXED */}
              <div>Date of Birth</div>
              <div className="border-b border-black pb-1 font-medium">
                {(() => {
                  const dob = client?.date_of_birth || client?.dob || client?.birth_date;
                  if (!dob) return "—";
                  
                  // Handle ISO string with time (e.g., "1995-04-04T22:00:00.000Z")
                  if (typeof dob === 'string') {
                    // Try to extract just the date part
                    const dateMatch = dob.match(/^(\d{4})-(\d{2})-(\d{2})/);
                    if (dateMatch) {
                      const year = dateMatch[1];
                      const month = dateMatch[2];
                      const day = dateMatch[3];
                      return `${day}/${month}/${year}`;
                    }
                    
                    // If already in DD/MM/YYYY format
                    if (dob.includes('/') && dob.length === 10) {
                      return dob;
                    }
                    
                    // Try parsing as Date object
                    try {
                      const date = new Date(dob);
                      if (!isNaN(date.getTime())) {
                        const day = date.getDate().toString().padStart(2, '0');
                        const month = (date.getMonth() + 1).toString().padStart(2, '0');
                        const year = date.getFullYear();
                        return `${day}/${month}/${year}`;
                      }
                    } catch (e) {
                      // Fall through
                    }
                  }
                  
                  return dob;
                })()}
              </div>

              {/* Street */}
              <div>Street</div>
              <div className="border-b border-black pb-1">
                {address?.address_line_1 || address?.street || "—"}
              </div>

              {/* City/Town */}
              <div>City/Town</div>
              <div className="border-b border-black pb-1">
                {address?.city || "—"}
              </div>

              {/* Suburb */}
              <div>Suburb</div>
              <div className="border-b border-black pb-1">
                {address?.suburb || address?.region || address?.state || "—"}
              </div>

              {/* Province */}
              <div>Province</div>
              <div className="border-b border-black pb-1">
                {address?.province || "—"}
              </div>

              {/* Postal Code */}
              <div>Postal Code</div>
              <div className="border-b border-black pb-1">
                {address?.postal_code || address?.zipCode || "—"}
              </div>

              <div>Mobile Number</div>
              <div className="border-b border-black pb-1">
                {client?.mobile_number || client?.mobile_no || "—"}
              </div>

              <div>Telephone Number</div>
              <div className="border-b border-black pb-1">
                {client?.telephone_number || "—"}
              </div>

              <div>e-Mail</div>
              <div className="border-b border-black pb-1 break-all">
                {client?.email || "—"}
              </div>

              <div>Occupation</div>
              <div className="border-b border-black pb-1">
                {client?.job_description || "—"}
              </div>

              <div>Employer</div>
              <div className="border-b border-black pb-1">
                {client?.employer_name || "—"}
              </div>

              <div>Marital Status</div>
              <div className="border-b border-black pb-1">
                {client?.marital_status || "—"}
              </div>

              <div>Language</div>
              <div className="border-b border-black pb-1">
                {client?.language || "—"}
              </div>

              {/* Optional: No address message */}
              {!address && !loadingAddress && (
                <div className="col-span-2 text-sm text-gray-500 italic mt-2">
                  No address recorded for this client
                </div>
              )}
            </div>
          )}
        </div>

        {/* Broker Details */}
        <div className="mb-12">
          <h2 className="font-bold text-lg mb-4 border-b border-black pb-1 uppercase">
            BROKER DETAILS
          </h2>
          
          {loadingBroker ? (
            <div className="py-4 text-gray-500 italic">Loading broker information...</div>
          ) : broker ? (
            <div className="space-y-6">
              {/* Basic Info Grid */}
              <div className="grid grid-cols-2 gap-x-16 gap-y-3 text-sm">
                <div>FSP Name</div>
                <div className="border-b border-black pb-1 font-medium">
                  {broker.companyName || "—"}
                </div>

                <div>Broker Code</div>
                <div className="border-b border-black pb-1 font-medium">
                  {broker.registrationNumber || "—"}
                </div>

                <div>FSP Number</div>
                <div className="border-b border-black pb-1 font-medium">
                  {broker.fspNumber || "—"}
                </div>

                <div>Broker Contact No:</div>
                <div className="border-b border-black pb-1 font-medium">
                  {broker.contactNumber || "—"}
                </div>

                {/* Sales Consultant */}
                <div>Sales Consultant</div>
                <div className="border-b border-black pb-1 font-medium">
                  {broker.salesConsultant || 
                   broker.sales_consultant || 
                   broker.consultant_name || 
                   broker.primaryContactPerson || 
                   (broker as any).primary_contact_person || 
                   "—"}
                </div>

                <div>Email Address</div>
                <div className="border-b border-black pb-1 break-all font-medium">
                  {broker.email || "—"}
                </div>
              </div>

              
            </div>
          ) : (
            <div className="py-4 text-gray-500 italic">
              No broker details available
            </div>
          )}
        </div>

        {/* Debit Order Authority */}
        <div className="mb-12">
          <h2 className="font-bold text-lg mb-4 border-b border-black pb-1 uppercase tracking-wide">
            DEBIT ORDER AUTHORITY
          </h2>

          <div className="grid grid-cols-2 gap-x-16 gap-y-4 text-sm">
            <div className="font-medium">Full Name Of Account Holder</div>
<div>
  <input
    type="text"
    value={debitOrder.surnameInitials}
    onChange={handleDebitChange('surnameInitials')}
    className="w-full border-b border-black pb-1 focus:outline-none font-medium"
    placeholder="e.g. John Smith"
  />
</div>

<div className="font-medium">Account Holder ID</div>
<div>
  <input
    type="text"
    value={debitOrder.accountHolderId || ''}
    onChange={handleDebitChange('accountHolderId')}
    className="w-full border-b border-black pb-1 focus:outline-none font-medium"
    placeholder="e.g. 8001015000088"
  />
</div>

            {/* Account Number */}
            <div className="font-medium">Account Number</div>
            <div>
              <input
                type="text"
                value={debitOrder.accountNumber}
                onChange={handleDebitChange('accountNumber')}
                className="w-full border-b border-black pb-1 focus:outline-none font-medium"
                placeholder="e.g. 123456789012"
              />
            </div>

            {/* Account Type */}
            <div className="font-medium">Account Type</div>
            <div>
              <select
                value={debitOrder.accountType}
                onChange={handleDebitChange('accountType')}
                className="w-full border-b border-black pb-1 focus:outline-none font-medium bg-white"
              >
                <option value="">Select account type</option>
                <option value="Cheque">Cheque / Current Account</option>
                <option value="Savings">Savings Account</option>
                <option value="Transmission">Transmission Account</option>
              </select>
            </div>

            {/* Name of Bank */}
            <div className="font-medium">Name of Bank</div>
            <div>
              <input
                type="text"
                value={debitOrder.bankName}
                onChange={handleDebitChange('bankName')}
                className="w-full border-b border-black pb-1 focus:outline-none font-medium"
                placeholder="e.g. ABSA, FNB, Standard Bank, Nedbank"
              />
            </div>

            {/* Branch Code */}
            <div className="font-medium">Branch Code</div>
            <div>
              <input
                type="text"
                value={debitOrder.branchCode}
                onChange={handleDebitChange('branchCode')}
                className="w-full border-b border-black pb-1 focus:outline-none font-medium"
                placeholder="e.g. 632005"
                maxLength={6}
              />
            </div>

            {/* Bank Branch */}
            <div className="font-medium">Bank Branch</div>
            <div>
              <input
                type="text"
                value={debitOrder.bankBranch}
                onChange={handleDebitChange('bankBranch')}
                className="w-full border-b border-black pb-1 focus:outline-none font-medium"
                placeholder="e.g. Durban North, Sandton, Cape Town Central"
              />
            </div>

            {/* Debit Order Date */}
            <div className="font-medium">Debit Order Date</div>
            <div>
              <select
                value={debitOrder.debitOrderDate}
                onChange={handleDebitChange('debitOrderDate')}
                className="w-full border-b border-black pb-1 focus:outline-none font-medium bg-white"
              >
                <option value="">Select debit date</option>
                <option value="1st">1st of the month</option>
                <option value="5th">5th of the month</option>
                <option value="10th">10th of the month</option>
                <option value="15th">15th of the month</option>
                <option value="20th">20th of the month</option>
                <option value="25th">25th of the month</option>
                <option value="Last working day">Last working day of the month</option>
              </select>
            </div>

            {/* Premium Payable */}
            <div className="font-medium">Premium Payable</div>
            <div>
              <input
                type="text"
                value={debitOrder.premiumPayable}
                onChange={handleDebitChange('premiumPayable')}
                className="w-full border-b border-black pb-1 focus:outline-none font-medium"
                placeholder="e.g. R 1 250.00"
              />
            </div>
          </div>

          {/* Small legal-ish note (optional) */}
          <p className="mt-6 text-xs text-gray-600 italic">
            I/We hereby authorise and instruct the above-mentioned bank to debit my/our account 
            with the premium amount shown above on the selected debit order date each month.
          </p>
        </div>

        {/* Debit Order Declaration */}
        <div className="mb-12">
          <h2 className="font-bold text-lg mb-4 border-b border-black pb-1">DEBIT ORDER AUTHORITY DECLARATION</h2>
          
          {loadingDebitOrderQuestions ? (
            <div className="py-4 text-gray-500 italic">Loading debit order declaration...</div>
          ) : debitOrderQuestions.length === 0 ? (
            <p className="text-gray-600 italic">No debit order declaration questions configured.</p>
          ) : (
            <div className="space-y-6">
              {debitOrderQuestions.map((question, index) => (
                <div key={question.id || index} className="flex items-start gap-3">
                  <div className="mt-1">
                    <input
                      type="checkbox"
                      id={`debit-declaration-${question.id || index}`}
                      checked={question.id ? debitOrderResponses[question.id] || false : false}
                      onChange={question.id ? handleDebitOrderCheckbox(question.id) : undefined}
                      className="h-5 w-5 rounded border-gray-300 focus:ring-2 focus:ring-black"
                    />
                  </div>
                  <label 
                    htmlFor={`debit-declaration-${question.id || index}`}
                    className="text-sm leading-relaxed text-gray-800 cursor-pointer"
                  >
                    {question.question}
                  </label>
                </div>
              ))}
              
              {/* Signature and date fields */}
              <div className="mt-8 pt-6 border-t border-gray-300">
                <div className="grid grid-cols-2 gap-8">
                  <div>
                    <div className="text-sm font-medium mb-2">Signature of Account Holder</div>
                    <div className="border-b border-black h-10"></div>
                    <div className="text-xs text-gray-600 mt-1">Sign here</div>
                  </div>

                  <div>
  <div className="text-sm font-medium mb-2">Date</div>
  <StyledDatePicker
    value={declarationDate}
    onChange={(date) => setDeclarationDate(date)}
    className="border-b border-black w-full py-1 focus:outline-none"
  />
</div>

                </div>
                
                <div className="mt-6">
                  <div className="text-sm font-medium mb-2">Full Name of Account Holder</div>
                  <input
                    type="text"
                    value={debitOrder.surnameInitials}
                    onChange={handleDebitChange('surnameInitials')}
                    className="w-full border-b border-black pb-2 focus:outline-none font-medium"
                    placeholder="Print name clearly"
                  />
                  <div className="text-xs text-gray-600 mt-1">Print name clearly</div>
                </div>

              </div>
            </div>
          )}
        </div>

        

            {/* Declaration of Fees */}
<div className="mb-12">
  <h2 className="font-bold text-lg mb-4 border-b border-black pb-1">DECLARATION OF FEES</h2>
  
  <div className="text-sm leading-relaxed space-y-6">
    <div>
      <p className="font-bold uppercase tracking-wide mb-3">ADDITIONAL BROKER / INTERMEDIARY FEE</p>
      
      <div className="mb-4">
        <p className="text-gray-800 mb-2">
          I acknowledge that my monthly premium is
        </p>
        <div className="flex items-center gap-2">
          <span className="font-medium">R</span>
          <span className="font-medium border-b border-black pb-1 px-2 min-w-[120px] inline-block">
            {acceptData?.insurers?.[0]?.premium 
              ? parseFloat(acceptData.insurers[0].premium).toLocaleString('en-ZA', { minimumFractionDigits: 2 })
              : '________'}
          </span>
          <span className="text-gray-600 text-xs">(premium amount)</span>
        </div>
      </div>
      
      <div className="mb-4">
        <p className="text-gray-800 mb-2">
          which includes a Broker Fee of
        </p>
        <div className="flex items-center gap-2">
          <span className="font-medium">R</span>
          <span className="font-medium border-b border-black pb-1 px-2 min-w-[120px] inline-block">
            {acceptData?.insurers?.[0]?.brokerFee 
              ? parseFloat(acceptData.insurers[0].brokerFee).toLocaleString('en-ZA', { minimumFractionDigits: 2 })
              : '________'}
          </span>
          <span className="text-gray-600 text-xs">(broker fee amount)</span>
        </div>
      </div>
      
      <p className="text-gray-800 italic">
        to which I have provided full consent to my Broker to charge such fee.
      </p>
    </div>
    
    <div>
      <p className="text-gray-800 mb-2">
        I hereby confirm that my Broker has disclosed the Management fee of
      </p>
      <div className="flex items-center gap-2">
        <span className="font-medium">R</span>
        <span className="font-medium border-b border-black pb-1 px-2 min-w-[120px] inline-block">
          {acceptData?.insurers?.[0]?.managementFee 
            ? parseFloat(acceptData.insurers[0].managementFee).toLocaleString('en-ZA', { minimumFractionDigits: 2 })
            : '________'}
        </span>
        <span className="text-gray-600 text-xs">(management fee amount)</span>
      </div>
    </div>
    
    {/* Combined acknowledgment */}
    <div className="mt-8 pt-6 border-t border-gray-300">
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          className="mt-1 h-5 w-5 rounded border-gray-300 focus:ring-2 focus:ring-black"
        />
        <label className="text-sm leading-relaxed text-gray-800">
          I acknowledge and confirm that I have read and understood all the fees disclosed above, 
          including the Broker Fee and Management Fee, and I consent to these fees being charged 
          as part of my insurance premium.
        </label>
      </div>
    </div>
  </div>
</div>

        {/* Management Fees Section */}
        <div className="mb-12">
            <h2 className="font-bold text-lg mb-4 border-b border-black pb-1">MANAGEMENT FEES</h2>
            
            {/* Get management fee from acceptData */}
            {(() => {
                const managementFeeAmount = acceptData?.insurers?.[0]?.managementFee || acceptData?.managementFee || managementFee || '';
                
                if (managementFeeAmount && managementFeeAmount !== '' && managementFeeAmount !== '0') {
                    const formattedAmount = `R ${parseFloat(managementFeeAmount).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;
                    return (
                        <div style={{ marginBottom: '6mm', padding: '4mm', backgroundColor: '#f0f9ff', borderLeft: '4px solid #1a365d', borderRadius: '2mm' }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '2mm', fontSize: '11pt' }}>Management Fee Amount:</div>
                            <div style={{ fontSize: '14pt', fontWeight: 'bold', color: '#1a365d' }}>
                                {formattedAmount}
                            </div>
                            <div style={{ fontSize: '8pt', color: '#666', marginTop: '2mm' }}>
                                This management fee is charged for the additional services provided by the insurer.
                            </div>
                        </div>
                    );
                } else {
                    return (
                        <div className="py-4 text-gray-600 italic">
                            <p>No management fee information available.</p>
                        </div>
                    );
                }
            })()}
        </div>

        {/* Services Provided */}
        <div className="mb-12">
          <h2 className="font-bold text-lg mb-4 border-b border-black pb-1">SERVICES PROVIDED</h2>
          
          {loadingServiceProviderQuestions ? (
            <div className="py-4 text-gray-500 italic">
              Loading services information...
            </div>
          ) : serviceProviderQuestions.length === 0 ? (
            <p className="text-gray-700 italic">
              No services provided information configured at this time.
            </p>
          ) : (
            <div className="space-y-4 text-gray-800">
              {serviceProviderQuestions.map((question, index) => (
                <div key={question.id || index} className="flex items-start">
                  <span className="mr-2 text-gray-700">{index + 1}.</span>
                  <p className={`leading-relaxed ${question.is_default ? 'font-medium' : ''}`}>
                    {question.question}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Insurance Proposal Form */}
          <div className="mb-12">
            <h2 className="font-bold text-lg mb-4 border-b border-black pb-1">INSURANCE PROPOSAL FORM</h2>
            
            <div className="text-sm leading-relaxed space-y-4 text-gray-800">
              <p>
                For the additional services set out above,{' '}
                <span className="font-medium border-b border-gray-400 px-1">
                  {acceptData?.insurers?.[0]?.name || '[Insurer selected from Flowchart]'}
                </span>{' '}
                charges a flat fee amount of R{' '}
                <span className="font-medium border-b border-gray-400 px-1">
                  {managementFee || acceptData?.managementFee || acceptData?.insurers?.[0]?.managementFee || '[Management fee amount]'}
                </span>{' '}
                and this amount is inclusive of VAT. This fee will form part of your total premium.
              </p>
              
              <p className="mt-6">
                I,{' '}
                <span className="font-medium border-b border-black px-2 inline-block min-w-[300px]">
                  {client 
                    ? `${client.name || ''} ${client.surname || ''}`.trim() || '[Policyholder name and surname]'
                    : '[Policyholder name and surname]'}
                </span>
                , consent to paying the{' '}
                <span className="font-medium border-b border-gray-400 px-1">
                  {acceptData?.insurers?.[0]?.name || '[Insurer selected from Flowchart]'}
                </span>{' '}
                Management Fee, for the additional services set out above.
              </p>
              
              <div className="mt-8 space-y-6">
              {/* Signed at - editable */}
              <div>
                <p className="mb-2 font-medium">Signed at</p>
                <input
                  type="text"
                  value={commentForm.signed_at}
                  onChange={handleCommentFormChange('signed_at')}
                  className="w-full max-w-md border-b border-black pb-2 focus:outline-none font-medium"
                  placeholder="Enter location"
                />
                <div className="text-xs text-gray-600 mt-1">Location</div>
              </div>
              
              {/* Date fields - editable */}
              <div className="flex items-center gap-4">
                <div>
                  <p className="mb-2 font-medium">on this</p>
                  <input
                    type="text"
                    id="signatureDay"
                    placeholder="DD"
                    className="w-24 border-b border-black pb-2 focus:outline-none font-medium text-center"
                    maxLength={2}
                  />
                  <div className="text-xs text-gray-600 mt-1">Day</div>
                </div>
                
                <div>
                  <p className="mb-2 font-medium">day of</p>
                  <input
                    type="text"
                    id="signatureMonth"
                    placeholder="MM"
                    className="w-32 border-b border-black pb-2 focus:outline-none font-medium text-center"
                    maxLength={2}
                  />
                  <div className="text-xs text-gray-600 mt-1">Month</div>
                </div>
                
                <div>
                  <p className="mb-2 font-medium">20</p>
                  <input
                    type="text"
                    id="signatureYear"
                    placeholder="YY"
                    className="w-16 border-b border-black pb-2 focus:outline-none font-medium text-center"
                    maxLength={2}
                  />
                  <div className="text-xs text-gray-600 mt-1">Year</div>
                </div>
              </div>
              
              {/* Signature - keep as is for now (will be captured at the end) */}
              <div>
                <p className="mb-2 font-medium">Policyholder Signature</p>
                <div className="border-b border-black w-full max-w-md pb-1"></div>
                <div className="text-xs text-gray-600 mt-1">Sign here</div>
              </div>
            </div>

            </div>
          </div>

          {/* Previous Claims */}
            <div className="mb-12">
              <h2 className="font-bold text-lg mb-4 border-b border-black pb-1">PREVIOUS CLAIMS</h2>
              
              {loadingClaims ? (
                <div className="py-4 text-gray-500 italic">
                  Loading previous claims...
                </div>
              ) : clientClaims.length === 0 ? (
                <div className="py-8 text-center text-gray-500 italic">
                  No previous claims recorded.
                </div>
              ) : (
                <div className="text-sm">
                  <div className="grid grid-cols-4 gap-4 mb-2 font-medium border-b border-gray-300 pb-2">
                    <div>Claim Date</div>
                    <div>Claim Value</div>
                    <div>Description</div>
                    <div>Policy No / Previous Insurer</div>
                  </div>
                  
                  {/* Display only database records */}
                  {clientClaims.map((claim, index) => (
                    <div key={claim.id || index} className="grid grid-cols-4 gap-4 mb-4 py-2">
                      <div className="border-b border-gray-300 pb-2">
                        {claim.claim_date ? claim.claim_date.split('T')[0] : '—'}
                      </div>
                      <div className="border-b border-gray-300 pb-2">
                        {claim.claim_value ? `R ${claim.claim_value}` : 'R 0.00'}
                      </div>
                      <div className="border-b border-gray-300 pb-2">
                        {claim.description || '—'}
                      </div>
                      <div className="border-b border-gray-300 pb-2">
                        {claim.policy_number || claim.previous_insurer ? (
                          <>
                            {claim.policy_number || ''}
                            {claim.policy_number && claim.previous_insurer ? ' / ' : ''}
                            {claim.previous_insurer || ''}
                          </>
                        ) : '—'}
                      </div>
                    </div>
                  ))}
                  
                  {/* Optional note */}
                  <div className="mt-6 text-xs text-gray-600 italic">
                    <p>Note: Details of claims made in the past 3-5 years.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Previous Insurance Section */}
<div className="mb-12">
  <h2 className="font-bold text-lg mb-4 border-b border-black pb-1">PREVIOUS INSURANCE</h2>
  
  {loadingPreviousInsurance ? (
    <div className="py-4 text-gray-500 italic">
      Loading previous insurance...
    </div>
  ) : previousInsurance.length === 0 ? (
    <div className="py-8 text-center text-gray-500 italic">
      No previous insurance recorded.
    </div>
  ) : (
    <div className="text-sm">
      <div className="grid grid-cols-4 gap-4 mb-2 font-medium border-b border-gray-300 pb-2">
        <div>Insurer</div>
        <div>Policy Number</div>
        <div>Start Date</div>
        <div>End Date</div>
      </div>
      
      {previousInsurance.map((insurance, index) => (
        <div key={insurance.id || index} className="grid grid-cols-4 gap-4 mb-4 py-2">
          <div className="border-b border-gray-300 pb-2">
            {insurance.insurer || '—'}
          </div>
          <div className="border-b border-gray-300 pb-2">
            {insurance.policy_number || insurance.policyNumber || '—'}
          </div>
          <div className="border-b border-gray-300 pb-2">
            {insurance.start_date || insurance.startDate ? new Date(insurance.start_date || insurance.startDate).toLocaleDateString() : '—'}
          </div>
          <div className="border-b border-gray-300 pb-2">
            {insurance.end_date || insurance.endDate ? new Date(insurance.end_date || insurance.endDate).toLocaleDateString() : '—'}
          </div>
        </div>
      ))}
      
      {/* Optional description column if needed */}
      {previousInsurance.some(ins => ins.description) && (
        <div className="mt-4">
          <div className="grid grid-cols-1 gap-2">
            {previousInsurance.map((insurance, index) => (
              insurance.description && (
                <div key={`desc-${insurance.id || index}`} className="text-sm">
                  <span className="font-medium">Note for {insurance.insurer}:</span> {insurance.description}
                </div>
              )
            ))}
          </div>
        </div>
      )}
      
      <div className="mt-4 text-xs text-gray-600 italic">
        <p>Previous insurance history for the past 3-5 years.</p>
      </div>
    </div>
  )}
</div>

            {/* Property Details Section */}
              <div className="mb-12">
                <h2 className="font-bold text-lg mb-4 border-b border-black pb-1">PROPERTY DETAILS</h2>
                
                {loadingProperty ? (
                  <div className="py-4 text-gray-500 italic">
                    Loading property details...
                  </div>
                ) : !propertyData ? (
                  <div className="py-8 text-center text-gray-500 italic">
                    No property recorded.
                  </div>
                ) : (
                  <>
                    <div className="space-y-6">
                      {/* Address Details */}
                      <div className="space-y-3">
                        <h3 className="font-bold text-gray-800 text-base mb-3">Address</h3>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="text-gray-700">Street:</div>
                          <div className="text-gray-800">
                            {propertyData.address_line_1} {propertyData.address_line_2 ? `, ${propertyData.address_line_2}` : ''}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="text-gray-700">City/Town:</div>
                          <div className="text-gray-800">{propertyData.city}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="text-gray-700">Suburb:</div>
                          <div className="text-gray-800">{propertyData.suburb}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="text-gray-700">Province:</div>
                          <div className="text-gray-800">{propertyData.region}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="text-gray-700">Postal Code:</div>
                          <div className="text-gray-800">{propertyData.postal_code}</div>
                        </div>
                      </div>
                      
                      {/* Property Information */}
                      <div className="space-y-3">
                        <h3 className="font-bold text-gray-800 text-base mb-3">Property Information</h3>

                        {/* Row 1: Property Type & Residence Type */}
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="grid grid-cols-2 gap-2">
                            <div className="text-gray-700">Property Type:</div>
                            <div className="text-gray-800">{propertyData.property_type || '—'}</div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="text-gray-700">Residence Type:</div>
                            <div className="text-gray-800">{propertyData.residence_type || '—'}</div>
                          </div>
                        </div>

                        {/* Row 2: Building Age & Situated At */}
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="grid grid-cols-2 gap-2">
                            <div className="text-gray-700">Building Age:</div>
                            <div className="text-gray-800">{propertyData.building_age || '—'}</div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="text-gray-700">Situated At:</div>
                            <div className="text-gray-800">{propertyData.situated_at || '—'}</div>
                          </div>
                        </div>

                        {/* Row 3: Cover Type & Insured Value */}
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="grid grid-cols-2 gap-2">
                            <div className="text-gray-700">Cover Type:</div>
                            <div className="text-gray-800">{propertyData.cover_type || '—'}</div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="text-gray-700">Insured Value:</div>
                            <div className="text-gray-800">R {propertyData.insured_value?.toLocaleString() || '0.00'}</div>
                          </div>
                        </div>

                        {/* Row 4: No Claim Bonus (alone, but can be paired with something else if needed) */}
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="grid grid-cols-2 gap-2">
                            <div className="text-gray-700">No Claim Bonus:</div>
                            <div className="text-gray-800">{propertyData.no_claim_bonus || 'None'}</div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            {/* Empty space for balance - or add another field here */}
                            <div></div>
                            <div></div>
                          </div>
                        </div>
                      </div>

                      {/* Additional Property Details */}
                      <div className="space-y-3">
                        <h3 className="font-bold text-gray-800 text-base mb-3">Additional Property Details</h3>

                        {/* Row 1 */}
                        <div className="grid grid-cols-4 gap-2 text-sm">
                          <div className="text-gray-700">Has swimming pool:</div>
                          <div className="text-gray-800">{propertyData.has_swimming_pool ? 'Yes' : 'No'}</div>
                          <div className="text-gray-700">Has motorized gate:</div>
                          <div className="text-gray-800">{propertyData.has_motorized_gate ? 'Yes' : 'No'}</div>
                        </div>

                        {/* Row 2 */}
                        <div className="grid grid-cols-4 gap-2 text-sm">
                          <div className="text-gray-700">Is the property unoccupied for more than 60 consecutive days per annum:</div>
                          <div className="text-gray-800">{propertyData.is_unoccupied_more_than_60_days ? 'Yes' : 'No'}</div>
                          <div className="text-gray-700">Is commune:</div>
                          <div className="text-gray-800">{propertyData.is_commune ? 'Yes' : 'No'}</div>
                        </div>

                        {/* Row 3 */}
                        <div className="grid grid-cols-4 gap-2 text-sm">
                          <div className="text-gray-700">Under Construction:</div>
                          <div className="text-gray-800">{propertyData.under_construction ? 'Yes' : 'No'}</div>
                          <div className="text-gray-700">Built on Steep Slope:</div>
                          <div className="text-gray-800">{propertyData.built_on_steep_slope ? 'Yes' : 'No'}</div>
                        </div>

                        {/* Row 4 */}
                        <div className="grid grid-cols-4 gap-2 text-sm">
                          <div className="text-gray-700">Has excessive use of retaining walls:</div>
                          <div className="text-gray-800">{propertyData.has_excessive_retaining_walls ? 'Yes' : 'No'}</div>
                          <div className="text-gray-700">Less than 100 m from Sea, Lagoon, River, Natural Stream, Dam or Swamp:</div>
                          <div className="text-gray-800">{propertyData.near_water_body ? 'Yes' : 'No'}</div>
                        </div>

                        {/* Row 5 */}
                        <div className="grid grid-cols-4 gap-2 text-sm">
                          <div className="text-gray-700">Has visible signs of rising damp:</div>
                          <div className="text-gray-800">{propertyData.has_rising_damp ? 'Yes' : 'No'}</div>
                          <div className="text-gray-700">Has visible signs of cracks in the wall:</div>
                          <div className="text-gray-800">{propertyData.has_wall_cracks ? 'Yes' : 'No'}</div>
                        </div>

                        {/* Row 6 */}
                        <div className="grid grid-cols-4 gap-2 text-sm">
                          <div className="text-gray-700">Has history of subsidence or landslip in the area:</div>
                          <div className="text-gray-800">{propertyData.has_subsidence_history ? 'Yes' : 'No'}</div>
                          <div className="text-gray-700">Has Outbuilding:</div>
                          <div className="text-gray-800">{propertyData.has_outbuilding ? 'Yes' : 'No'}</div>
                        </div>

                        {/* Row 7 */}
                        <div className="grid grid-cols-4 gap-2 text-sm">
                          <div className="text-gray-700">Property surrounded by perimeter fence at least 1.8m high:</div>
                          <div className="text-gray-800">{propertyData.has_perimeter_fence ? 'Yes' : 'No'}</div>
                          <div className="text-gray-700">Has electric fence on perimeter:</div>
                          <div className="text-gray-800">{propertyData.has_electric_fence ? 'Yes' : 'No'}</div>
                        </div>

                        {/* Row 8 */}
                        <div className="grid grid-cols-4 gap-2 text-sm">
                          <div className="text-gray-700">Thatch roof present:</div>
                          <div className="text-gray-800">{propertyData.has_thatch_roof ? 'Yes' : 'No'}</div>
                          <div className="text-gray-700"></div>
                          <div className="text-gray-800"></div>
                        </div>

                        {/* Thatch roof sub-options */}
                        {propertyData.has_thatch_roof && (
                          <>
                            <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                              <div className="text-gray-700">Thatch 5m to boundary:</div>
                              <div className="text-gray-800">{propertyData.thatch_less_than_5m ? 'Yes' : 'No'}</div>
                              <div className="text-gray-700">SABS Lightning Conductor:</div>
                              <div className="text-gray-800">{propertyData.thatch_sabs_conductor ? 'Yes' : 'No'}</div>
                            </div>
                            <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                              <div className="text-gray-700">Thatch Treated:</div>
                              <div className="text-gray-800">{propertyData.thatch_treated ? 'Yes' : 'No'}</div>
                              <div className="text-gray-700"></div>
                              <div className="text-gray-800"></div>
                            </div>
                          </>
                        )}

                        {/* Row 9 - Accidental damage cover */}
                        <div className="grid grid-cols-4 gap-2 text-sm">
                          <div className="text-gray-700">Accidental damage cover:</div>
                          <div className="text-gray-800">
                            {propertyData.has_accidental_damage_cover ? `Yes - R ${propertyData.accidental_damage_cover_value?.toLocaleString() || '0.00'}` : 'No'}
                          </div>
                          <div className="text-gray-700">Power Surge Cover:</div>
                          <div className="text-gray-800">
                            {propertyData.has_power_surge_cover ? `Yes - R ${propertyData.power_surge_cover_value?.toLocaleString() || '0.00'}` : 'No'}
                          </div>
                        </div>
                      </div>

                      {/* Construction and Surroundings */}
                      <div className="space-y-4">
                        <h3 className="font-bold text-gray-800 text-base mb-3">Construction and Surroundings</h3>
                        
                        {/* Wall Materials Subheading */}
                        <div className="space-y-2">
                          <h4 className="font-semibold text-gray-700 text-sm border-b border-gray-200 pb-1">Wall Materials</h4>
                          
                          {/* Row 1 */}
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Brick:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = propertyData.wall_materials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) return materials.includes('Brick') ? 'Yes' : 'No';
                                if (typeof materials === 'string') return materials.toLowerCase().includes('brick') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                            <div className="text-gray-700">Cement:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = propertyData.wall_materials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) return materials.includes('Cement') ? 'Yes' : 'No';
                                if (typeof materials === 'string') return materials.toLowerCase().includes('cement') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                          </div>
                          
                          {/* Row 2 */}
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Corrugated Iron:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = propertyData.wall_materials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) return materials.includes('Corrugated Iron') ? 'Yes' : 'No';
                                if (typeof materials === 'string') return materials.toLowerCase().includes('corrugated') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                            <div className="text-gray-700">Glass:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = propertyData.wall_materials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) return materials.includes('Glass') ? 'Yes' : 'No';
                                if (typeof materials === 'string') return materials.toLowerCase().includes('glass') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                          </div>
                          
                          {/* Row 3 */}
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Prefab:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = propertyData.wall_materials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) return materials.includes('Prefab') ? 'Yes' : 'No';
                                if (typeof materials === 'string') return materials.toLowerCase().includes('prefab') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                            <div className="text-gray-700">Asbestos:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = propertyData.wall_materials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) return materials.includes('Asbestos') ? 'Yes' : 'No';
                                if (typeof materials === 'string') return materials.toLowerCase().includes('asbestos') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                          </div>
                          
                          {/* Row 4 */}
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Wood:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = propertyData.wall_materials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) return materials.includes('Wood') ? 'Yes' : 'No';
                                if (typeof materials === 'string') return materials.toLowerCase().includes('wood') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                            <div className="text-gray-700">Other:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = propertyData.wall_materials;
                                const wallOptions = ['Brick', 'Cement', 'Corrugated Iron', 'Prefab', 'Glass', 'Asbestos', 'Wood'];
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) return materials.filter(m => !wallOptions.includes(m)).length > 0 ? 'Yes' : 'No';
                                if (typeof materials === 'string') return materials.toLowerCase().includes('other') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                          </div>
                        </div>
                        
                        {/* Roof Materials Subheading */}
                        <div className="space-y-2">
                          <h4 className="font-semibold text-gray-700 text-sm border-b border-gray-200 pb-1">Roof Materials</h4>
                          
                          {/* Row 1 */}
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Concrete:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = propertyData.roof_materials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) return materials.includes('Concrete') ? 'Yes' : 'No';
                                if (typeof materials === 'string') return materials.toLowerCase().includes('concrete') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                            <div className="text-gray-700">Corrugated Iron:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = propertyData.roof_materials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) return materials.includes('Corrugated Iron') ? 'Yes' : 'No';
                                if (typeof materials === 'string') return materials.toLowerCase().includes('corrugated') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                          </div>
                          
                          {/* Row 2 */}
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Glass:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = propertyData.roof_materials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) return materials.includes('Glass') ? 'Yes' : 'No';
                                if (typeof materials === 'string') return materials.toLowerCase().includes('glass') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                            <div className="text-gray-700">Thatch:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = propertyData.roof_materials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) return materials.includes('Thatch') ? 'Yes' : 'No';
                                if (typeof materials === 'string') return materials.toLowerCase().includes('thatch') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                          </div>
                          
                          {/* Row 3 */}
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Asbestos:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = propertyData.roof_materials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) return materials.includes('Asbestos') ? 'Yes' : 'No';
                                if (typeof materials === 'string') return materials.toLowerCase().includes('asbestos') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                            <div className="text-gray-700">Wood:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = propertyData.roof_materials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) return materials.includes('Wood') ? 'Yes' : 'No';
                                if (typeof materials === 'string') return materials.toLowerCase().includes('wood') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                          </div>
                          
                          {/* Row 4 */}
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Tiles:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = propertyData.roof_materials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) return materials.includes('Tiles') ? 'Yes' : 'No';
                                if (typeof materials === 'string') return materials.toLowerCase().includes('tile') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                          </div>
                        </div>
                        
                        {/* Floor Materials Subheading */}
                        <div className="space-y-2">
                          <h4 className="font-semibold text-gray-700 text-sm border-b border-gray-200 pb-1">Floor Materials</h4>
                          {/* Row 1 */}
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Carpet:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = propertyData.floor_materials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) return materials.includes('Carpet') ? 'Yes' : 'No';
                                if (typeof materials === 'string') return materials.toLowerCase().includes('carpet') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                            <div className="text-gray-700">Sealed:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = propertyData.floor_materials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) return materials.includes('Sealed') ? 'Yes' : 'No';
                                if (typeof materials === 'string') return materials.toLowerCase().includes('sealed') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                          </div>
                          
                          {/* Row 2 */}
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Tiles:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = propertyData.floor_materials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) return materials.includes('Tiles') ? 'Yes' : 'No';
                                if (typeof materials === 'string') return materials.toLowerCase().includes('tile') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                            <div className="text-gray-700">Wooden Floors:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = propertyData.floor_materials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) return materials.includes('Wooden Floors') ? 'Yes' : 'No';
                                if (typeof materials === 'string') return materials.toLowerCase().includes('wooden floors') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                          </div>
                        </div>
                        
                        {/* Other Surrounding Areas Subheading */}
                        <div className="space-y-2">
                          <h4 className="font-semibold text-gray-700 text-sm border-b border-gray-200 pb-1">Other Surrounding Areas</h4>
                          
                          {/* Row 1 */}
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Farm:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const areas = propertyData.other_surrounding_areas;
                                if (!areas) return 'No';
                                if (Array.isArray(areas)) return areas.includes('Farm') ? 'Yes' : 'No';
                                if (typeof areas === 'string') return areas.toLowerCase().includes('farm') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                            <div className="text-gray-700">Plot:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const areas = propertyData.other_surrounding_areas;
                                if (!areas) return 'No';
                                if (Array.isArray(areas)) return areas.includes('Plot') ? 'Yes' : 'No';
                                if (typeof areas === 'string') return areas.toLowerCase().includes('plot') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                          </div>
                          
                          {/* Row 2 */}
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Golf Estate:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const areas = propertyData.other_surrounding_areas;
                                if (!areas) return 'No';
                                if (Array.isArray(areas)) return areas.includes('Golf Estate') ? 'Yes' : 'No';
                                if (typeof areas === 'string') return areas.toLowerCase().includes('golf estate') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                            <div className="text-gray-700">Country Estate/Residential:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const areas = propertyData.other_surrounding_areas;
                                if (!areas) return 'No';
                                if (Array.isArray(areas)) return areas.includes('Country Estate') || areas.includes('Residential Area') ? 'Yes' : 'No';
                                if (typeof areas === 'string') return areas.toLowerCase().includes('country estate') || areas.toLowerCase().includes('residential area') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                          </div>
                          
                          {/* Row 3 */}
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Security Complex:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const areas = propertyData.other_surrounding_areas;
                                if (!areas) return 'No';
                                if (Array.isArray(areas)) return areas.includes('Security Complex') ? 'Yes' : 'No';
                                if (typeof areas === 'string') return areas.toLowerCase().includes('security complex') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                            <div className="text-gray-700">Complex With No Access Control:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const areas = propertyData.other_surrounding_areas;
                                if (!areas) return 'No';
                                if (Array.isArray(areas)) return areas.includes('Complex With No Access Control') ? 'Yes' : 'No';
                                if (typeof areas === 'string') return areas.toLowerCase().includes('complex with no access control') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                          </div>
                          
                          {/* Row 4 */}
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Boomed Off Suburb:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const areas = propertyData.other_surrounding_areas;
                                if (!areas) return 'No';
                                if (Array.isArray(areas)) return areas.includes('Boomed Off Suburb') ? 'Yes' : 'No';
                                if (typeof areas === 'string') return areas.toLowerCase().includes('boomed off suburb') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                            <div className="text-gray-700">Suburb:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const areas = propertyData.other_surrounding_areas;
                                if (!areas) return 'No';
                                if (Array.isArray(areas)) return areas.includes('Suburb') ? 'Yes' : 'No';
                                if (typeof areas === 'string') return areas.toLowerCase().includes('suburb') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                          </div>
                          
                          {/* Row 5 */}
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Industrial Area:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const areas = propertyData.other_surrounding_areas;
                                if (!areas) return 'No';
                                if (Array.isArray(areas)) return areas.includes('Industrial Area') ? 'Yes' : 'No';
                                if (typeof areas === 'string') return areas.toLowerCase().includes('industrial area') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                            <div className="text-gray-700">Commercial Area:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const areas = propertyData.other_surrounding_areas;
                                if (!areas) return 'No';
                                if (Array.isArray(areas)) return areas.includes('Commercial Area') ? 'Yes' : 'No';
                                if (typeof areas === 'string') return areas.toLowerCase().includes('commercial area') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                          </div>
                          
                          {/* Row 6 */}
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Inner City/CBD:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const areas = propertyData.other_surrounding_areas;
                                if (!areas) return 'No';
                                if (Array.isArray(areas)) return areas.includes('Inner City') || areas.includes('CBD') ? 'Yes' : 'No';
                                if (typeof areas === 'string') return areas.toLowerCase().includes('inner city') || areas.toLowerCase().includes('cbd') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                            <div className="text-gray-700">Informal Settlement:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const areas = propertyData.other_surrounding_areas;
                                if (!areas) return 'No';
                                if (Array.isArray(areas)) return areas.includes('Informal Settlement') ? 'Yes' : 'No';
                                if (typeof areas === 'string') return areas.toLowerCase().includes('informal settlement') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                          </div>
                          
                          {/* Row 7 */}
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Retirement Village:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const areas = propertyData.other_surrounding_areas;
                                if (!areas) return 'No';
                                if (Array.isArray(areas)) return areas.includes('Retirement Village') ? 'Yes' : 'No';
                                if (typeof areas === 'string') return areas.toLowerCase().includes('retirement village') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                            <div className="text-gray-700">Nature Reserve:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const areas = propertyData.other_surrounding_areas;
                                if (!areas) return 'No';
                                if (Array.isArray(areas)) return areas.includes('Nature Reserve') ? 'Yes' : 'No';
                                if (typeof areas === 'string') return areas.toLowerCase().includes('nature reserve') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                          </div>
                          
                          {/* Row 8 */}
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Holiday Resort:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const areas = propertyData.other_surrounding_areas;
                                if (!areas) return 'No';
                                if (Array.isArray(areas)) return areas.includes('Holiday Resort') ? 'Yes' : 'No';
                                if (typeof areas === 'string') return areas.toLowerCase().includes('holiday resort') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                            <div className="text-gray-700">School/University:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const areas = propertyData.other_surrounding_areas;
                                if (!areas) return 'No';
                                if (Array.isArray(areas)) return areas.includes('School') || areas.includes('University') ? 'Yes' : 'No';
                                if (typeof areas === 'string') return areas.toLowerCase().includes('school') || areas.toLowerCase().includes('university') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                          </div>
                          
                          {/* Row 9 */}
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Parks:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const areas = propertyData.other_surrounding_areas;
                                if (!areas) return 'No';
                                if (Array.isArray(areas)) return areas.includes('Parks') ? 'Yes' : 'No';
                                if (typeof areas === 'string') return areas.toLowerCase().includes('parks') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                            <div className="text-gray-700">Highways:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const areas = propertyData.other_surrounding_areas;
                                if (!areas) return 'No';
                                if (Array.isArray(areas)) return areas.includes('Highways') ? 'Yes' : 'No';
                                if (typeof areas === 'string') return areas.toLowerCase().includes('highways') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                          </div>
                          
                          {/* Row 10 */}
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Railway Lines:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const areas = propertyData.other_surrounding_areas;
                                if (!areas) return 'No';
                                if (Array.isArray(areas)) return areas.includes('Railway Lines') ? 'Yes' : 'No';
                                if (typeof areas === 'string') return areas.toLowerCase().includes('railway lines') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                            <div className="text-gray-700">Shops/Cafes:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const areas = propertyData.other_surrounding_areas;
                                if (!areas) return 'No';
                                if (Array.isArray(areas)) return areas.includes('Shops') || areas.includes('Cafes') ? 'Yes' : 'No';
                                if (typeof areas === 'string') return areas.toLowerCase().includes('shops') || areas.toLowerCase().includes('cafes') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                          </div>
                          
                          {/* Row 11 */}
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Squatter Camps:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const areas = propertyData.other_surrounding_areas;
                                if (!areas) return 'No';
                                if (Array.isArray(areas)) return areas.includes('Squatter Camps') ? 'Yes' : 'No';
                                if (typeof areas === 'string') return areas.toLowerCase().includes('squatter camps') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                            <div className="text-gray-700">Open Fields:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const areas = propertyData.other_surrounding_areas;
                                if (!areas) return 'No';
                                if (Array.isArray(areas)) return areas.includes('Open Fields') ? 'Yes' : 'No';
                                if (typeof areas === 'string') return areas.toLowerCase().includes('open fields') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                          </div>
                          
                          {/* Row 12 */}
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Other:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const areas = propertyData.other_surrounding_areas;
                                const areaOptions = ['Garden', 'Paved Areas', 'Pool Area', 'Farm', 'Plot', 'Golf Estate', 'Country Estate', 'Residential Area', 'Security Complex', 'Complex With No Access Control', 'Boomed Off Suburb', 'Suburb', 'Industrial Area', 'Commercial Area', 'Inner City', 'CBD', 'Informal Settlement', 'Retirement Village', 'Nature Reserve', 'Holiday Resort', 'School', 'University', 'Parks', 'Highways', 'Railway Lines', 'Shops', 'Cafes', 'Squatter Camps', 'Open Fields'];
                                if (!areas) return 'No';
                                if (Array.isArray(areas)) return areas.filter(a => !areaOptions.some(option => a.includes(option))).length > 0 ? 'Yes' : 'No';
                                if (typeof areas === 'string') return areas.toLowerCase().includes('other') ? 'Yes' : 'No';
                                return 'No';
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>

                    </div>

                    {/* Insurance Details Section - Separate section after Construction and Surroundings */}
                    <div className="mt-6 space-y-3">
                      <h3 className="font-bold text-gray-800 text-base mb-3">Insurance Details</h3>
                      
                      <div className="grid grid-cols-4 gap-2 text-sm">
                        <div className="text-gray-700">Premium:</div>
                        <div className="text-gray-800">
                          {propertyData.premium ? `R ${propertyData.premium.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                        </div>
                        <div className="text-gray-700">Excess:</div>
                        <div className="text-gray-800">
                          {propertyData.excess ? `R ${propertyData.excess.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                        </div>
                      </div>
                      
                    </div>

                    {/* ===== PROPERTY QUESTIONS - ONLY SHOW IF PRODUCT EXISTS ===== */}
                    {!loadingPropertyQuestions && propertyQuestions.length > 0 && (
                      <div className="mt-6 space-y-4 pt-4 border-t border-gray-300">
                        <h3 className="font-bold text-gray-800 text-base mb-3">Property Questions</h3>
                        
                        {propertyQuestions.map((question, index) => (
                          <div key={question.id || index} className="flex items-start">
                            <span className="text-sm text-gray-800">
                              {index + 1}. {question.question}
                              {question.is_important && (
                                <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">Important</span>
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

            {/* Household Contents Section */}
            <div className="mb-12">
              <h2 className="font-bold text-lg mb-4 border-b border-black pb-1">HOUSEHOLD CONTENTS</h2>
              
              {loadingHouseholdContents ? (
                <div className="py-4 text-gray-500 italic">
                  Loading household contents...
                </div>
              ) : householdContents.length === 0 ? (
                <div className="py-8 text-center text-gray-500 italic">
                  No household contents recorded.
                </div>
              ) : (
                householdContents.map((content, index) => (
                  <div key={content.id || index} className="mb-8 last:mb-0">
                    {index > 0 && <hr className="my-6 border-gray-300" />}
                    
                    <div className="space-y-6">

                      {/* Address Details */}
                      <div className="space-y-3">
                        <h3 className="font-bold text-gray-800 text-base mb-3">Address Details</h3>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="text-gray-700">Address:</div>
                          <div className="text-gray-800">
                            {content.addressLine1} {content.addressLine2 ? `, ${content.addressLine2}` : ''}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="text-gray-700">Suburb:</div>
                          <div className="text-gray-800">{content.suburb || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="text-gray-700">City/Town:</div>
                          <div className="text-gray-800">{content.cityTown || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="text-gray-700">Region/Province:</div>
                          <div className="text-gray-800">{content.region || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="text-gray-700">Postal Code:</div>
                          <div className="text-gray-800">{content.postalCode || '—'}</div>
                        </div>
                      </div>

                      {/* Property Information */}
                      <div className="space-y-3">
                        <h3 className="font-bold text-gray-800 text-base mb-3">Property Information</h3>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="text-gray-700">Years Lived on Property:</div>
                          <div className="text-gray-800">{content.yearsLivedOnProperty || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="text-gray-700">Burglar Bar Type:</div>
                          <div className="text-gray-800">{content.burglarBarType || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="text-gray-700">Day Time Occupant Type:</div>
                          <div className="text-gray-800">{content.dayTimeOccupantType || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="text-gray-700">Cover Type:</div>
                          <div className="text-gray-800">{content.coverType || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="text-gray-700">Insured Value:</div>
                          <div className="text-gray-800">R {content.insuredValue?.toLocaleString() || '0.00'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="text-gray-700">No Claim Bonus:</div>
                          <div className="text-gray-800">{content.noClaimBonus || 'None'}</div>
                        </div>
                        
                      </div>
                      
                      {/* Security Features */}
                      <div className="space-y-3">
                        <h3 className="font-bold text-gray-800 text-base mb-3">Security Features</h3>
                        
                        {/* Row 1 */}
                        <div className="grid grid-cols-4 gap-2 text-sm">
                          <div className="text-gray-700">Does the resident conduct a business on the premises:</div>
                          <div className="text-gray-800">{content.conductsBusiness ? 'Yes' : 'No'}</div>
                          <div className="text-gray-700">Has 2 month alarm triggered records:</div>
                          <div className="text-gray-800">{content.hasAlarmRecords ? 'Yes' : 'No'}</div>
                        </div>
                        
                        {/* Row 2 */}
                        <div className="grid grid-cols-4 gap-2 text-sm">
                          <div className="text-gray-700">Has surveillance camera:</div>
                          <div className="text-gray-800">{content.hasSurveillanceCamera ? 'Yes' : 'No'}</div>
                          <div className="text-gray-700">Has external sliding door:</div>
                          <div className="text-gray-800">{content.hasExternalSlidingDoor ? 'Yes' : 'No'}</div>
                        </div>
                        
                        {/* Row 3 */}
                        <div className="grid grid-cols-4 gap-2 text-sm">
                          <div className="text-gray-700">Has security gate on doors:</div>
                          <div className="text-gray-800">{content.hasSecurityGate ? 'Yes' : 'No'}</div>
                          <div className="text-gray-700">Is in a security complex:</div>
                          <div className="text-gray-800">{content.inSecurityComplex ? 'Yes' : 'No'}</div>
                        </div>
                        
                        {/* Row 4 - Accidental damage cover */}
                        <div className="grid grid-cols-4 gap-2 text-sm">
                          <div className="text-gray-700">Accidental damage cover:</div>
                          <div className="text-gray-800">
                            {content.hasAccidentalDamageCover ? `Yes - R ${content.accidentalDamageValue?.toLocaleString() || '0.00'}` : 'No'}
                          </div>
                          <div className="text-gray-700">Power surge cover:</div>
                          <div className="text-gray-800">
                            {content.hasPowerSurgeCover ? `Yes - R ${content.powerSurgeValue?.toLocaleString() || '0.00'}` : 'No'}
                          </div>
                        </div>
                        
                        {/* Row 5 - Alarm System Type only (takes full width or half) */}
                        <div className="grid grid-cols-4 gap-2 text-sm">
                          <div className="text-gray-700">Alarm System Type:</div>
                          <div className="text-gray-800">{content.alarmSystemType || '—'}</div>
                          <div className="text-gray-700"></div>
                          <div className="text-gray-800"></div>
                        </div>
                      </div>
                      
                      {/* Property Details */}
                      <div className="space-y-3">
                        <h3 className="font-bold text-gray-800 text-base mb-3">Property Details</h3>
                        
                        {/* Row 1 - Property Type & Residence Type */}
                        <div className="grid grid-cols-4 gap-2 text-sm">
                          <div className="text-gray-700">Property Type:</div>
                          <div className="text-gray-800">{content.propertyType || '—'}</div>
                          <div className="text-gray-700">Residence Type:</div>
                          <div className="text-gray-800">{content.residenceType || '—'}</div>
                        </div>
                        
                        {/* Row 2 - Building Age & Situated Type */}
                        <div className="grid grid-cols-4 gap-2 text-sm">
                          <div className="text-gray-700">Building Age:</div>
                          <div className="text-gray-800">{content.buildingAge || '—'}</div>
                          <div className="text-gray-700">Situated Type:</div>
                          <div className="text-gray-800">{content.situatedType || '—'}</div>
                        </div>
                      </div>
                      {/* Property Features - NEW SECTION (after Property Details) */}
                      <div className="space-y-3">
                        <h3 className="font-bold text-gray-800 text-base mb-3">Property Features</h3>
                        
                        {/* Row 1 */}
                        <div className="grid grid-cols-4 gap-2 text-sm">
                          <div className="text-gray-700">Has swimming pool:</div>
                          <div className="text-gray-800">{content.hasSwimmingPool ? 'Yes' : 'No'}</div>
                          <div className="text-gray-700">Less than 100 m from Sea, Lagoon, River, Natural Stream, Dam or Swamp:</div>
                          <div className="text-gray-800">{content.nearWaterBody ? 'Yes' : 'No'}</div>
                        </div>
                        
                        {/* Row 2 */}
                        <div className="grid grid-cols-4 gap-2 text-sm">
                          <div className="text-gray-700">Has motorized gate:</div>
                          <div className="text-gray-800">{content.hasMotorizedGate ? 'Yes' : 'No'}</div>
                          <div className="text-gray-700">Has the property any visible signs of rising damp:</div>
                          <div className="text-gray-800">{content.hasRisingDamp ? 'Yes' : 'No'}</div>
                        </div>
                        
                        {/* Row 3 */}
                        <div className="grid grid-cols-4 gap-2 text-sm">
                          <div className="text-gray-700">Is property unoccupied for more than 60 consecutive days per annum:</div>
                          <div className="text-gray-800">{content.isPropertyUnoccupied ? 'Yes' : 'No'}</div>
                          <div className="text-gray-700">Has the property any visible signs of cracks in the wall:</div>
                          <div className="text-gray-800">{content.hasWallCracks ? 'Yes' : 'No'}</div>
                        </div>
                        
                        {/* Row 4 */}
                        <div className="grid grid-cols-4 gap-2 text-sm">
                          <div className="text-gray-700">Is commune:</div>
                          <div className="text-gray-800">{content.isCommune ? 'Yes' : 'No'}</div>
                          <div className="text-gray-700">History of landslip or Subsidence in the area:</div>
                          <div className="text-gray-800">{content.historyOfLandslip ? 'Yes' : 'No'}</div>
                        </div>
                        
                        {/* Row 5 */}
                        <div className="grid grid-cols-4 gap-2 text-sm">
                          <div className="text-gray-700">Under construction:</div>
                          <div className="text-gray-800">{content.underConstruction ? 'Yes' : 'No'}</div>
                          <div className="text-gray-700">Has outbuilding:</div>
                          <div className="text-gray-800">{content.hasOutbuilding ? 'Yes' : 'No'}</div>
                        </div>
                        
                        {/* Row 6 */}
                        <div className="grid grid-cols-4 gap-2 text-sm">
                          <div className="text-gray-700">Built on steep slope:</div>
                          <div className="text-gray-800">{content.builtOnSteepSlope ? 'Yes' : 'No'}</div>
                          <div className="text-gray-700">Property surrounded by perimeter fence at least 1.8m high:</div>
                          <div className="text-gray-800">{content.hasPerimeterFence ? 'Yes' : 'No'}</div>
                        </div>
                        
                        {/* Row 7 */}
                        <div className="grid grid-cols-4 gap-2 text-sm">
                          <div className="text-gray-700">Has the property any excessive use of retaining walls:</div>
                          <div className="text-gray-800">{content.hasRetainingWalls ? 'Yes' : 'No'}</div>
                          <div className="text-gray-700">Has electric fence on perimeter:</div>
                          <div className="text-gray-800">{content.hasElectricFence ? 'Yes' : 'No'}</div>
                        </div>
                        
                        {/* Row 8 */}
                        <div className="grid grid-cols-4 gap-2 text-sm">
                          <div className="text-gray-700">Thatch roof present:</div>
                          <div className="text-gray-800">{content.hasThatchRoof ? 'Yes' : 'No'}</div>
                          <div className="text-gray-700"></div>
                          <div className="text-gray-800"></div>
                        </div>
                      </div>

                      {/* Material Details */}    
                      <div className="space-y-3">
                        <h3 className="font-bold text-gray-800 text-base mb-3">Material Details</h3>
                        
                        {/* Perimeter Type Subheading */}
                        <div className="space-y-2">
                          <h4 className="font-semibold text-gray-700 text-sm border-b border-gray-200 pb-1">Perimeter Type</h4>
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Walls:</div>
                            <div className="text-gray-800">
                              {content.perimeterType ? 
                                (Array.isArray(content.perimeterType) 
                                  ? (content.perimeterType.includes('Walls') ? 'Yes' : 'No')
                                  : (typeof content.perimeterType === 'string' && content.perimeterType.split(',').some(p => p.trim().toLowerCase() === 'walls') ? 'Yes' : 'No')) 
                                : 'No'}
                            </div>
                            <div className="text-gray-700">Palisades:</div>
                            <div className="text-gray-800">
                              {content.perimeterType ? 
                                (Array.isArray(content.perimeterType) 
                                  ? (content.perimeterType.includes('Palisade Fencing') ? 'Yes' : 'No')
                                  : (typeof content.perimeterType === 'string' && content.perimeterType.split(',').some(p => p.trim().toLowerCase() === 'palisade fencing') ? 'Yes' : 'No')) 
                                : 'No'}
                            </div>
                          </div>
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Electric Fencing:</div>
                            <div className="text-gray-800">
                              {content.perimeterType ? 
                                (Array.isArray(content.perimeterType) 
                                  ? (content.perimeterType.includes('Electric Fencing') ? 'Yes' : 'No')
                                  : (typeof content.perimeterType === 'string' && content.perimeterType.split(',').some(p => p.trim().toLowerCase() === 'electric fencing') ? 'Yes' : 'No')) 
                                : 'No'}
                            </div>
                            <div className="text-gray-700">Barbed Wire:</div>
                            <div className="text-gray-800">
                              {content.perimeterType ? 
                                (Array.isArray(content.perimeterType) 
                                  ? (content.perimeterType.includes('Barbed Wire') ? 'Yes' : 'No')
                                  : (typeof content.perimeterType === 'string' && content.perimeterType.split(',').some(p => p.trim().toLowerCase() === 'barbed wire') ? 'Yes' : 'No')) 
                                : 'No'}
                            </div>
                          </div>
                        </div>
                        
                        {/* Wall Materials Subheading */}
                        <div className="space-y-2 mt-4">
                          <h4 className="font-semibold text-gray-700 text-sm border-b border-gray-200 pb-1">Wall Materials</h4>
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Brick:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = content.wallMaterials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) {
                                  return materials.includes('Brick') ? 'Yes' : 'No';
                                }
                                if (typeof materials === 'string') {
                                  return materials.split(',').some(m => m.trim().toLowerCase() === 'brick') ? 'Yes' : 'No';
                                }
                                return 'No';
                              })()}
                            </div>
                            <div className="text-gray-700">Cement:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = content.wallMaterials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) {
                                  return materials.includes('Cement') ? 'Yes' : 'No';
                                }
                                if (typeof materials === 'string') {
                                  return materials.split(',').some(m => m.trim().toLowerCase() === 'cement') ? 'Yes' : 'No';
                                }
                                return 'No';
                              })()}
                            </div>
                          </div>
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Corrugated Iron:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = content.wallMaterials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) {
                                  return materials.includes('Corrugated Iron') ? 'Yes' : 'No';
                                }
                                if (typeof materials === 'string') {
                                  return materials.split(',').some(m => m.trim().toLowerCase() === 'corrugated iron') ? 'Yes' : 'No';
                                }
                                return 'No';
                              })()}
                            </div>
                            <div className="text-gray-700">Glass:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = content.wallMaterials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) {
                                  return materials.includes('Glass') ? 'Yes' : 'No';
                                }
                                if (typeof materials === 'string') {
                                  return materials.split(',').some(m => m.trim().toLowerCase() === 'glass') ? 'Yes' : 'No';
                                }
                                return 'No';
                              })()}
                            </div>
                          </div>
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Prefab:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = content.wallMaterials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) {
                                  return materials.includes('Prefab') ? 'Yes' : 'No';
                                }
                                if (typeof materials === 'string') {
                                  return materials.split(',').some(m => m.trim().toLowerCase() === 'prefab') ? 'Yes' : 'No';
                                }
                                return 'No';
                              })()}
                            </div>
                            <div className="text-gray-700">Asbestos:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = content.wallMaterials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) {
                                  return materials.includes('Asbestos') ? 'Yes' : 'No';
                                }
                                if (typeof materials === 'string') {
                                  return materials.split(',').some(m => m.trim().toLowerCase() === 'asbestos') ? 'Yes' : 'No';
                                }
                                return 'No';
                              })()}
                            </div>
                          </div>
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Wood:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = content.wallMaterials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) {
                                  return materials.includes('Wood') ? 'Yes' : 'No';
                                }
                                if (typeof materials === 'string') {
                                  return materials.split(',').some(m => m.trim().toLowerCase() === 'wood') ? 'Yes' : 'No';
                                }
                                return 'No';
                              })()}
                            </div>
                            <div className="text-gray-700">Other:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = content.wallMaterials;
                                const wallOptions = ['Brick', 'Cement', 'Corrugated Iron', 'Glass', 'Prefab', 'Asbestos', 'Wood'];
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) {
                                  return materials.filter(m => !wallOptions.includes(m)).length > 0 ? 'Yes' : 'No';
                                }
                                if (typeof materials === 'string') {
                                  const items = materials.split(',').map(m => m.trim());
                                  return items.some(m => !wallOptions.some(opt => opt.toLowerCase() === m.toLowerCase())) ? 'Yes' : 'No';
                                }
                                return 'No';
                              })()}
                            </div>
                          </div>
                        </div>
                        
                        {/* Roof Materials Subheading */}
                        <div className="space-y-2 mt-4">
                          <h4 className="font-semibold text-gray-700 text-sm border-b border-gray-200 pb-1">Roof Materials</h4>
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Concrete:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = content.roofMaterials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) {
                                  return materials.includes('Concrete') ? 'Yes' : 'No';
                                }
                                if (typeof materials === 'string') {
                                  return materials.split(',').some(m => m.trim().toLowerCase() === 'concrete') ? 'Yes' : 'No';
                                }
                                return 'No';
                              })()}
                            </div>
                            <div className="text-gray-700">Corrugated Iron:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = content.roofMaterials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) {
                                  return materials.includes('Corrugated Iron') ? 'Yes' : 'No';
                                }
                                if (typeof materials === 'string') {
                                  return materials.split(',').some(m => m.trim().toLowerCase() === 'corrugated iron') ? 'Yes' : 'No';
                                }
                                return 'No';
                              })()}
                            </div>
                          </div>
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Glass:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = content.roofMaterials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) {
                                  return materials.includes('Glass') ? 'Yes' : 'No';
                                }
                                if (typeof materials === 'string') {
                                  return materials.split(',').some(m => m.trim().toLowerCase() === 'glass') ? 'Yes' : 'No';
                                }
                                return 'No';
                              })()}
                            </div>
                            <div className="text-gray-700">Thatch:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = content.roofMaterials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) {
                                  return materials.includes('Thatch') ? 'Yes' : 'No';
                                }
                                if (typeof materials === 'string') {
                                  return materials.split(',').some(m => m.trim().toLowerCase() === 'thatch') ? 'Yes' : 'No';
                                }
                                return 'No';
                              })()}
                            </div>
                          </div>
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Asbestos:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = content.roofMaterials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) {
                                  return materials.includes('Asbestos') ? 'Yes' : 'No';
                                }
                                if (typeof materials === 'string') {
                                  return materials.split(',').some(m => m.trim().toLowerCase() === 'asbestos') ? 'Yes' : 'No';
                                }
                                return 'No';
                              })()}
                            </div>
                            <div className="text-gray-700">Wood:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = content.roofMaterials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) {
                                  return materials.includes('Wood') ? 'Yes' : 'No';
                                }
                                if (typeof materials === 'string') {
                                  return materials.split(',').some(m => m.trim().toLowerCase() === 'wood') ? 'Yes' : 'No';
                                }
                                return 'No';
                              })()}
                            </div>
                          </div>
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Tiles:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = content.roofMaterials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) {
                                  return materials.includes('Tiles') ? 'Yes' : 'No';
                                }
                                if (typeof materials === 'string') {
                                  return materials.split(',').some(m => m.trim().toLowerCase() === 'tiles') ? 'Yes' : 'No';
                                }
                                return 'No';
                              })()}
                            </div>
                            <div className="text-gray-700">Other:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = content.roofMaterials;
                                const roofOptions = ['Concrete', 'Corrugated Iron', 'Glass', 'Thatch', 'Asbestos', 'Wood', 'Tiles'];
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) {
                                  return materials.filter(m => !roofOptions.includes(m)).length > 0 ? 'Yes' : 'No';
                                }
                                if (typeof materials === 'string') {
                                  const items = materials.split(',').map(m => m.trim());
                                  return items.some(m => !roofOptions.some(opt => opt.toLowerCase() === m.toLowerCase())) ? 'Yes' : 'No';
                                }
                                return 'No';
                              })()}
                            </div>
                          </div>
                        </div>
                        
                        {/* Floor Materials Subheading */}
                        <div className="space-y-2 mt-4">
                          <h4 className="font-semibold text-gray-700 text-sm border-b border-gray-200 pb-1">Floor Materials</h4>
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Carpet:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = content.floorMaterials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) {
                                  return materials.includes('Carpet') ? 'Yes' : 'No';
                                }
                                if (typeof materials === 'string') {
                                  return materials.split(',').some(m => m.trim().toLowerCase() === 'carpet') ? 'Yes' : 'No';
                                }
                                return 'No';
                              })()}
                            </div>
                            <div className="text-gray-700">Sealed:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = content.floorMaterials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) {
                                  return materials.includes('Sealed') ? 'Yes' : 'No';
                                }
                                if (typeof materials === 'string') {
                                  return materials.split(',').some(m => m.trim().toLowerCase() === 'sealed') ? 'Yes' : 'No';
                                }
                                return 'No';
                              })()}
                            </div>
                          </div>
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Tiles:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = content.floorMaterials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) {
                                  return materials.includes('Tiles') ? 'Yes' : 'No';
                                }
                                if (typeof materials === 'string') {
                                  return materials.split(',').some(m => m.trim().toLowerCase() === 'tiles') ? 'Yes' : 'No';
                                }
                                return 'No';
                              })()}
                            </div>
                            <div className="text-gray-700">Wooden Floors:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = content.floorMaterials;
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) {
                                  return materials.includes('Wooden Floors') ? 'Yes' : 'No';
                                }
                                if (typeof materials === 'string') {
                                  return materials.split(',').some(m => m.trim().toLowerCase() === 'wooden floors') ? 'Yes' : 'No';
                                }
                                return 'No';
                              })()}
                            </div>
                          </div>
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Other:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const materials = content.floorMaterials;
                                const floorOptions = ['Carpet', 'Sealed', 'Tiles', 'Wooden Floors'];
                                if (!materials) return 'No';
                                if (Array.isArray(materials)) {
                                  return materials.filter(m => !floorOptions.includes(m)).length > 0 ? 'Yes' : 'No';
                                }
                                if (typeof materials === 'string') {
                                  const items = materials.split(',').map(m => m.trim());
                                  return items.some(m => !floorOptions.some(opt => opt.toLowerCase() === m.toLowerCase())) ? 'Yes' : 'No';
                                }
                                return 'No';
                              })()}
                            </div>
                          </div>
                        </div>

                        {/* Surrounding Areas Subheading */}
                        <div className="space-y-2 mt-4">
                          <h4 className="font-semibold text-gray-700 text-sm border-b border-gray-200 pb-1">Surrounding Areas</h4>
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Farm:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const areas = content.otherSurroundingArea;
                                if (!areas) return 'No';
                                if (Array.isArray(areas)) {
                                  return areas.includes('Farm') ? 'Yes' : 'No';
                                }
                                if (typeof areas === 'string') {
                                  return areas.split(',').some(a => a.trim().toLowerCase() === 'farm') ? 'Yes' : 'No';
                                }
                                return 'No';
                              })()}
                            </div>
                            <div className="text-gray-700">Plot:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const areas = content.otherSurroundingArea;
                                if (!areas) return 'No';
                                if (Array.isArray(areas)) {
                                  return areas.includes('Plot') ? 'Yes' : 'No';
                                }
                                if (typeof areas === 'string') {
                                  return areas.split(',').some(a => a.trim().toLowerCase() === 'plot') ? 'Yes' : 'No';
                                }
                                return 'No';
                              })()}
                            </div>
                          </div>
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Golf Estate:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const areas = content.otherSurroundingArea;
                                if (!areas) return 'No';
                                if (Array.isArray(areas)) {
                                  return areas.includes('Golf Estate') ? 'Yes' : 'No';
                                }
                                if (typeof areas === 'string') {
                                  return areas.split(',').some(a => a.trim().toLowerCase() === 'golf estate') ? 'Yes' : 'No';
                                }
                                return 'No';
                              })()}
                            </div>
                            <div className="text-gray-700">Country Estate / Residential Area:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const areas = content.otherSurroundingArea;
                                if (!areas) return 'No';
                                if (Array.isArray(areas)) {
                                  return areas.includes('Country Estate') || areas.includes('Residential Area') ? 'Yes' : 'No';
                                }
                                if (typeof areas === 'string') {
                                  const areaList = areas.split(',').map(a => a.trim().toLowerCase());
                                  return areaList.some(a => a === 'country estate' || a === 'residential area') ? 'Yes' : 'No';
                                }
                                return 'No';
                              })()}
                            </div>
                          </div>
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Security Complex:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const areas = content.otherSurroundingArea;
                                if (!areas) return 'No';
                                if (Array.isArray(areas)) {
                                  return areas.includes('Security Complex') ? 'Yes' : 'No';
                                }
                                if (typeof areas === 'string') {
                                  return areas.split(',').some(a => a.trim().toLowerCase() === 'security complex') ? 'Yes' : 'No';
                                }
                                return 'No';
                              })()}
                            </div>
                            <div className="text-gray-700">Complex With No Access Control:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const areas = content.otherSurroundingArea;
                                if (!areas) return 'No';
                                if (Array.isArray(areas)) {
                                  return areas.includes('Complex With No Access Control') ? 'Yes' : 'No';
                                }
                                if (typeof areas === 'string') {
                                  return areas.split(',').some(a => a.trim().toLowerCase() === 'complex with no access control') ? 'Yes' : 'No';
                                }
                                return 'No';
                              })()}
                            </div>
                          </div>
                          <div className="grid grid-cols-4 gap-2 text-sm pl-4">
                            <div className="text-gray-700">Boomed Off Suburb:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const areas = content.otherSurroundingArea;
                                if (!areas) return 'No';
                                if (Array.isArray(areas)) {
                                  return areas.includes('Boomed Off Suburb') ? 'Yes' : 'No';
                                }
                                if (typeof areas === 'string') {
                                  return areas.split(',').some(a => a.trim().toLowerCase() === 'boomed off suburb') ? 'Yes' : 'No';
                                }
                                return 'No';
                              })()}
                            </div>
                            <div className="text-gray-700">Suburb:</div>
                            <div className="text-gray-800">
                              {(() => {
                                const areas = content.otherSurroundingArea;
                                if (!areas) return 'No';
                                if (Array.isArray(areas)) {
                                  return areas.includes('Suburb') ? 'Yes' : 'No';
                                }
                                if (typeof areas === 'string') {
                                  return areas.split(',').some(a => a.trim().toLowerCase() === 'suburb') ? 'Yes' : 'No';
                                }
                                return 'No';
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Financial Details - NEW SECTION */}
                      <div className="space-y-3">
                        <h3 className="font-bold text-gray-800 text-base mb-3">Financial Details</h3>
                        
                        <div className="grid grid-cols-4 gap-2 text-sm">
                          <div className="text-gray-700">Premium:</div>
                          <div className="text-gray-800">
                            {content.premium ? `R ${content.premium.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                          </div>
                          <div className="text-gray-700">Excess:</div>
                          <div className="text-gray-800">
                            {content.excess ? `R ${content.excess.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                          </div>
                        </div>
                        
                        
                      </div>
                    </div>
                  </div>
                  
                ))
              )}

              {/* ===== HOUSEHOLD CONTENTS QUESTIONS - ONLY SHOW IF PRODUCT EXISTS ===== */}
              {!loadingHouseholdContents && householdContents.length > 0 && (
                <>
                  {loadingHouseholdContentQuestions ? (
                    <div className="mt-6 py-4 text-gray-500 italic">
                      Loading household contents questions...
                    </div>
                  ) : householdContentQuestions.length > 0 ? (
                    <div className="mt-6 space-y-4 pt-4 border-t border-gray-300">
                      <h3 className="font-bold text-gray-800 text-base mb-3">Household Contents Questions</h3>
                      
                      {householdContentQuestions.map((question, index) => (
                        <div key={question.id || index} className="flex items-start">
                          <span className="text-sm text-gray-800">
                            {index + 1}. {question.question}
                            {question.is_important && (
                              <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">Important</span>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </>
              )}
            </div>

            {/* Vehicles Section */}
            <div className="mb-12">
              <h2 className="font-bold text-lg mb-4 border-b border-black pb-1">VEHICLES</h2>
              
              {loadingVehicles ? (
                <div className="py-4 text-gray-500 italic">
                  Loading vehicles...
                </div>
              ) : vehicles.length === 0 ? (
                <div className="py-8 text-center text-gray-500 italic">
                  No vehicles recorded.
                </div>
              ) : (
                vehicles.map((vehicle, index) => (
                  <div key={vehicle.id || index} className="mb-8 last:mb-0 space-y-4">
                    {index > 0 && <hr className="my-6 border-gray-300" />}
                    
                    <div className="space-y-6">

                      {/* Address Details */}
                      <div className="space-y-3 text-sm">
                        <h3 className="font-bold text-gray-800 text-base mb-3">Address Details</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Address:</div>
                          <div className="text-gray-800">
                            {vehicle.address_line1} {vehicle.address_line2 ? `, ${vehicle.address_line2}` : ''}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">City/Town:</div>
                          <div className="text-gray-800">{vehicle.city_town || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Suburb:</div>
                          <div className="text-gray-800">{vehicle.suburb || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Province:</div>
                          <div className="text-gray-800">{vehicle.region || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Postal Code:</div>
                          <div className="text-gray-800">{vehicle.postal_code || '—'}</div>
                        </div>
                      </div>
                      
                      {/* Vehicle Information */}
                      <div className="space-y-3 text-sm">
                        <h3 className="font-bold text-gray-800 text-base mb-3">Vehicle Information</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Vehicle Type:</div>
                          <div className="text-gray-800">{vehicle.vehicle_type || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Registration Number:</div>
                          <div className="text-gray-800">{vehicle.registration_number || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Make:</div>
                          <div className="text-gray-800">{vehicle.make || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Model:</div>
                          <div className="text-gray-800">{vehicle.model || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Year:</div>
                          <div className="text-gray-800">{vehicle.year || '—'}</div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Variant:</div>
                          <div className="text-gray-800">{vehicle.variant || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Color:</div>
                          <div className="text-gray-800">{vehicle.color || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Vehicle Usage:</div>
                          <div className="text-gray-800">{vehicle.usage || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">VIN Number:</div>
                          <div className="text-gray-800">{vehicle.vin_number || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Engine Number:</div>
                          <div className="text-gray-800">{vehicle.engine_number || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">MM Code:</div>
                          <div className="text-gray-800">{vehicle.mmcode || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Cover Type:</div>
                          <div className="text-gray-800">{getCoverTypeDisplay(vehicle.cover_type)}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Insured Value:</div>
                          <div className="text-gray-800">R {vehicle.insured_value || '0.00'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">No Claim Bonus:</div>
                          <div className="text-gray-800">{getNoClaimBonusDisplay(vehicle.no_claim_bonus)}</div>
                        </div>
                      </div>

                      {/* Cover & Features */}
                      <div className="space-y-3 text-sm">
                        <h3 className="font-bold text-gray-800 text-base mb-3">Cover</h3>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Use Retail Value:</div>
                          <div className="text-gray-800">{vehicle.use_retail_value ? 'Yes' : 'No'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Excess Buy Back:</div>
                          <div className="text-gray-800">{vehicle.excess_buy_back ? 'Yes' : 'No'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Credit Shortfall:</div>
                          <div className="text-gray-800">{vehicle.credit_shortfall ? 'Yes' : 'No'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Car Hire:</div>
                          <div className="text-gray-800">{vehicle.car_hire ? 'Yes' : 'No'}</div>
                        </div>
                      </div>

                      {/* Parking Locations */}
                      <div className="space-y-3 text-sm">
                        <h3 className="font-bold text-gray-800 text-base mb-3">Parking Locations</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Day Parking Location:</div>
                          <div className="text-gray-800">{getParkingLocationDisplay(vehicle.day_parking_location)}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Night Parking Location:</div>
                          <div className="text-gray-800">{getParkingLocationDisplay(vehicle.night_parking_location)}</div>
                        </div>
                      </div>

                      {/* Driver Information */}
                      <div className="space-y-3 text-sm">
                        <h3 className="font-bold text-gray-800 text-base mb-3">Driver Information</h3>
                        
                                                {/* Registered Owner */}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Registered Owner:</div>
                          <div className="text-gray-800">
                            {getPersonName(vehicle.registered_owner, client, personsOfInterest || [])}
                            {getDriverLicenseDisplay(vehicle.registered_owner, personsOfInterest || [], client)}
                          </div>
                        </div>
                        
                        {/* Regular Driver */}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Regular Driver:</div>
                          <div className="text-gray-800">
                            {getPersonName(vehicle.regular_driver, client, personsOfInterest || [])}
                            {getDriverLicenseDisplay(vehicle.regular_driver, personsOfInterest || [], client)}
                          </div>
                        </div>
                        
                        {/* Other Drivers */}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Other Drivers:</div>
                          <div className="text-gray-800">
                            {(() => {
                              const drivers = vehicle.other_drivers;
                              if (!drivers) return 'None';
                              
                              const driverList = Array.isArray(drivers) ? drivers : [drivers];
                              
                              const names = driverList
                                .map((d: any) => {
                                  const name = getPersonName(d, client, personsOfInterest || []);
                                  // Check if this driver is the client
                                  const isClient = String(d).includes('client-') || String(d) === String(client?.id);
                                  const licenseInfo = getDriverLicenseDisplay(d, personsOfInterest || [], isClient ? client : undefined);
                                  return `${name}${licenseInfo}`;
                                })
                                .filter((name: string) => name && name !== '—');
                              
                              return names.length > 0 ? names.join(', ') : 'None';
                            })()}
                          </div>
                        </div>
                        
                        {/* Safety Features */}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Safety Features:</div>
                          <div className="text-gray-800">
                            {getSafetyFeaturesDisplay(vehicle.safety_features)}
                          </div>
                        </div>
                      </div>

                      

                      {/* Financial Details - NEW SECTION after Address Details */}
                      <div className="space-y-3">
                        <h3 className="font-bold text-gray-800 text-base mb-3">Financial Details</h3>
                        
                        <div className="grid grid-cols-4 gap-2 text-sm">
                          <div className="text-gray-700">Premium:</div>
                          <div className="text-gray-800">
                            {vehicle.premium ? `R ${parseFloat(vehicle.premium).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                          </div>
                          <div className="text-gray-700">Excess:</div>
                          <div className="text-gray-800">
                            {vehicle.excess ? `R ${parseFloat(vehicle.excess).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                          </div>
                        </div>
                        
                      </div>                                      

                      {/* Overnight Parking */}
                      {vehicle.overnight_parking_elsewhere && (
                        <div className="space-y-3 text-sm">
                          <h3 className="font-medium text-gray-700 mb-2">Alternative Overnight Parking Address</h3>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="font-medium text-gray-700">Address:</div>
                            <div className="text-gray-800">
                              {vehicle.address_line1 || '—'}
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="font-medium text-gray-700">City/Town:</div>
                            <div className="text-gray-800">{vehicle.city_town || '—'}</div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="font-medium text-gray-700">Suburb:</div>
                            <div className="text-gray-800">{vehicle.suburb || '—'}</div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="font-medium text-gray-700">Province:</div>
                            <div className="text-gray-800">{vehicle.region || '—'}</div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="font-medium text-gray-700">Postal Code:</div>
                            <div className="text-gray-800">{vehicle.postal_code || '—'}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}

              {/* ===== VEHICLE QUESTIONS - ONLY SHOW IF PRODUCT EXISTS ===== */}
              {!loadingVehicles && vehicles.length > 0 && (
                <>
                  {loadingVehicleQuestions ? (
                    <div className="mt-6 py-4 text-gray-500 italic">
                      Loading vehicle questions...
                    </div>
                  ) : vehicleQuestions.length > 0 ? (
                    <div className="mt-6 space-y-4 pt-4 border-t border-gray-300">
                      <h3 className="font-medium text-gray-700 mb-3">Vehicle Questions</h3>
                      
                      {vehicleQuestions.map((question, index) => (
                        <div key={question.id || index} className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-800">
                            {index + 1}. {question.question}
                            {question.is_important && (
                              <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">Important</span>
                            )}
                          </p>
                          
                          <div className="flex space-x-6">
                            <label className="flex items-center space-x-2">
                              <input
                                type="radio"
                                name={`vehicle-question-${question.id}`}
                                value="yes"
                                checked={vehicleQuestionResponses[question.id] === true}
                                onChange={() => handleVehicleQuestionResponse(question.id, true)}
                                className="h-4 w-4 text-black focus:ring-black border-gray-300"
                              />
                              <span className="text-sm text-gray-700">Yes</span>
                            </label>
                            <label className="flex items-center space-x-2">
                              <input
                                type="radio"
                                name={`vehicle-question-${question.id}`}
                                value="no"
                                checked={vehicleQuestionResponses[question.id] === false}
                                onChange={() => handleVehicleQuestionResponse(question.id, false)}
                                className="h-4 w-4 text-black focus:ring-black border-gray-300"
                              />
                              <span className="text-sm text-gray-700">No</span>
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </>
              )}
            </div>

            {/* Trailer/Caravan Section */}
            <div className="mb-12">
              <h2 className="font-bold text-lg mb-4 border-b border-black pb-1">TRAILER/CARAVAN</h2>
              
              {loadingTrailersCaravans ? (
                <div className="py-4 text-gray-500 italic">
                  Loading trailers/caravans...
                </div>
              ) : trailersCaravans.length === 0 ? (
                <div className="py-8 text-center text-gray-500 italic">
                  No trailers/caravans recorded.
                </div>
              ) : (
                trailersCaravans.map((trailer, index) => (
                  <div key={trailer.id || index} className="mb-8 last:mb-0">
                    {index > 0 && <hr className="my-6 border-gray-300" />}
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {/* Vehicle Type */}
                      <div className="font-medium text-gray-700">Vehicle Type:</div>
                      <div className="text-gray-800">{trailer.vehicle_type || '—'}</div>
                      
                      {/* Registration Number */}
                      <div className="font-medium text-gray-700">Registration Number:</div>
                      <div className="text-gray-800">{trailer.registration_number || '—'}</div>
                      
                      {/* Make */}
                      <div className="font-medium text-gray-700">Make:</div>
                      <div className="text-gray-800">{trailer.make || '—'}</div>
                      
                      {/* Model */}
                      <div className="font-medium text-gray-700">Model:</div>
                      <div className="text-gray-800">{trailer.model || '—'}</div>
                      
                      {/* Year */}
                      <div className="font-medium text-gray-700">Year:</div>
                      <div className="text-gray-800">{trailer.year || '—'}</div>
                      
                      {/* Cover Type */}
                      <div className="font-medium text-gray-700">Cover Type:</div>
                      <div className="text-gray-800">{trailer.cover_type || '—'}</div>
                      
                      {/* Insured Value */}
                      <div className="font-medium text-gray-700">Insured Value:</div>
                      <div className="text-gray-800">R {trailer.insured_value?.toLocaleString() || '0.00'}</div>
                      
                      {/* Use Retail Value */}
                      <div className="font-medium text-gray-700">Use Retail Value:</div>
                      <div className="text-gray-800">{trailer.use_retail_value ? 'Yes' : 'No'}</div>
                      
                      {/* Premium */}
                      <div className="font-medium text-gray-700">Premium:</div>
                      <div className="text-gray-800">R {trailer.premium?.toLocaleString() || '0.00'}</div>
                      
                      {/* Excess */}
                      <div className="font-medium text-gray-700">Excess:</div>
                      <div className="text-gray-800">R {trailer.excess?.toLocaleString() || '0.00'}</div>
                      
                      {/* No Claims Bonus */}
                      <div className="font-medium text-gray-700">No Claims Bonus:</div>
                      <div className="text-gray-800">{trailer.no_claims_bonus || '—'}</div>
                    </div>
                  </div>
                ))
              )}

              {/* ===== TRAILER/CARAVAN QUESTIONS - ONLY SHOW IF PRODUCT EXISTS ===== */}
              {!loadingTrailersCaravans && trailersCaravans.length > 0 && (
                <>
                  {loadingTrailerCaravanQuestions ? (
                    <div className="mt-6 py-4 text-gray-500 italic">
                      Loading trailer/caravan questions...
                    </div>
                  ) : trailerCaravanQuestions.length > 0 ? (
                    <div className="mt-6 space-y-4 pt-4 border-t border-gray-300">
                      <h3 className="font-medium text-gray-700 mb-3">Trailer/Caravan Questions</h3>
                      
                      {trailerCaravanQuestions.map((question, index) => (
                        <div key={question.id || index} className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-800">
                            {index + 1}. {question.question}
                            {question.is_important && (
                              <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">Important</span>
                            )}
                          </p>
                          
                          <div className="flex space-x-6">
                            <label className="flex items-center space-x-2">
                              <input
                                type="radio"
                                name={`trailer-question-${question.id}`}
                                value="yes"
                                checked={trailerCaravanQuestionResponses[question.id] === true}
                                onChange={() => handleTrailerCaravanQuestionResponse(question.id, true)}
                                className="h-4 w-4 text-black focus:ring-black border-gray-300"
                              />
                              <span className="text-sm text-gray-700">Yes</span>
                            </label>
                            <label className="flex items-center space-x-2">
                              <input
                                type="radio"
                                name={`trailer-question-${question.id}`}
                                value="no"
                                checked={trailerCaravanQuestionResponses[question.id] === false}
                                onChange={() => handleTrailerCaravanQuestionResponse(question.id, false)}
                                className="h-4 w-4 text-black focus:ring-black border-gray-300"
                              />
                              <span className="text-sm text-gray-700">No</span>
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </>
              )}
            </div>

            {/* Watercraft Section */}
            <div className="mb-12">
              <h2 className="font-bold text-lg mb-4 border-b border-black pb-1">WATERCRAFT</h2>
              
              {loadingWatercraft ? (
                <div className="py-4 text-gray-500 italic">
                  Loading watercraft...
                </div>
              ) : watercraft.length === 0 ? (
                <div className="py-8 text-center text-gray-500 italic">
                  No watercraft recorded.
                </div>
              ) : (
                watercraft.map((craft, index) => (
                  <div key={craft.id || index} className="mb-8 last:mb-0 space-y-4">
                    {index > 0 && <hr className="my-6 border-gray-300" />}
                    
                    <div className="space-y-6">
                      {/* Basic Information */}
                      <div className="space-y-3 text-sm">
                        <h3 className="font-medium text-gray-700 mb-2">Basic Information</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Year:</div>
                          <div className="text-gray-800">{craft.year || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Type of Craft:</div>
                          <div className="text-gray-800">{craft.craft_type || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Make of Hull:</div>
                          <div className="text-gray-800">{craft.hull_make || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Length of Hull (meters):</div>
                          <div className="text-gray-800">{craft.hull_length || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Year Model:</div>
                          <div className="text-gray-800">{craft.year_model || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Body Paint:</div>
                          <div className="text-gray-800">{craft.body_paint || '—'}</div>
                        </div>
                      </div>
                      
                      {/* Motor Information */}
                      <div className="space-y-3 text-sm">
                        <h3 className="font-medium text-gray-700 mb-2">Motor Information</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Make & Model of Outboard Motor:</div>
                          <div className="text-gray-800">{craft.outboard_motor_make || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Outboard/Engine Number:</div>
                          <div className="text-gray-800">{craft.outboard_engine_number || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Horse Power:</div>
                          <div className="text-gray-800">{craft.horse_power || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Max Speed (km/h):</div>
                          <div className="text-gray-800">{craft.max_speed || '—'}</div>
                        </div>
                      </div>
                      
                      {/* Vessel Information */}
                      <div className="space-y-3 text-sm">
                        <h3 className="font-medium text-gray-700 mb-2">Vessel Information</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Name of Vessel:</div>
                          <div className="text-gray-800">{craft.vessel_name || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Where will vessel be kept:</div>
                          <div className="text-gray-800">{craft.vessel_storage || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Where will vessel be used:</div>
                          <div className="text-gray-800">{craft.vessel_usage || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Built by amateur:</div>
                          <div className="text-gray-800">{craft.amateur_built ? 'Yes' : 'No'}</div>
                        </div>
                                                <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Registered Owner:</div>
                          <div className="text-gray-800">
                            {craft.registered_owner 
                              ? (typeof craft.registered_owner === 'object' 
                                  ? (craft.registered_owner as any).name || (craft.registered_owner as any).full_name
                                  : String(craft.registered_owner)) 
                              : '—'}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Finance House:</div>
                          <div className="text-gray-800">{craft.finance_house || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Finance Branch:</div>
                          <div className="text-gray-800">{craft.finance_branch || '—'}</div>
                        </div>
                      </div>
                      
                      {/* Insurance Values */}
                      <div className="space-y-3 text-sm">
                        <h3 className="font-medium text-gray-700 mb-2">Insurance Values</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Value of Hull and Fixed Equipment:</div>
                          <div className="text-gray-800">R {craft.hull_value?.toLocaleString() || '0.00'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Value of Outboard Motors:</div>
                          <div className="text-gray-800">R {craft.outboard_value?.toLocaleString() || '0.00'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Value of Sails, Masts and Rigging:</div>
                          <div className="text-gray-800">R {craft.sails_value?.toLocaleString() || '0.00'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Total Sum Insured:</div>
                          <div className="text-gray-800 font-medium">R {craft.total_sum_insured?.toLocaleString() || '0.00'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Total Monthly Premium:</div>
                          <div className="text-gray-800">R {craft.total_monthly_premium?.toLocaleString() || '0.00'}</div>
                        </div>
                      </div>
                      
                      {/* Trailer Information */}
                      <div className="space-y-3 text-sm">
                        <h3 className="font-medium text-gray-700 mb-2">Trailer Information</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Trailer Make:</div>
                          <div className="text-gray-800">{craft.trailer_make || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Trailer Model:</div>
                          <div className="text-gray-800">{craft.trailer_model || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Trailer Registration Number:</div>
                          <div className="text-gray-800">{craft.trailer_registration || '—'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Trailer Value:</div>
                          <div className="text-gray-800">R {craft.trailer_value?.toLocaleString() || '0.00'}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="font-medium text-gray-700">Trailer Premium:</div>
                          <div className="text-gray-800">R {craft.trailer_premium?.toLocaleString() || '0.00'}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}

              {/* ===== WATERCRAFT QUESTIONS - ONLY SHOW IF PRODUCT EXISTS ===== */}
              {!loadingWatercraft && watercraft.length > 0 && (
                <>
                  {loadingWatercraftQuestions ? (
                    <div className="mt-6 py-4 text-gray-500 italic">
                      Loading watercraft questions...
                    </div>
                  ) : watercraftQuestions.length > 0 ? (
                    <div className="mt-6 space-y-4 pt-4 border-t border-gray-300">
                      <h3 className="font-medium text-gray-700 mb-3">Watercraft Questions</h3>
                      
                      {watercraftQuestions.map((question, index) => (
                        <div key={question.id || index} className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-800">
                            {index + 1}. {question.question}
                            {question.is_important && (
                              <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">Important</span>
                            )}
                          </p>
                          
                          <div className="flex space-x-6">
                            <label className="flex items-center space-x-2">
                              <input
                                type="radio"
                                name={`watercraft-question-${question.id}`}
                                value="yes"
                                checked={watercraftQuestionResponses[question.id] === true}
                                onChange={() => handleWatercraftQuestionResponse(question.id, true)}
                                className="h-4 w-4 text-black focus:ring-black border-gray-300"
                              />
                              <span className="text-sm text-gray-700">Yes</span>
                            </label>
                            <label className="flex items-center space-x-2">
                              <input
                                type="radio"
                                name={`watercraft-question-${question.id}`}
                                value="no"
                                checked={watercraftQuestionResponses[question.id] === false}
                                onChange={() => handleWatercraftQuestionResponse(question.id, false)}
                                className="h-4 w-4 text-black focus:ring-black border-gray-300"
                              />
                              <span className="text-sm text-gray-700">No</span>
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </>
              )}
            </div>
            
            {/* All Risk Section */}
            <div className="mb-12">
              <h2 className="font-bold text-lg mb-4 border-b border-black pb-1">ALL RISK</h2>
            
              {loadingAllRiskItems ? (
                <div className="py-4 text-gray-500 italic">
                  Loading all risk items...
                </div>
              ) : allRiskItems.length === 0 ? (
                <div className="py-8 text-center text-gray-500 italic">
                  No all risk items recorded.
                </div>
              ) : (
                <div className="space-y-6">
                  {allRiskItems.map((item, index) => (
                    <div key={item.id || index} className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
                      {/* Item Type */}
                      <div className="flex justify-between items-center mb-3">
                        <div className="text-sm font-medium text-gray-700">Item Type</div>
                        <div className="font-semibold text-gray-800">{item.item_type || '—'}</div>
                      </div>

                      {/* Item Name */}
                      <div className="flex justify-between items-center mb-3">
                        <div className="text-sm font-medium text-gray-700">Item Name</div>
                        <div className="font-bold text-gray-800">{item.item_name || '—'}</div>
                      </div>

                      {/* Serial Number */}
                      <div className="flex justify-between items-center mb-3">
                        <div className="text-sm font-medium text-gray-700">Serial Number</div>
                        <div className="text-gray-800 font-mono text-sm">{item.serial_number || '—'}</div>
                      </div>

                      {/* Cover Type */}
                      <div className="flex justify-between items-center mb-4 pb-2 border-b border-gray-200">
                        <div className="text-sm font-medium text-gray-700">Cover Type</div>
                        <div className="text-gray-800">{item.cover_type || '—'}</div>
                      </div>

                      {/* Description */}
                      <div className="flex justify-between items-center mb-3">
                        <div className="text-sm font-medium text-gray-700">Description</div>
                        <div className="text-gray-800 text-right max-w-[60%]">{item.description || '—'}</div>
                      </div>

                      {/* Insured Value */}
                      <div className="flex justify-between items-center mb-3">
                        <div className="text-sm font-medium text-gray-700">Insured Value</div>
                        <div className="font-bold text-green-700">
                          R {item.insured_value?.toLocaleString() || '0.00'}
                        </div>
                      </div>

                      {/* No Claims Bonus */}
                      <div className="flex justify-between items-center mb-4 pb-2 border-b border-gray-200">
                        <div className="text-sm font-medium text-gray-700">No Claims Bonus</div>
                        <div className="text-gray-800">{item.no_claims_bonus || '—'}</div>
                      </div>

                      {/* Premium & Excess - stacked vertically */}
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between items-center">
                          <div className="font-medium text-gray-700">Premium:</div>
                          <div className="font-bold text-green-700">R {item.premium?.toLocaleString() || '0.00'}</div>
                        </div>
                        <div className="flex justify-between items-center">
                          <div className="font-medium text-gray-700">Excess:</div>
                          <div className="font-medium">R {item.excess?.toLocaleString() || '0.00'}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ===== ALL RISK QUESTIONS - ONLY SHOW IF PRODUCT EXISTS ===== */}
              {!loadingAllRiskItems && allRiskItems.length > 0 && (
                <>
                  {loadingAllRiskQuestions ? (
                    <div className="mt-6 py-4 text-gray-500 italic">
                      Loading all risk questions...
                    </div>
                  ) : allRiskQuestions.length > 0 ? (
                    <div className="mt-8 space-y-4 pt-4 border-t border-gray-300">
                      <h3 className="font-bold text-gray-800 text-base mb-3">All Risk Questions</h3>
                      
                      {allRiskQuestions.map((question, index) => (
                        <div key={question.id || index} className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-800">
                            {index + 1}. {question.question}
                            {question.is_important && (
                              <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">Important</span>
                            )}
                          </p>
                          
                          <div className="flex space-x-6">
                            <label className="flex items-center space-x-2">
                              <input
                                type="radio"
                                name={`allrisk-question-${question.id}`}
                                value="yes"
                                checked={allRiskQuestionResponses[question.id] === true}
                                onChange={() => handleAllRiskQuestionResponse(question.id, true)}
                                className="h-4 w-4 text-black focus:ring-black border-gray-300"
                              />
                              <span className="text-sm text-gray-700">Yes</span>
                            </label>
                            <label className="flex items-center space-x-2">
                              <input
                                type="radio"
                                name={`allrisk-question-${question.id}`}
                                value="no"
                                checked={allRiskQuestionResponses[question.id] === false}
                                onChange={() => handleAllRiskQuestionResponse(question.id, false)}
                                className="h-4 w-4 text-black focus:ring-black border-gray-300"
                              />
                              <span className="text-sm text-gray-700">No</span>
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </>
              )}
            </div>

            {/* Value Added Products Section */}
<div className="mb-12">
  <h2 className="font-bold text-lg mb-4 border-b border-black pb-1">VALUE ADDED PRODUCTS</h2>
 
  {loadingValueAddedProducts ? (
    <div className="py-4 text-gray-500 italic">
      Loading value added products...
    </div>
  ) : valueAddedProducts.length === 0 ? (
    <div className="py-8 text-center text-gray-500 italic">
      No value added products recorded.
    </div>
  ) : (
    <div className="space-y-6">
      {valueAddedProducts.map((product, index) => (
        <div key={product.id || index} className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
          {/* Product Type */}
          <div className="flex justify-between items-center mb-3">
            <div className="text-sm font-medium text-gray-700">Product Type</div>
            <div className="font-semibold text-gray-800">{product.product_type || '—'}</div>
          </div>

          {/* Description */}
          <div className="flex justify-between items-center mb-3">
            <div className="text-sm font-medium text-gray-700">Description</div>
            <div className="text-gray-800 text-right max-w-[60%]">{product.description || '—'}</div>
          </div>

          {/* Value (Insured Value) */}
          <div className="flex justify-between items-center mb-4 pb-2 border-b border-gray-200">
            <div className="text-sm font-medium text-gray-700">Value</div>
            <div className="font-bold text-green-700">
              R {product.value?.toLocaleString() || '0.00'}
            </div>
          </div>

          {/* Premium */}
          <div className="flex justify-between items-center">
            <div className="text-sm font-medium text-gray-700">Premium:</div>
            <div className="font-bold text-green-700">R {product.premium?.toLocaleString() || '0.00'}</div>
          </div>
        </div>
      ))}
    </div>
  )}

  {/* ===== VALUE ADDED QUESTIONS - ONLY SHOW IF PRODUCT EXISTS ===== */}
  {!loadingValueAddedProducts && valueAddedProducts.length > 0 && (
    <>
      {loadingValueAddedQuestions ? (
        <div className="mt-6 py-4 text-gray-500 italic">
          Loading value added questions...
        </div>
      ) : valueAddedQuestions.length > 0 ? (
        <div className="mt-8 space-y-4 pt-4 border-t border-gray-300">
          <h3 className="font-bold text-gray-800 text-base mb-3">Value Added Questions</h3>
          
          {valueAddedQuestions.map((question, index) => (
            <div key={question.id || index} className="flex items-center justify-between">
              <p className="text-sm font-medium text-gray-800">
                {index + 1}. {question.question}
                {question.is_important && (
                  <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">Important</span>
                )}
              </p>
              
              <div className="flex space-x-6">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name={`valueadded-question-${question.id}`}
                    value="yes"
                    checked={valueAddedQuestionResponses[question.id] === true}
                    onChange={() => handleValueAddedQuestionResponse(question.id, true)}
                    className="h-4 w-4 text-black focus:ring-black border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Yes</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name={`valueadded-question-${question.id}`}
                    value="no"
                    checked={valueAddedQuestionResponses[question.id] === false}
                    onChange={() => handleValueAddedQuestionResponse(question.id, false)}
                    className="h-4 w-4 text-black focus:ring-black border-gray-300"
                  />
                  <span className="text-sm text-gray-700">No</span>
                </label>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </>
  )}
</div>

            {/* Declaration Questions */}
            <div className="mb-12">
              <h2 className="font-bold text-lg mb-4 border-b border-black pb-1">DECLARATION QUESTIONS</h2>
              
              {loadingDeclarationQuestions ? (
                <div className="py-4 text-gray-500 italic">
                  Loading declaration questions...
                </div>
              ) : declarationQuestions.length === 0 ? (
                <div className="py-8 text-center text-gray-500 italic">
                  No declaration questions configured for this quote.
                </div>
              ) : (
                <div className="space-y-4">
                  {declarationQuestions.map((question, index) => (
                    <div key={question.id || index} className="text-sm flex items-start">
                      <span className="mr-2 font-medium mt-0.5">{index + 1}.</span>
                      <p className="text-gray-800 leading-relaxed">
                        {question.question}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* CONSENT CLAUSE - Matching PDF exactly */}
            <div className="mb-12">
              <h2 className="font-bold text-lg mb-4 border-b border-black pb-1">CONSENT CLAUSE</h2>
             
              {loadingConsentClausQuestions ? (
                <div className="py-4 text-gray-500 italic">
                  Loading consent clause...
                </div>
              ) : consentClausQuestions.length === 0 ? (
                <div className="py-8 text-center text-gray-500 italic">
                  No consent clause questions configured for this quote.
                </div>
              ) : (
                <div className="space-y-6">
                  {consentClausQuestions.map((question, index) => {
                    const currentResponse = declarationResponses[question.id] ?? false;
                    
                    return (
                      <div key={question.id || index} className="flex items-start gap-4 text-sm">
                        <span className="font-medium mt-1 min-w-[24px]">{index + 1}.</span>
                        <div className="flex-1">
                          <p className="text-gray-800 leading-relaxed mb-3">
                            {question.question}
                          </p>
                          
                          <div className="flex gap-8">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name={`consent-${question.id}`}
                                checked={currentResponse === true}
                                onChange={() => handleDeclarationResponse(question.id, true)}
                                className="h-4 w-4 text-black focus:ring-black border-gray-300"
                              />
                              <span className="text-gray-700">Yes</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name={`consent-${question.id}`}
                                checked={currentResponse === false}
                                onChange={() => handleDeclarationResponse(question.id, false)}
                                className="h-4 w-4 text-black focus:ring-black border-gray-300"
                              />
                              <span className="text-gray-700">No</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Comments Section - Static for form display */}
            <div className="mb-12">
              <h2 className="font-bold text-lg mb-6 border-b border-black pb-2">COMMENTS</h2>
              
              <div className="space-y-6">
                {/* Comment Text Area */}
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-2">Comment</div>
                  <textarea
                    value={commentForm.comment}
                    onChange={handleCommentFormChange('comment')}
                    className="w-full h-40 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-black resize-none"
                    placeholder="Enter any additional comments or notes here..."
                    rows={6}
                  />
                </div>
                
                {/* Commenter Details - In a grid layout similar to other sections */}
                <div className="grid grid-cols-2 gap-x-16 gap-y-4">
                  {/* Name */}
                  <div className="font-medium text-gray-700">Name</div>
                  <div>
                    <input
                      type="text"
                      value={commentForm.comment_by_name}
                      onChange={handleCommentFormChange('comment_by_name')}
                      className="w-full border-b border-black pb-1 focus:outline-none font-medium"
                      placeholder="Enter full name"
                    />
                  </div>
                  
                  {/* ID Number */}
                  <div className="font-medium text-gray-700">ID No</div>
                  <div>
                    <input
                      type="text"
                      value={commentForm.comment_by_id_no}
                      onChange={handleCommentFormChange('comment_by_id_no')}
                      className="w-full border-b border-black pb-1 focus:outline-none font-medium"
                      placeholder="Enter ID number"
                    />
                  </div>
                  
                  {/* Signed At */}
                  <div className="font-medium text-gray-700">Signed at</div>
                  <div>
                    <input
                      type="text"
                      value={commentForm.signed_at}
                      onChange={handleCommentFormChange('signed_at')}
                      className="w-full border-b border-black pb-1 focus:outline-none font-medium"
                      placeholder="Location where signed"
                    />
                  </div>
                  
                  {/* Date field*/}
                  <div className="font-medium text-gray-700">Date</div>
                    <div>
                      <StyledDatePicker
                        value={commentForm.comment_date}
                        onChange={(date) => {
                          setCommentForm(prev => ({
                            ...prev,
                            comment_date: date
                          }));
                        }}
                        className="w-full border-b border-black pb-1 focus:outline-none font-medium"
                      />
                    </div>
                </div>
                
                {/* Signature Line */}
                <div className="mt-8">
                  <div className="text-sm font-medium text-gray-700 mb-2">Signature</div>
                  <div className="border-b border-black h-12"></div>
                  <div className="text-xs text-gray-600 mt-1">Sign above line</div>
                </div>
                
                {/* Optional: Witness signature if needed */}
                <div className="mt-8 border-t border-gray-300 pt-6">
                  <h3 className="font-medium text-gray-700 mb-4">Witness (If Applicable)</h3>
                  <div className="grid grid-cols-2 gap-x-16 gap-y-4">
                    <div className="font-medium text-gray-700">Witness Name</div>
                    <div className="border-b border-black h-8"></div>
                    
                    <div className="font-medium text-gray-700">Witness ID No</div>
                    <div className="border-b border-black h-8"></div>
                    
                    <div className="font-medium text-gray-700">Witness Signature</div>
                    <div className="border-b border-black h-12"></div>
                    
                    <div className="font-medium text-gray-700">Date</div>
                    <div className="border-b border-black h-8"></div>
                  </div>
                </div>
              </div>
            </div>

                        {/* BROKER CONSENT SECTION - Matching PDF */}
            <div className="mb-12">
              <h2 className="font-bold text-lg mb-4 border-b border-black pb-1">BROKER CONSENT</h2>
             
              {loadingBrokerConsentQuestions ? (
                <div className="py-4 text-gray-500 italic">
                  Loading broker consent questions...
                </div>
              ) : brokerConsentQuestions.length === 0 ? (
                <div className="py-8 text-center text-gray-500 italic">
                  No broker consent questions configured for this quote.
                </div>
              ) : (
                <div className="space-y-6">
                  {brokerConsentQuestions.map((question, index) => {
                    const currentResponse = declarationResponses[question.id] ?? false;
                    
                    return (
                      <div key={question.id || index} className="flex items-start gap-4 text-sm">
                        <span className="font-medium mt-1 min-w-[24px]">{index + 1}.</span>
                        <div className="flex-1">
                          <p className="text-gray-800 leading-relaxed mb-3">
                            {question.question}
                          </p>
                          
                          <div className="flex gap-8">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name={`broker-consent-${question.id}`}
                                checked={currentResponse === true}
                                onChange={() => handleDeclarationResponse(question.id, true)}
                                className="h-4 w-4 text-black focus:ring-black border-gray-300"
                              />
                              <span className="text-gray-700">Yes</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name={`broker-consent-${question.id}`}
                                checked={currentResponse === false}
                                onChange={() => handleDeclarationResponse(question.id, false)}
                                className="h-4 w-4 text-black focus:ring-black border-gray-300"
                              />
                              <span className="text-gray-700">No</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

        {/* Office Use Only */}
        <div className="mt-16">
          <h2 className="font-bold text-lg mb-4 border-b border-black pb-1">OFFICE USE ONLY</h2>
          
          <div className="space-y-6 mt-6">
            {/* Checklist items with Yes/No radio buttons */}
            <div className="space-y-4">
              {/* Signed Quotation and excesses received */}
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-gray-700">Signed Quotation and excesses received</div>
                <div className="flex items-center space-x-6">
                  <div className="flex items-center">
                    <input type="radio" id="signed-yes" name="signed" className="h-4 w-4 border-gray-300 focus:ring-2 focus:ring-black" />
                    <label htmlFor="signed-yes" className="ml-2 text-sm text-gray-700">Yes</label>
                  </div>
                  <div className="flex items-center">
                    <input type="radio" id="signed-no" name="signed" className="h-4 w-4 border-gray-300 focus:ring-2 focus:ring-black" />
                    <label htmlFor="signed-no" className="ml-2 text-sm text-gray-700">No</label>
                  </div>
                </div>
              </div>
              
              {/* Policy number */}
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-gray-700">Policy number</div>
                <div className="flex items-center space-x-6">
                  <div className="flex items-center">
                    <input type="radio" id="policy-yes" name="policy" className="h-4 w-4 border-gray-300 focus:ring-2 focus:ring-black" />
                    <label htmlFor="policy-yes" className="ml-2 text-sm text-gray-700">Yes</label>
                  </div>
                  <div className="flex items-center">
                    <input type="radio" id="policy-no" name="policy" className="h-4 w-4 border-gray-300 focus:ring-2 focus:ring-black" />
                    <label htmlFor="policy-no" className="ml-2 text-sm text-gray-700">No</label>
                  </div>
                </div>
              </div>
              
              {/* Notes / Outstanding information required */}
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-gray-700">Notes / Outstanding information required</div>
                <div className="flex items-center space-x-6">
                  <div className="flex items-center">
                    <input type="radio" id="notes-yes" name="notes" className="h-4 w-4 border-gray-300 focus:ring-2 focus:ring-black" />
                    <label htmlFor="notes-yes" className="ml-2 text-sm text-gray-700">Yes</label>
                  </div>
                  <div className="flex items-center">
                    <input type="radio" id="notes-no" name="notes" className="h-4 w-4 border-gray-300 focus:ring-2 focus:ring-black" />
                    <label htmlFor="notes-no" className="ml-2 text-sm text-gray-700">No</label>
                  </div>
                </div>
              </div>
              
              {/* Date Received */}
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-gray-700">Date Received</div>
                <div className="flex items-center space-x-6">
                  <div className="flex items-center">
                    <input type="radio" id="date-received-yes" name="date-received" className="h-4 w-4 border-gray-300 focus:ring-2 focus:ring-black" />
                    <label htmlFor="date-received-yes" className="ml-2 text-sm text-gray-700">Yes</label>
                  </div>
                  <div className="flex items-center">
                    <input type="radio" id="date-received-no" name="date-received" className="h-4 w-4 border-gray-300 focus:ring-2 focus:ring-black" />
                    <label htmlFor="date-received-no" className="ml-2 text-sm text-gray-700">No</label>
                  </div>
                </div>
              </div>
              
              {/* Date Finalized */}
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-gray-700">Date Finalized</div>
                <div className="flex items-center space-x-6">
                  <div className="flex items-center">
                    <input type="radio" id="date-finalized-yes" name="date-finalized" className="h-4 w-4 border-gray-300 focus:ring-2 focus:ring-black" />
                    <label htmlFor="date-finalized-yes" className="ml-2 text-sm text-gray-700">Yes</label>
                  </div>
                  <div className="flex items-center">
                    <input type="radio" id="date-finalized-no" name="date-finalized" className="h-4 w-4 border-gray-300 focus:ring-2 focus:ring-black" />
                    <label htmlFor="date-finalized-no" className="ml-2 text-sm text-gray-700">No</label>
                  </div>
                </div>
              </div>
              
              {/* Underwriter Name */}
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-gray-700">Underwriter Name</div>
                <div className="flex items-center space-x-6">
                  <div className="flex items-center">
                    <input type="radio" id="underwriter-name-yes" name="underwriter-name" className="h-4 w-4 border-gray-300 focus:ring-2 focus:ring-black" />
                    <label htmlFor="underwriter-name-yes" className="ml-2 text-sm text-gray-700">Yes</label>
                  </div>
                  <div className="flex items-center">
                    <input type="radio" id="underwriter-name-no" name="underwriter-name" className="h-4 w-4 border-gray-300 focus:ring-2 focus:ring-black" />
                    <label htmlFor="underwriter-name-no" className="ml-2 text-sm text-gray-700">No</label>
                  </div>
                </div>
              </div>
              
              {/* Underwriter Signature */}
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-gray-700">Underwriter Signature</div>
                <div className="flex items-center space-x-6">
                  <div className="flex items-center">
                    <input type="radio" id="underwriter-signature-yes" name="underwriter-signature" className="h-4 w-4 border-gray-300 focus:ring-2 focus:ring-black" />
                    <label htmlFor="underwriter-signature-yes" className="ml-2 text-sm text-gray-700">Yes</label>
                  </div>
                  <div className="flex items-center">
                    <input type="radio" id="underwriter-signature-no" name="underwriter-signature" className="h-4 w-4 border-gray-300 focus:ring-2 focus:ring-black" />
                    <label htmlFor="underwriter-signature-no" className="ml-2 text-sm text-gray-700">No</label>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Additional information section (optional) */}
            <div className="mt-8 pt-6 border-t border-gray-300">
              <div className="text-sm font-medium text-gray-700 mb-2">Additional Notes</div>
              <textarea 
                className="w-full h-20 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-black resize-none" 
                placeholder="Any additional office notes..."
                rows={3}
              />
            </div>
          </div>
        </div>

      </div>

            <div className="flex justify-between mt-8">
  <button onClick={() => navigate(`/quotes/${quoteId}`, { 
    state: { 
      selectedQuoteId: quoteId,
      clientId: client?.id,
      scrollToQuote: true 
    } 
  })} className="px-6 py-3 border border-gray-400 rounded hover:bg-gray-50 transition-colors">
    ← Back
  </button>
  
  <div className="flex space-x-4">
    {!isPreview && (
      <button 
        onClick={() => navigate(`/quotes/${quoteId}`, { state: { clientId: client?.id } })}
        className="px-8 py-3 border border-gray-400 rounded hover:bg-gray-50 transition-colors"
      >
        Edit
      </button>
    )}

    {/* Sign Online Button - Always show in preview mode, otherwise show when method is online_signature */}
    {(isPreview || (location.state && (location.state as any)?.acceptMethod === 'online_signature')) && (
      <button 
        onClick={handleAcceptOnlinePreview}
        className="px-8 py-3 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors flex items-center gap-2"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
        </svg>
        Sign Online
      </button>
    )}

    {/* Sign via Email Button - Always show in preview mode, otherwise show when method is email */}
{(isPreview || (location.state && (location.state as any)?.acceptMethod === 'email')) && (
  <button 
    onClick={handleAcceptEmailPreview}
    disabled={isSendingPDF}
    className={`px-8 py-3 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center gap-2 ${
      isSendingPDF ? 'opacity-50 cursor-not-allowed' : ''
    }`}
  >
        {isSendingPDF ? (
          <>
            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Sending...
          </>
        ) : (
          <>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Sign via Email
          </>
        )}
      </button>
    )}

    {/* Fallback Accept button for other cases (non-preview, no specific method) */}
    {!isPreview && 
     !(location.state && ((location.state as any)?.acceptMethod === 'email' || (location.state as any)?.acceptMethod === 'online_signature')) && (
      <button 
        onClick={handleAcceptClick}
        className="px-8 py-3 bg-black text-white rounded hover:bg-gray-800 transition-colors"
      >
        Accept
      </button>
    )}
  </div>
</div>

      {/* Accept Proposal Modal */}
      {showAcceptModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Accept Proposal</h3>
              <p className="text-sm text-gray-500 mt-1">Choose how you want to accept this proposal</p>
            </div>
            
            {/* Modal Body */}
            <div className="px-6 py-4">
              <div className="space-y-4">
                <div className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={handleAcceptOnline}>
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-4">
                      <h4 className="text-sm font-medium text-gray-900">Accept Online</h4>
                      <p className="text-sm text-gray-500">Submit and accept the proposal immediately online</p>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={handleAcceptEmail}>
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                        <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-4">
                      <h4 className="text-sm font-medium text-gray-900">Send via Email</h4>
                      <p className="text-sm text-gray-500">Email the proposal form for acceptance</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 text-sm text-gray-600">
                <p>Quote: <span className="font-medium">{quoteId}</span></p>
                <p>Client: <span className="font-medium">{client ? `${client.name || ''} ${client.surname || ''}`.trim() : 'N/A'}</span></p>
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="flex space-x-3">
              <button
                onClick={handleAcceptEmail}
                disabled={isSendingPDF}
                className={`px-4 py-2 border border-gray-300 rounded-md text-sm font-medium ${
                  isSendingPDF 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : 'text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black'
                }`}
              >
                {isSendingPDF ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </span>
                ) : 'Send via Email'}
              </button>

              <button
                onClick={handleAcceptOnline}
                className="px-4 py-2 bg-black text-white rounded-md text-sm font-medium hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
              >
                Sign Online
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Signature Modal */}
      <SignatureModal
        isOpen={isSignatureModalOpen}
        onClose={() => setIsSignatureModalOpen(false)}
        onSave={handleSignatureSave}
        clientName={`${client?.name || ''} ${client?.surname || ''}`.trim()}
      />

      {/* Loading overlay while processing */}
      {isProcessingSignature && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 shadow-xl text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-black mb-4"></div>
            <p className="text-gray-700">Generating signed PDF and sending email...</p>
            <p className="text-sm text-gray-500 mt-2">This may take a moment</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProposalApplicationForm;